import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { projects as projectsApi, financialAnalysis } from "../services/api";

import ScrollSyncBox from "../components/ScrollSyncBox"; // v3.4.244 — v3.4.249 صار مكوّنًا مشتركًا
import toast from "react-hot-toast";
import { FiFileText, FiCreditCard, FiTrendingDown, FiPercent, FiRefreshCw, FiGrid, FiDownload, FiFilter, FiHome, FiMessageCircle, FiSettings } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
// v3.3.344 — Recharts للرسم البياني الزمني
import { ComposedChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell } from "recharts";
import WhatsAppChatModal from "../components/WhatsAppChatModal";
import { buildReminder, ReminderSettingsModal } from "../components/CollectionsReminder"; // v3.4.691 — تذكير بالدفعة القادمة
import ReservationNotesCell from "../components/ReservationNotesCell";
// v3.3.640 — إعادة استخدام بوب-أب تفاصيل الحساب من صفحة الخزينة
import { AccountDetailSlideOver } from "./TreasuryPage";
// v3.3.991 — رؤوس أعمدة بنمط جدول الحجوزات (قائمة: ترتيب + فلتر قيم)
import GenericHeaderMenu from "../components/GenericHeaderMenu";

// ═══════════════════════════════════════════════════════════════════
// Shared utilities
// ═══════════════════════════════════════════════════════════════════
const fmt = (n) => (n == null || isNaN(n)) ? "0" : Math.round(Number(n)).toLocaleString("en-US");
const fmt2 = (n) => (n == null || isNaN(n)) ? "0" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
const pct = (n) => (n == null || isNaN(n)) ? "0%" : `${Math.round(Number(n))}%`;

function PageHeader({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1e293b", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
        {icon}
        {title}
      </h1>
      {subtitle && <p style={{ fontSize: 13, color: "#64748b", marginTop: 6, marginBottom: 0 }}>{subtitle}</p>}
    </div>
  );
}

function StatCard({ icon, label, value, subValue, color = "#1e293b", bg = "#f8fafc", onClick, active, hint }) {
  // v3.4.719 — بطاقة قابلة للضغط (تفلتر القائمة أسفلها)؛ active = الفلتر الحالي هو هذه البطاقة
  const clickable = typeof onClick === "function";
  return (
    <div onClick={onClick} role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined} onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      title={clickable ? (active ? "اضغط لإلغاء الفلتر" : (hint || "اضغط لعرض دفعات هذه البطاقة في القائمة")) : undefined}
      style={{ background: bg, borderRadius: 12, padding: "14px 16px", border: active ? `2px solid ${color}` : "1px solid #e2e8f0", boxShadow: active ? `0 0 0 3px ${color}22` : "none", cursor: clickable ? "pointer" : "default", transition: "box-shadow .15s, border-color .15s", position: "relative" }}>
      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
        {icon} {label}{active && <span style={{ marginInlineStart: "auto", fontSize: 10, fontWeight: 800, color, background: color + "18", borderRadius: 999, padding: "1px 7px" }}>مُفلتر ✕</span>}
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
      {subValue && <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 3, lineHeight: 1.6 }}>{subValue}</div>}{/* v3.4.300 — خط أكبر */}
    </div>
  );
}

function ProjectFilter({ value, onChange, projects }) {
  return (
    <select value={value || ""} onChange={e => onChange(e.target.value || null)}
      style={{ padding: "7px 12px", fontSize: 12, borderRadius: 7, border: "1px solid #e2e8f0", fontFamily: "inherit", cursor: "pointer" }}>
      <option value="">كل المشاريع</option>
      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
}

function useProjects() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    projectsApi.list().then(r => setProjects(r.data.projects || [])).catch(() => {});
  }, []);
  return projects;
}

// ═══════════════════════════════════════════════════════════════════
// Shared YearMonthFilters — multi-select years/months + quick presets
// ═══════════════════════════════════════════════════════════════════
const ARABIC_MONTHS = [
  { n: 1, label: "يناير" }, { n: 2, label: "فبراير" }, { n: 3, label: "مارس" },
  { n: 4, label: "أبريل" }, { n: 5, label: "مايو" },   { n: 6, label: "يونيو" },
  { n: 7, label: "يوليو" }, { n: 8, label: "أغسطس" },  { n: 9, label: "سبتمبر" },
  { n: 10, label: "أكتوبر" }, { n: 11, label: "نوفمبر" }, { n: 12, label: "ديسمبر" },
];

function YearMonthFilters({ selectedYears, selectedMonths, setSelectedYears, setSelectedMonths, availableYears = [] }) {
  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    const years = new Set(availableYears);
    for (let i = 0; i < 4; i++) years.add(currentYear - i);
    return Array.from(years).sort((a, b) => b - a);
  }, [availableYears, currentYear]);

  const totalYearsLabel = selectedYears.length > 0 ? selectedYears.length : "الكل";
  const totalMonthsLabel = selectedMonths.length > 0 ? selectedMonths.length : "الكل";

  // Quick presets
  const applyLastNMonths = (n) => {
    const today = new Date();
    const yearsSet = new Set();
    const monthsSet = new Set();
    for (let i = 0; i < n; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      yearsSet.add(d.getFullYear());
      monthsSet.add(d.getMonth() + 1);
    }
    setSelectedYears(Array.from(yearsSet));
    setSelectedMonths(Array.from(monthsSet));
  };

  const toggleYear = (y) => {
    setSelectedYears(selectedYears.includes(y)
      ? selectedYears.filter(x => x !== y)
      : [...selectedYears, y].sort((a, b) => b - a));
  };
  const toggleMonth = (m) => {
    setSelectedMonths(selectedMonths.includes(m)
      ? selectedMonths.filter(x => x !== m)
      : [...selectedMonths, m].sort((a, b) => a - b));
  };

  const pillBtn = (active, color = "#0891b2") => ({
    padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6,
    border: "1px solid " + (active ? color : "#e2e8f0"),
    background: active ? color : "#fff",
    color: active ? "#fff" : "#475569",
    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
  });
  const totalBtnStyle = (active, color = "#1e293b") => ({
    padding: "5px 14px", fontSize: 11, fontWeight: 700, borderRadius: 6,
    border: "1.5px solid " + color,
    background: active ? color : "#fff",
    color: active ? "#fff" : color,
    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
  });

  return (
    <>
      {/* Years */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, minWidth: 55 }}>السنة:</span>
        <button onClick={() => setSelectedYears([])}
          style={totalBtnStyle(selectedYears.length === 0, "#1e293b")}>
          الإجمالي ({totalYearsLabel})
        </button>
        {yearOptions.map(y => (
          <button key={y} onClick={() => toggleYear(y)}
            style={pillBtn(selectedYears.includes(y), "#2563eb")}>
            {y}
          </button>
        ))}
      </div>

      {/* Months + quick presets */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, minWidth: 55 }}>الشهر:</span>
        <button onClick={() => setSelectedMonths([])}
          style={totalBtnStyle(selectedMonths.length === 0, "#1e293b")}>
          الإجمالي ({totalMonthsLabel})
        </button>
        {ARABIC_MONTHS.map(m => (
          <button key={m.n} onClick={() => toggleMonth(m.n)}
            style={pillBtn(selectedMonths.includes(m.n), "#0891b2")}>
            {m.label}
          </button>
        ))}
        <span style={{ width: 8 }} />
        <button onClick={() => applyLastNMonths(3)}
          style={{ padding: "5px 10px", fontSize: 11, fontWeight: 600, borderRadius: 6, border: "1px dashed #f59e0b", background: "#fffbeb", color: "#92400e", cursor: "pointer", fontFamily: "inherit" }}>
          آخر 3 أشهر
        </button>
        <button onClick={() => applyLastNMonths(6)}
          style={{ padding: "5px 10px", fontSize: 11, fontWeight: 600, borderRadius: 6, border: "1px dashed #f59e0b", background: "#fffbeb", color: "#92400e", cursor: "pointer", fontFamily: "inherit" }}>
          آخر 6 أشهر
        </button>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// v3.3.344 — PaymentsTimelineChart
// رسم بياني زمني: stacked area لـ (عقود + تحصيل) = إجمالي التحصيل
// + line منفصل لإجمالي المبيعات
// v3.3.353 — فلتر مشروع داخلي في البوكس
// ═══════════════════════════════════════════════════════════════════
function PaymentsTimelineChart({ data, error, projects = [], chartProjectId, setChartProjectId, selectedMonth, onMonthClick }) {
  // تحويل مفاتيح الشهر "2025-04" إلى تنسيق عربي قصير
  const formattedData = useMemo(() => {
    const monthNames = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
    return (data || []).map(d => {
      const [y, m] = d.month.split("-");
      const mIdx = parseInt(m) - 1;
      return {
        ...d,
        label: `${monthNames[mIdx]} ${y}`,
        total_collection: (Number(d.contracts) || 0) + (Number(d.collections) || 0), // v3.4.364 — منحنى إجمالي التحصيل
      };
    });
  }, [data]);

  // v3.3.353 — Header مع dropdown مشروع — يظهر دائماً (حتى عند الـ empty state)
  const chartHeader = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 2 }}>📈 الحركة الزمنية للتحصيل والمبيعات
          {selectedMonth && <span style={{ marginInlineStart: 10, display: "inline-flex", alignItems: "center", gap: 6, background: "#e0f2fe", color: "#075985", borderRadius: 999, padding: "2px 10px", fontSize: 11.5, fontWeight: 800 }}>{/* v3.4.537 */}
            🔍 القائمة مصفّاة: {(formattedData.find((d) => d.month === selectedMonth) || {}).label || selectedMonth}
            <button type="button" onClick={() => onMonthClick && onMonthClick(null)} style={{ border: 0, background: "none", cursor: "pointer", color: "inherit", fontWeight: 900 }}>✕</button>
          </span>}
        </div>
        <div style={{ fontSize: 11, color: "#64748b" }}>تجميع شهري لكامل النطاق الزمني للبيانات • {formattedData.length} شهر{onMonthClick ? " • انقر على شهر لعرض دفعاته في القائمة أسفل" : ""}</div>
      </div>
      {setChartProjectId && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
          <span style={{ color: "#64748b", fontWeight: 600 }}>🏢 المشروع:</span>
          <select value={chartProjectId || ""} onChange={e => setChartProjectId(e.target.value || null)}
            style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11, fontFamily: "inherit", background: "#fff", cursor: "pointer", minWidth: 140 }}>
            <option value="">📊 كل المشاريع</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  if (!data || data.length === 0) {
    let message = "📊 لا توجد بيانات كافية لعرض الرسم البياني";
    let detail = "";
    if (error === "endpoint_missing") {
      message = "⚠️ خدمة الرسم البياني غير متوفرة";
      detail = "يبدو أن النسخة المنشورة على الخادم لا تحتوي على endpoint /payments-timeline. يرجى إعادة النشر.";
    } else if (error === "load_failed") {
      message = "❌ فشل تحميل بيانات الرسم البياني";
      detail = "حدث خطأ أثناء جلب البيانات من الخادم. تحقق من سجلات الخادم.";
    } else if (error === "empty") {
      message = "📊 لا توجد بيانات مدفوعات بعد";
      detail = chartProjectId ? "لا توجد بيانات لهذا المشروع. جرّب مشروعاً آخر أو الكل." : "أضف دفعات للمشاريع لعرض الحركة الزمنية للتحصيل.";
    }
    return (
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        {chartHeader}
        <div style={{ textAlign: "center", color: "#64748b", fontSize: 13, padding: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: detail ? 6 : 0 }}>{message}</div>
          {detail && <div style={{ fontSize: 11, color: "#94a3b8" }}>{detail}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      {chartHeader}
      <div style={{ width: "100%", height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={formattedData} margin={{ top: 10, right: 20, left: 10, bottom: 50 }} style={{ cursor: onMonthClick ? "pointer" : "default" }}
            onClick={(e) => { if (!onMonthClick) return; const m = e && e.activePayload && e.activePayload[0] && e.activePayload[0].payload && e.activePayload[0].payload.month; if (m) onMonthClick(m === selectedMonth ? null : m); }}>{/* v3.4.537 */}
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            {selectedMonth && formattedData.some((d) => d.month === selectedMonth) && <ReferenceLine x={(formattedData.find((d) => d.month === selectedMonth) || {}).label} stroke="#0284c7" strokeWidth={2} strokeDasharray="4 3" />}
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#64748b" }}
              angle={-40}
              textAnchor="end"
              interval="preserveStartEnd"
              height={60}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#64748b" }}
              tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}م` : v >= 1000 ? `${(v/1000).toFixed(0)}ك` : v}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                // استخراج القيم من الـ payload
                const get = (key) => {
                  const item = payload.find(p => p.dataKey === key);
                  return item ? Number(item.value) || 0 : 0;
                };
                const contracts = get("contracts");
                const collections = get("collections");
                const sales = get("sales");
                const resale = get("resale"); // v3.4.367
                const totalCollection = contracts + collections;
                const fmtN = (n) => Math.round(n).toLocaleString("en-US");
                return (
                  <div style={{
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontFamily: "inherit",
                    fontSize: 11,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    minWidth: 180,
                  }}>
                    <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 12, marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #f1f5f9" }}>
                      📅 {label}
                    </div>
                    {/* v3.4.365 — الترتيب: إجمالي المبيعات ← إجمالي التحصيل ← عناصر التحصيل */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #f1f5f9" }}>
                      <span style={{ color: "#f43f5e", fontSize: 11, fontWeight: 700 }}>🏷️ إجمالي المبيعات</span>
                      <span style={{ color: "#1e293b", fontWeight: 800, fontSize: 13 }}>{fmtN(sales)} ر.س</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ color: "#0f766e", fontSize: 11, fontWeight: 700 }}>💰 إجمالي التحصيل</span>
                      <span style={{ color: "#0f766e", fontWeight: 800, fontSize: 13 }}>{fmtN(totalCollection)} ر.س</span>
                    </div>
                    <div style={{ paddingRight: 10, borderRight: "2px solid #ccfbf1" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                        <span style={{ color: "#3b82f6", fontSize: 10 }}>📝 العقود الجديدة</span>
                        <span style={{ color: "#1e293b", fontWeight: 600, fontSize: 11 }}>{fmtN(contracts)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: "#10b981", fontSize: 10 }}>💵 التحصيل من العقود السابقة</span>
                        <span style={{ color: "#1e293b", fontWeight: 600, fontSize: 11 }}>{fmtN(collections)}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, paddingTop: 6, borderTop: "1px solid #f1f5f9" }}>{/* v3.4.367 */}
                      <span style={{ color: "#b45309", fontSize: 10 }}>🔄 إعادة بيع (خارج التحصيل)</span>
                      <span style={{ color: "#1e293b", fontWeight: 600, fontSize: 11 }}>{fmtN(resale)}</span>
                    </div>
                  </div>
                );
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} iconType="circle" />
            {/* v3.4.365 — منحنيات مستقلة غير مكدسة: الخلفية الملوّنة لإجمالي التحصيل فقط، والبقية خطوط */}
            <Area
              type="monotone"
              dataKey="total_collection"
              name="💰 إجمالي التحصيل"
              stroke="#0f766e"
              fill="#99f6e4"
              fillOpacity={0.45}
              strokeWidth={2.5}
              dot={{ r: 3, fill: "#0f766e" }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="contracts"
              name="📝 العقود الجديدة"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 2.5, fill: "#3b82f6" }}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="collections"
              name="💵 التحصيل من العقود السابقة"
              stroke="#10b981"
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={{ r: 2.5, fill: "#10b981" }}
              activeDot={{ r: 4 }}
            />
            {/* v3.4.367 — دفعات عقود إعادة البيع: منحنى مستقل (خارج التحصيل) */}
            <Line
              type="monotone"
              dataKey="resale"
              name="🔄 إعادة بيع"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={{ r: 2.5, fill: "#f59e0b" }}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="sales"
              name="🏷️ إجمالي المبيعات"
              stroke="#fb7185"
              strokeWidth={2.5}
              dot={{ r: 3, fill: "#fb7185" }}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ marginTop: 8, fontSize: 10, color: "#94a3b8", textAlign: "center" }}>
        💡 المنطقة الملوّنة = إجمالي التحصيل (العقود الجديدة + التحصيل من العقود السابقة، كل منهما خط مستقل — بلا إعادة البيع) • الخط البرتقالي المتقطّع = دفعات عقود إعادة البيع • الخط المرجاني = إجمالي المبيعات (بتعريف قائمة المبيعات: الوحدات المباعة خارجيًا بتاريخ أول دفعة غير العربون وإلا تاريخ العقد)
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 1. PaymentsPage — سجل المدفوعات
// ═══════════════════════════════════════════════════════════════════
// v3.4.056 — جدول السداد مقابل المُحصّل فعليًا: أعمدة الأقساط المستحقة شهريًا (مسددة/متأخرة/قادمة)
// وخط المبالغ المستلمة فعليًا في نفس الشهر — لقياس أداء التحصيل مقابل الجدول المتفق عليه
function RepaymentsTimelineChart({ projects = [] }) {
  const [projectId, setProjectId] = useState(null);
  const [data, setData] = useState({ timeline: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all"); // all | future | past

  useEffect(() => {
    setLoading(true);
    financialAnalysis.getRepaymentsTimeline(projectId ? { project_id: projectId } : {})
      .then(r => setData(r.data || { timeline: [], totals: {} }))
      .catch(() => setData({ timeline: [], totals: {} }))
      .finally(() => setLoading(false));
  }, [projectId]);

  const nowKey = new Date().toISOString().slice(0, 7);
  const rows = useMemo(() => {
    const monthNames = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
    return (data.timeline || [])
      .filter(d => scope === "all" || (scope === "future" ? d.month >= nowKey : d.month < nowKey))
      .map(d => {
        const [y, m] = d.month.split("-");
        /* v3.4.495 — «المستلم فعليًا» يتوقف عند الشهر الحالي (كان ينزل للصفر في الأشهر القادمة)،
           ويكمل بعده خط متقطع «المتوقع استلامه» = الأقساط المجدولة لكل شهر قادم (يبدأ من نقطة الشهر الحالي ليتصل بالخط) */
        const upcoming = d.month > nowKey;
        return { ...d, label: `${monthNames[parseInt(m) - 1]} ${y}`, isFuture: d.month >= nowKey, isUpcoming: upcoming,
          received: upcoming ? null : d.received,
          expected: upcoming ? (Number(d.due_total) || 0) : d.month === nowKey ? (Number(d.received) || 0) : null };
      });
  }, [data, scope, nowKey]);

  const fmtN = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");
  const t = data.totals || {};
  const collectedPct = t.due_total ? Math.round((t.due_paid / t.due_total) * 100) : 0;

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 2 }}>📅 جدول السداد مقابل المُحصّل فعليًا</div>
        <div style={{ fontSize: 11, color: "#64748b" }}>
          الأقساط المتفق عليها حسب شهر الاستحقاق (أعمدة) والمبالغ المستلمة فعليًا (خط) • {rows.length} شهر
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, flexWrap: "wrap" }}>
        {[["all", "الكل"], ["past", "◀ سابقة"], ["future", "قادمة ▶"]].map(([k, l]) => (
          <button key={k} onClick={() => setScope(k)}
            style={{ padding: "5px 12px", borderRadius: 14, fontSize: 11, fontWeight: scope === k ? 800 : 600, cursor: "pointer", fontFamily: "inherit",
              border: scope === k ? "2px solid #0f766e" : "1px solid #e2e8f0", background: scope === k ? "#f0fdfa" : "#fff", color: scope === k ? "#0f766e" : "#64748b" }}>{l}</button>
        ))}
        <span style={{ color: "#64748b", fontWeight: 600, marginInlineStart: 6 }}>🏢 المشروع:</span>
        <select value={projectId || ""} onChange={e => setProjectId(e.target.value || null)}
          style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11, fontFamily: "inherit", background: "#fff", cursor: "pointer", minWidth: 140 }}>
          <option value="">📊 كل المشاريع</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
    </div>
  );

  const chip = (label, value, color, bg) => (
    <div style={{ background: bg, border: `1px solid ${color}22`, borderRadius: 8, padding: "6px 12px", minWidth: 120 }}>
      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 900, color }}>{value}</div>
    </div>
  );

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      {header}
      {loading ? (
        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 12, padding: 30 }}>جارٍ التحميل…</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: "center", color: "#64748b", fontSize: 13, padding: 20 }}>
          <div style={{ fontWeight: 700 }}>📊 لا توجد جداول سداد لعرضها</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>تُضاف جداول الدفعات المتفق عليها من صفحة الوحدة</div>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {chip("إجمالي المجدول", `${fmtN(t.due_total)} ر.س`, "#1e293b", "#f8fafc")}
            {chip(`المسدد من الجدول (${collectedPct}%)`, `${fmtN(t.due_paid)} ر.س`, "#16a34a", "#f0fdf4")}
            {chip("متأخرات مستحقة", `${fmtN(t.due_overdue)} ر.س`, "#dc2626", "#fef2f2")}
            {chip("أقساط قادمة", `${fmtN(t.due_upcoming)} ر.س`, "#2563eb", "#eff6ff")}
            {chip("المستلم فعليًا", `${fmtN(t.received)} ر.س`, "#7c3aed", "#faf5ff")}
          </div>
          <div style={{ width: "100%", height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 10, right: 20, left: 10, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} angle={-40} textAnchor="end" interval="preserveStartEnd" height={60} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }}
                  tickFormatter={v => v >= 1000000 ? `${(v / 1000000).toFixed(1)}م` : v >= 1000 ? `${(v / 1000).toFixed(0)}ك` : v} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const d = payload[0]?.payload || {};
                    const gap = d.isUpcoming ? null : (Number(d.received) || 0) - (Number(d.due_total) || 0); /* v3.4.495 */
                    const line = (c, l, v) => (
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginBottom: 3 }}>
                        <span style={{ color: "#64748b" }}><span style={{ color: c, fontWeight: 900 }}>■</span> {l}</span>
                        <span style={{ fontWeight: 800, color: c }}>{fmtN(v)}</span>
                      </div>
                    );
                    return (
                      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", fontFamily: "inherit", fontSize: 11, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", minWidth: 220 }}>
                        <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 12, marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #f1f5f9" }}>📅 {label}</div>
                        {line("#16a34a", "أقساط مسددة", d.due_paid)}
                        {line("#dc2626", "أقساط متأخرة", d.due_overdue)}
                        {line("#2563eb", "أقساط قادمة", d.due_upcoming)}
                        <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 6, paddingTop: 6 }}>
                          {line("#0f172a", "إجمالي المستحق بالجدول", d.due_total)}
                          {d.isUpcoming ? line("#a78bfa", "المتوقع استلامه (حسب الجدول)", d.expected) : line("#7c3aed", "المستلم فعليًا", d.received)}
                        </div>
                        {gap === null ? (
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #f1f5f9", fontSize: 10.5, fontWeight: 700, color: "#7c3aed" }}>⏳ شهر قادم — لم يُستلم شيء بعد</div>
                        ) : (
                        <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #f1f5f9", fontSize: 10.5, fontWeight: 800, color: gap >= 0 ? "#16a34a" : "#b45309" }}>
                          {gap >= 0 ? `▲ فائض عن الجدول ${fmtN(gap)} ر.س` : `▼ عجز عن الجدول ${fmtN(Math.abs(gap))} ر.س`}
                        </div>)}
                        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>{d.due_count || 0} قسط • {d.received_count || 0} دفعة مستلمة</div>
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                <Bar dataKey="due_paid" name="أقساط مسددة" stackId="due" fill="#16a34a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="due_overdue" name="أقساط متأخرة" stackId="due" fill="#dc2626" radius={[0, 0, 0, 0]} />
                <Bar dataKey="due_upcoming" name="أقساط قادمة" stackId="due" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="received" name="المستلم فعليًا" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 3 }} />
                {/* v3.4.495 — امتداد متقطع للأشهر القادمة */}
                <Line type="monotone" dataKey="expected" name="المتوقع استلامه (حسب الجدول)" stroke="#a78bfa" strokeWidth={2.5} strokeDasharray="7 5" dot={{ r: 3, fill: "#fff", stroke: "#a78bfa" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

export function PaymentsPage() {
  const projects = useProjects();
  // v3.4.476 — صلاحيات مكوّنات الصفحة (الأدمن يرى الكل)
  const { user } = useAuth();
  const isAdminP = !!user && (user.role === "admin" || user.is_admin) && !user.impersonating; // v3.4.478 — في وضع المعاينة تُطبَّق صلاحيات البروفايل
  const showPayStats = isAdminP || !!user?.can_view_payments_stats;
  const showPayTimeline = isAdminP || !!user?.can_view_payments_timeline;
  const showPaySchedule = isAdminP || !!user?.can_view_payments_schedule;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [chartMonth, setChartMonth] = useState(null); // v3.4.537 — شهر مختار من مخطط الحركة الزمنية (YYYY-MM)

  const [projectId, setProjectId] = useState(null);
  const [selectedYears, setSelectedYears] = useState([currentYear]);     // [] = all
  const [selectedMonths, setSelectedMonths] = useState([currentMonth]);  // [] = all
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | contract | collection
  // v3.3.999 — هيدر الحجوزات القياسي: فرز + فلاتر قيم متسلسلة (نمط إكسل كما في التحصيلات)
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [colFilters, setColFilters] = useState({});
  const [data, setData] = useState({ payments: [], summary: {}, available_years: [] });
  const [loading, setLoading] = useState(false);
  // v3.3.344 — Timeline data للرسم البياني الزمني (كامل النطاق، يتأثر فقط بـ project)
  const [timeline, setTimeline] = useState([]);
  const [timelineError, setTimelineError] = useState(null);
  // v3.3.346 — تحميل كل المدفوعات (بدون فلاتر) لاستخدامها كـ fallback إن فشل /payments-timeline
  const [allPayments, setAllPayments] = useState([]);
  // v3.3.353 — فلتر مشروع مستقل للـ chart (لا يؤثر على الجدول)
  const [chartProjectId, setChartProjectId] = useState(null);

  // Load timeline (يتحدث فقط عند تغيير chartProjectId - لأنه يعرض كامل النطاق الزمني)
  useEffect(() => {
    const params = {};
    if (chartProjectId) params.project_id = chartProjectId;
    setTimelineError(null);
    financialAnalysis.getPaymentsTimeline(params)
      .then(r => {
        const tl = r.data.timeline || [];
        setTimeline(tl);
        if (tl.length === 0) setTimelineError("empty");
      })
      .catch(err => {
        console.error("timeline load error:", err);
        setTimeline([]);
        setTimelineError(err.response?.status === 404 ? "endpoint_missing" : "load_failed");
      });
  }, [chartProjectId]);

  // v3.3.346 — تحميل كل المدفوعات بدون أي فلتر تاريخ (للـ fallback chart) — يتبع chartProjectId
  useEffect(() => {
    const params = { limit: 5000 };
    if (chartProjectId) params.project_id = chartProjectId;
    // عدم تمرير years/months يجعل backend يُرجع كل البيانات
    financialAnalysis.getPayments(params)
      .then(r => setAllPayments(r.data.payments || []))
      .catch(() => setAllPayments([]));
  }, [chartProjectId]);

  // v3.3.346 — Fallback: بناء timeline من كل المدفوعات (بدون فلاتر تاريخ)
  // يضمن ظهور chart كامل النطاق حتى لو الـ backend القديم منشور
  const fallbackTimeline = useMemo(() => {
    if (!allPayments || allPayments.length === 0) return [];
    const byMonth = new Map();
    const firstPayPerUnit = new Map();

    // جمع أول دفعة لكل وحدة (لتمييز contract من collection)
    const sortedPays = [...allPayments].sort((a, b) => (a.id || 0) - (b.id || 0));
    for (const p of sortedPays) {
      if (!firstPayPerUnit.has(p.unit_id)) {
        firstPayPerUnit.set(p.unit_id, p.id);
      }
    }

    // تجميع شهري
    for (const p of allPayments) {
      const month = (p.payment_date || "").slice(0, 7); // YYYY-MM
      if (!month) continue;
      if (!byMonth.has(month)) {
        byMonth.set(month, { month, contracts: 0, collections: 0, resale: 0, sales: 0 });
      }
      const entry = byMonth.get(month);
      const isFirst = firstPayPerUnit.get(p.unit_id) === p.id;
      if (p.payment_type === "contract_resale") { entry.resale += Number(p.amount) || 0; } // v3.4.367
      else if (isFirst) {
        entry.contracts += Number(p.amount) || 0;
        // قيمة البيع للوحدة: نستعمل sale_price/unit_price إن وجدت، وإلا نقدرها بالقيمة الإجمالية
        entry.sales += Number(p.unit_total_value || p.unit_price || p.amount) || 0;
      } else {
        entry.collections += Number(p.amount) || 0;
      }
    }

    return Array.from(byMonth.values())
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(d => ({ ...d, total_inflow: d.contracts + d.collections }));
  }, [allPayments]);

  // استخدام الـ timeline الفعلي إن وُجد، وإلا الـ fallback
  const finalTimeline = (timeline.length > 0) ? timeline : fallbackTimeline;


  const load = () => {
    setLoading(true);
    const params = {};
    if (projectId) params.project_id = projectId;
    if (chartMonth && /^\d{4}-\d{2}$/.test(chartMonth)) { // v3.4.713 — كانت بلا \ فلا تطابق أبدًا // v3.4.568 — الشهر المضغوط في المخطط يحكم البطاقات العلوية والقائمة معًا
      params.years = String(parseInt(chartMonth.slice(0, 4), 10)); params.months_list = String(parseInt(chartMonth.slice(5, 7), 10));
    } else {
      if (selectedYears.length > 0)  params.years = selectedYears.join(",");
      if (selectedMonths.length > 0) params.months_list = selectedMonths.join(",");
      if (selectedYears.length === 0 && selectedMonths.length === 0) {
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
      }
    }
    if (search) params.search = search;
    financialAnalysis.getPayments(params)
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل المدفوعات"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [projectId, selectedYears, selectedMonths, fromDate, toDate, search, chartMonth]); // eslint-disable-line

  // v3.3.999 — سلسلة الفلاتر بنمط إكسل المتسلسل (كما في التحصيلات v3.3.994): exceptKey يستثني فلترًا واحدًا
  const getColVal = (p, k) =>
    k === "unit_label" ? (p.unit_label || p.unit_number || (p.unit_id ? `#${p.unit_id}` : ""))
    : k === "payment_type" ? (p.payment_type === "contract" ? "عقد جديد" : p.payment_type === "contract_resale" ? "إعادة بيع" : "تحصيل") // v3.4.055
    : k === "payment_date" ? String(p.payment_date || "").slice(0, 10)
    : (p[k] ?? "");
  const applyFilters = (arr, exceptKey) => arr
    .filter(p => exceptKey === "chart_month" || !chartMonth || String(p.payment_date || "").slice(0, 7) === chartMonth) /* v3.4.537 — شهر مختار من المخطط */
    .filter(p => exceptKey === "toolbar_type" || typeFilter === "all" || (typeFilter === "no_resale" ? p.payment_type !== "contract_resale" : p.payment_type === typeFilter)) /* v3.4.719 — no_resale = عقد + تحصيل */
    .filter(p => Object.entries(colFilters).every(([k, vals]) => k === exceptKey || !vals?.length || vals.includes(String(getColVal(p, k)))));
  const distinctOf = (k) => [...new Set(applyFilters(data.payments, k).map(p => String(getColVal(p, k))).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ar"));
  const filtered = useMemo(() => {
    let arr = applyFilters(data.payments, null);
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      arr = [...arr].sort((a, b) => {
        const va = getColVal(a, sortKey), vb = getColVal(b, sortKey);
        const na = Number(va), nb = Number(vb);
        if (va !== "" && vb !== "" && !isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
        return String(va).localeCompare(String(vb), "ar") * dir;
      });
    }
    return arr;
  }, [data.payments, typeFilter, colFilters, sortKey, sortDir]); // eslint-disable-line

  // v3.4.713 — البطاقات العلوية تُحسب من الصفوف المعروضة فعلًا (كل الفلاتر: الشهر/السنة، النوع، شهر المخطط، فلاتر الأعمدة، البحث)
  // أسطر «إجمالي المبيعات/المسدَّد» تبقى من الخادم لأنها تحتاج قيم الوحدات (تتبع فلاتر السنة/الشهر/المشروع/البحث)
  const cardSummary = useMemo(() => {
    const n = (v) => Number(v) || 0; const sum = (a) => a.reduce((s, p) => s + n(p.amount), 0);
    const rows = applyFilters(data.payments || [], null);
    const c = rows.filter(p => p.payment_type === "contract"), r = rows.filter(p => p.payment_type === "contract_resale"), k = rows.filter(p => p.payment_type !== "contract" && p.payment_type !== "contract_resale");
    const tc = sum(c), tk = sum(k), tr = sum(r);
    return { ...(data.summary || {}), count: c.length + k.length, total_amount: tc + tk, contracts_count: c.length, contracts_total: tc, collections_count: k.length, collections_total: tk, resale_count: r.length, resale_total: tr, total_with_resale: tc + tk + tr };
  }, [data, typeFilter, colFilters, chartMonth]); // eslint-disable-line
  // v3.4.719 — الضغط على بطاقة علوية يفلتر القائمة بنوعها (ضغطة ثانية تلغي)، ويمرّر إلى شريط الفلاتر
  const pickCard = (k) => { setTypeFilter((t) => (t === k ? "all" : k)); setTimeout(() => { const el = document.getElementById("pay-type-chips"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50); };
  // v3.3.999 — رأس عمود بنمط جدول الحجوزات
  const SortTh = ({ label, k, align = "center", color = "#475569", filter = false }) => (
    k ? (
      <GenericHeaderMenu label={label} colKey={k} sortBy={sortKey} sortDir={sortDir}
        onSort={(key, dir) => { setSortKey(key); setSortDir(dir); }}
        values={filter ? distinctOf(k) : null}
        activeFilter={colFilters[k] || []}
        onFilter={(key, vals) => setColFilters(f => ({ ...f, [key]: vals }))}
        align={align} color={color} />
    ) : (
      <th style={{ padding: "10px 8px", textAlign: align, fontWeight: 700, color, whiteSpace: "nowrap" }}>{label}</th>
    )
  );

  const exportCSV = () => {
    const headers = ["التاريخ", "المشروع", "الوحدة", "العميل", "المبلغ", "النوع", "ملاحظات"];
    const rows = filtered.map(p => [
      p.payment_date?.slice(0, 10),
      p.project_name,
      p.unit_label || p.unit_number,
      p.client_name || "",
      p.amount,
      p.payment_type === "contract" ? "عقد جديد" : p.payment_type === "contract_resale" ? "إعادة بيع" : "تحصيل", // v3.4.055
      (p.notes || "").replace(/[\n,]/g, " "),
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Pill button style generator
  const pillBtn = (active, color = "#16a34a") => ({
    padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6,
    border: "1px solid " + (active ? color : "#e2e8f0"),
    background: active ? color : "#fff",
    color: active ? "#fff" : "#475569",
    cursor: "pointer", fontFamily: "inherit",
    transition: "all 0.15s",
  });

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader icon={<FiCreditCard size={26} color="#16a34a" />} title="المدفوعات المحصّلة" subtitle="سجل جميع المدفوعات المستلمة عبر كل المشاريع" />

      {/* Stats — v3.3.316: ترتيب جديد مع بطاقة إجمالي المبيعات */}
      {/* RTL: العنصر الأول في DOM = يمين الشاشة */}
      {showPayStats && /* v3.4.476 — v3.4.713: الأرقام من الصفوف المعروضة */ <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <StatCard onClick={() => pickCard("no_resale")} active={typeFilter === "no_resale"} hint="عقود جديدة + تحصيل (بلا إعادة البيع)" label="💰 إجمالي المبالغ المحصّلة" value={`${fmt(cardSummary.total_amount)} ر.س`} subValue={<span>بلا إعادة البيع<br /><b style={{ color: "#0f172a", fontSize: 12.5 }}>مع إعادة البيع: {fmt(cardSummary.total_with_resale ?? ((cardSummary.total_amount || 0) + (cardSummary.resale_total || 0)))} ر.س</b></span>} color="#16a34a" bg="#f0fdf4" />{/* v3.4.367 */}
        <StatCard onClick={() => pickCard("no_resale")} active={typeFilter === "no_resale"} hint="عقود جديدة + تحصيل (بلا إعادة البيع)" label="📊 عدد المدفوعات" value={fmt(cardSummary.count)} color="#2563eb" bg="#eff6ff" />
        {/* v3.4.226-cards — إجمالي المبيعات تحت دفعات العقود الجديدة بدل بطاقة مستقلة */}
        <StatCard onClick={() => pickCard("contract")} active={typeFilter === "contract"} label="📝 دفعات عقود جديدة" value={`${fmt(cardSummary.contracts_total)} ر.س`} subValue={<span>{cardSummary.contracts_count || 0} دفعة<br /><b style={{ color: "#0f172a", fontSize: 13.5 }}>🏷️ إجمالي المبيعات {fmt(data.summary.total_sales || 0)} ر.س</b> ({data.summary.sales_count || 0} مبيعة)<br /><b style={{ color: (data.summary.sales_paid_pct || 0) >= 50 ? "#166534" : "#b45309", fontSize: 13.5 }}>💰 المسدَّد حتى اليوم على هذه المبيعات {fmt(data.summary.sales_paid_total || 0)} ر.س = {data.summary.sales_paid_pct || 0}%</b><span title="كل ما دُفع على وحدات مبيعات الفترة حتى الآن — دفعات العقد الجديد وما تلاها من تحصيل، حتى خارج الفترة" style={{ color: "#94a3b8", fontSize: 10.5 }}> (شامل تحصيلها اللاحق)</span>{/* v3.4.685 */} <span style={{ color: "#94a3b8" }}>· المتبقي {fmt(Math.max(0, (data.summary.total_sales || 0) - (data.summary.sales_paid_total || 0)))} ر.س</span></span>} color="#7c3aed" bg="#faf5ff" />{/* v3.4.300 — نسبة المدفوع من إجمالي المبيعات */}
        <StatCard onClick={() => pickCard("collection")} active={typeFilter === "collection"} label="💵 مبالغ التحصيل" value={`${fmt(cardSummary.collections_total)} ر.س`} subValue={`${cardSummary.collections_count || 0} دفعة`} color="#0891b2" bg="#ecfeff" />
        {/* v3.4.055 — بند مستقل خارج إجمالي المدفوعات */}
        <StatCard onClick={() => pickCard("contract_resale")} active={typeFilter === "contract_resale"} label="🔄 إجمالي إعادة البيع" value={`${fmt(cardSummary.resale_total || 0)} ر.س`} subValue={`${cardSummary.resale_count || 0} دفعة — خارج إجمالي التحصيل`} color="#b45309" bg="#fffbeb" />{/* v3.4.367 — بطاقة دائمة */}
      </div>}

      {/* v3.3.344 — رسم بياني زمني لكامل البيانات */}
      {showPayTimeline && <PaymentsTimelineChart
        data={finalTimeline}
        error={finalTimeline.length > 0 ? null : timelineError}
        projects={projects}
        chartProjectId={chartProjectId}
        setChartProjectId={setChartProjectId}
        selectedMonth={chartMonth}
        onMonthClick={setChartMonth}
      />}{/* v3.4.476 / v3.4.537 */}
      {/* v3.4.056 — جدول السداد مقابل المُحصّل فعليًا */}
      {showPaySchedule && <RepaymentsTimelineChart projects={projects} />}{/* v3.4.476 */}

      {/* Filters — structured rows */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>

        {/* Row 1: Project + Type — v3.4.702: المشاريع أزرارًا مستقلة يسبقها «الكل» (بطلب المالك) */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <FiFilter size={14} color="#64748b" />
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, minWidth: 55 }}>المشروع:</span>
          <button onClick={() => setProjectId(null)} style={pillBtn(!projectId, "#1d4ed8")}>الكل</button>
          {projects.map(p => <button key={p.id} onClick={() => setProjectId(String(p.id) === String(projectId) ? null : p.id)} style={pillBtn(String(p.id) === String(projectId), "#1d4ed8")}>{p.name}</button>)}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span id="pay-type-chips" style={{ fontSize: 11, color: "#64748b", fontWeight: 600, minWidth: 55, marginInlineStart: 22, scrollMarginTop: 12 }}>النوع:</span>{/* v3.4.719 — مرساة التمرير من البطاقات */}
          <button onClick={() => setTypeFilter("all")} style={pillBtn(typeFilter === "all", "#1e293b")}>الكل</button>
          <button onClick={() => setTypeFilter("no_resale")} style={pillBtn(typeFilter === "no_resale", "#16a34a")} title="عقود جديدة + تحصيل، بلا إعادة البيع (ما تحسبه بطاقة الإجمالي)">💰 بلا إعادة البيع</button>{/* v3.4.719 */}
          <button onClick={() => setTypeFilter("contract")} style={pillBtn(typeFilter === "contract", "#7c3aed")}>📝 عقد جديد</button>
          <button onClick={() => setTypeFilter("collection")} style={pillBtn(typeFilter === "collection", "#0891b2")}>💵 تحصيل</button>
          {/* v3.4.055 — بند مستقل: لا يدخل في إجمالي المدفوعات */}
          <button onClick={() => setTypeFilter("contract_resale")} style={pillBtn(typeFilter === "contract_resale", "#b45309")}>🔄 إعادة بيع</button>
        </div>

        {/* Years + Months (multi-select) */}
        <YearMonthFilters
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          setSelectedYears={setSelectedYears}
          setSelectedMonths={setSelectedMonths}
          availableYears={data.available_years || []}
        />

        {/* Date range (only when both year AND month filters are empty) */}
        {selectedYears.length === 0 && selectedMonths.length === 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, minWidth: 55 }}>نطاق:</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ padding: "7px 10px", fontSize: 12, borderRadius: 7, border: "1px solid #e2e8f0", fontFamily: "inherit" }} />
            <span style={{ fontSize: 11, color: "#94a3b8" }}>إلى</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ padding: "7px 10px", fontSize: 12, borderRadius: 7, border: "1px solid #e2e8f0", fontFamily: "inherit" }} />
          </div>
        )}

        {/* Row 5: Search + Export */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, minWidth: 55 }}>🔍 بحث:</span>
          <input type="text" placeholder="برمز الوحدة / العميل / المشروع..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: "7px 10px", fontSize: 12, borderRadius: 7, border: "1px solid #e2e8f0", fontFamily: "inherit", flex: 1, minWidth: 200 }} />
          <button onClick={exportCSV} style={{ padding: "7px 14px", background: "#166534", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <FiDownload size={12} /> تصدير CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
        ) : filtered.length === 0 && !Object.values(colFilters).some(v => v?.length) ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>لا توجد مدفوعات</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              {/* v3.3.999 — هيدر بنمط جدول الحجوزات: فلاتر قيم متسلسلة + فرز */}
              <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                <SortTh label="التاريخ" k="payment_date" align="right" />
                <SortTh label="المشروع" k="project_name" align="right" filter />
                <SortTh label="المبنى" k="building_name" align="right" filter />
                <SortTh label="الوحدة" k="unit_label" filter />
                <SortTh label="العميل" k="client_name" align="right" filter />
                <SortTh label="النوع" k="payment_type" filter />
                <SortTh label="المبلغ" k="amount" color="#16a34a" />
                <SortTh label="ملاحظات" align="right" />
                <SortTh label="إيصال" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>لا توجد نتائج مطابقة للفلاتر</td></tr>}
              {filtered.map(p => (
                <tr key={p.id} style={{
                  borderBottom: "1px solid #f1f5f9",
                  background: p.payment_type === "contract" ? "#faf5ff" : "transparent",
                }}>
                  <td style={{ padding: "8px 8px", fontFamily: "monospace", color: "#475569" }}>{p.payment_date?.slice(0, 10)}</td>
                  <td style={{ padding: "8px 8px", color: "#1e293b", fontWeight: 600 }}>{p.project_name}</td>
                  <td style={{ padding: "8px 8px", color: "#64748b" }}>{p.building_name || "—"}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>
                    <a href={`/projects/${p.project_id}/units/${p.unit_id}`} style={{ color: "#1e40af", textDecoration: "none", borderBottom: "1px dashed #1e40af" }}>
                      {p.unit_label || p.unit_number || `#${p.unit_id}`}
                    </a>
                  </td>
                  <td style={{ padding: "8px 8px", color: "#475569" }}>
                    {p.client_name && p.reservation_id ? (
                      <a href={`/?edit=${p.reservation_id}`} target="_blank" rel="noopener noreferrer"
                         style={{ color: "#475569", textDecoration: "none", borderBottom: "1px dashed #94a3b8" }}>
                        {p.client_name}
                      </a>
                    ) : (p.client_name || "—")}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    {p.payment_type === "contract" ? (
                      <span style={{ background: "#faf5ff", color: "#7c3aed", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                        📝 عقد جديد
                      </span>
                    ) : p.payment_type === "contract_resale" ? (
                      /* v3.4.055 — عقد جديد إعادة بيع: خارج إجمالي المدفوعات */
                      <span title="دفعة عقد إعادة بيع — لا تُحتسب ضمن إجمالي المدفوعات" style={{ background: "#fffbeb", color: "#b45309", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>
                        🔄 إعادة بيع
                      </span>
                    ) : (
                      <span style={{ background: "#ecfeff", color: "#0891b2", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                        💵 تحصيل
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", color: "#16a34a", fontWeight: 700 }}>{fmt(p.amount)} ر.س</td>
                  <td style={{ padding: "8px 8px", color: "#94a3b8", fontSize: 11 }}>{p.notes || "—"}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    {p.receipt_url ? (
                      <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", fontSize: 11 }}>📄 عرض</a>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 2. CollectionsPage — التحصيلات والديون
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// v3.3.348 — CollectionsTimelineChart
// أعمدة جنباً إلى جنب: التحصيل (الدفعات اللاحقة) + الديون الجديدة (الدفعات الآجلة)
// + خط منحنى على Y axis ثاني: إجمالي الديون المتبقية تراكمي
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// v3.4.529 — ExpectedPaymentsChart: الدفعات المتوقعة شهرًا بشهر من الشهر الحالي (12 شهرًا + لاحقًا)
// المصدر لكل وحدة بمتبقي: الدفعات المجدولة غير المسددة (أول دفعة تنتقل إلى «التاريخ المتوقع حسب التفاهمات» إن وُجد)،
// وإلا المتبقي كاملًا في شهر التفاهم؛ المتأخر يُطوى في الشهر الحالي. التمرير يُظهر الأسماء والمبالغ.
// ═══════════════════════════════════════════════════════════════
const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
function ExpectedPaymentsChart({ units, selected, onSelect }) {
  const [hoverKind, setHoverKind] = useState(null); // v3.4.535 — العمود المُمرَّر عليه (sched / late / resched)
  // v3.4.531 — ثلاث فئات: «مجدول» (أزرق) حسب الجدولة/الاتفاق، «متأخر غير معاد جدولته» (أحمر — في الشهر الحالي فقط)،
  // و«متأخر معاد جدولته» (برتقالي — في شهر «التاريخ المتوقع حسب التفاهمات»، ومنه الشهر الحالي). التمرير على عمود يُظهر قائمته وحده.
  const model = useMemo(() => {
    const num = (v) => parseFloat(v || 0) || 0;
    const now = new Date();
    const ymOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const months = Array.from({ length: 12 }, (_, i) => ymOf(new Date(now.getFullYear(), now.getMonth() + i, 1)));
    const cur = months[0], last = months[11];
    const mk = (key) => ({ key, sched: 0, late: 0, resched: 0, items: [] });
    const buckets = {}; months.forEach((m) => { buckets[m] = mk(m); });
    const later = mk("later"), undated = { total: 0, items: [] };
    // kind: "sched" | "late" (متأخر غير معاد جدولته) | "resched" (متأخر أُعيدت جدولته بتاريخ التفاهم)
    const push = (ym, item) => {
      if (!ym) { undated.total += item.amount; undated.items.push(item); return; }
      const m = ym < cur ? cur : ym;
      const bkt = m > last ? later : buckets[m];
      bkt[item.kind] += item.amount; bkt.items.push(item);
    };
    (units || []).forEach((u) => {
      if (u.is_internal) return;
      const remaining = num(u.remaining); if (remaining <= 0) return;
      const base = { id: u.id, name: u.client_name || "—", unit: u.unit_label || "", project: u.project_name || "" };
      const sched = Array.isArray(u.unpaid_schedule) ? u.unpaid_schedule.map((x) => ({ date: String(x.due_date || "").slice(0, 10), amount: num(x.amount) })).filter((x) => x.amount > 0 && x.date) : [];
      const agreed = u.agreed_expected_date ? String(u.agreed_expected_date).slice(0, 10) : "";
      if (sched.length) {
        let sum = 0;
        sched.forEach((x, i) => {
          const amt = Math.max(0, Math.min(x.amount, remaining - sum)); if (amt <= 0) return; sum += amt;
          const overdue = x.date < today;
          const useAgreed = i === 0 && !!agreed;
          const eff = useAgreed ? agreed : x.date;
          const kind = overdue ? (useAgreed ? "resched" : "late") : ((useAgreed && eff < today) ? "resched" : "sched");
          push(eff.slice(0, 7), { ...base, amount: amt, date: eff, orig: x.date, kind });
        });
        if (remaining - sum > 0.5) push("", { ...base, amount: remaining - sum, date: "", kind: "sched" });
      } else if (agreed) push(agreed.slice(0, 7), { ...base, amount: remaining, date: agreed, kind: agreed < today ? "late" : "sched" });
      else push("", { ...base, amount: remaining, date: "", kind: "sched" });
    });
    const rows = months.map((m) => { const [y, mm] = m.split("-"); return { ...buckets[m], label: `${MONTHS_AR[parseInt(mm, 10) - 1]} ${y}`, isCurrent: m === cur }; });
    if (later.sched + later.late + later.resched > 0) rows.push({ ...later, label: "لاحقًا", isCurrent: false });
    rows.forEach((r) => { r.total = r.sched + r.late + r.resched; r.items.sort((x, y) => y.amount - x.amount); });
    const sumS = rows.reduce((acc, r) => acc + r.sched, 0), sumL = rows.reduce((acc, r) => acc + r.late, 0), sumR = rows.reduce((acc, r) => acc + r.resched, 0);
    return { rows, total: sumS + sumL + sumR, sumS, sumL, sumR, undated, count: rows.reduce((acc, r) => acc + r.items.length, 0) };
  }, [units]);
  const KIND = { sched: ["🟦 مجدول حسب الاتفاق", "#0369a1"], late: ["🟥 متأخر غير معاد جدولته", "#b91c1c"], resched: ["🟧 متأخر معاد جدولته", "#b45309"] };
  const ForecastTip = ({ active, payload }) => { // v3.4.535 — التلميح لكل عمود: نقرأ العمود المُمرَّر عليه من حالة hoverKind لا من أول عنصر
    if (!active || !payload?.length) return null;
    const kind = hoverKind || payload[0].dataKey;
    const r = (payload.find((p) => p.dataKey === kind) || payload[0]).payload;
    const list = r.items.filter((it) => it.kind === kind); const [title, color] = KIND[kind] || KIND.sched;
    const two = list.length > 9; const top = list; // v3.4.536 — كل الأسماء؛ عمودان عند كثرتها
    return (
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", boxShadow: "0 12px 32px rgba(0,0,0,.18)", minWidth: two ? 600 : 280, maxWidth: two ? 640 : 380, direction: "rtl", fontSize: 11.5 }}>
        <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 4 }}>{r.label}</div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontWeight: 800, color, borderBottom: "1px solid #f1f5f9", paddingBottom: 3 }}><span>{title}</span><span>{fmt(r[kind] || 0)} ر.س · {list.length}</span></div>
        <div style={{ display: "grid", gridTemplateColumns: two ? "1fr 1fr" : "1fr", columnGap: 18 }}>
          {top.map((it, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0", borderTop: "1px dashed #f1f5f9" }}>
              <span style={{ color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name} <span style={{ color: "#94a3b8" }}>· {it.unit}{kind === "resched" && it.orig ? ` · كان ${it.orig}` : ""}</span></span>
              <span style={{ fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" }}>{fmt(it.amount)}</span>
            </div>
          ))}
        </div>
        {!list.length && <div style={{ color: "#94a3b8", marginTop: 4 }}>لا شيء</div>}
      </div>
    );
  };
  const isSel = (r, kind) => !!selected && selected.key === r.key && selected.kind === kind;
  const pick = (d, kind) => { const r = d && (d.payload || d); if (!r || !r.key) return null; if (isSel(r, kind)) return null; const ids = r.items.filter((it) => it.kind === kind).map((it) => it.id); return { key: r.key, kind, label: r.label, ids, total: r[kind] }; };
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 2 }}>📅 الدفعات المتوقعة للأشهر القادمة
            {selected && <span style={{ marginInlineStart: 10, display: "inline-flex", alignItems: "center", gap: 6, background: selected.kind === "late" ? "#fee2e2" : "#e0f2fe", color: selected.kind === "late" ? "#b91c1c" : "#075985", borderRadius: 999, padding: "2px 10px", fontSize: 11.5, fontWeight: 800 }}>
              🔍 القائمة مصفّاة: {selected.label} — {selected.kind === "late" ? "متأخر غير معاد جدولته" : selected.kind === "resched" ? "متأخر معاد جدولته" : "مجدول"} ({selected.ids.length} وحدة · {fmt(selected.total)} ر.س)
              <button type="button" onClick={() => onSelect && onSelect(null)} style={{ border: 0, background: "none", cursor: "pointer", color: "inherit", fontWeight: 900 }}>✕</button>
            </span>}
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>🟦 مجدول حسب الاتفاق • 🟥 متأخر غير معاد جدولته (الشهر الحالي) • 🟧 متأخر معاد جدولته حسب «التاريخ المتوقع حسب التفاهمات» • مرّر الفأرة على العمود لرؤية قائمته • انقر العمود لتصفية القائمة أسفل</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ textAlign: "left", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "8px 14px" }}>
            <div style={{ fontSize: 10, color: "#0369a1", fontWeight: 700 }}>إجمالي المستحق للأشهر القادمة</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#0c4a6e", lineHeight: 1.2 }}>{fmt(model.total)} <span style={{ fontSize: 12, fontWeight: 600 }}>ر.س</span></div>
            <div style={{ fontSize: 10, color: "#64748b" }}>🟦 مجدول {fmt(model.sumS)} • 🟥 متأخر {fmt(model.sumL)} • 🟧 معاد جدولته {fmt(model.sumR)} • {model.count} دفعة{model.undated.total > 0 ? ` • غير مجدول: ${fmt(model.undated.total)} (${model.undated.items.length})` : ""}</div>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart data={model.rows} margin={{ top: 18, right: 10, left: 10, bottom: 8 }} barGap={2} barCategoryGap="22%">
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-20} textAnchor="end" height={48} />
          <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => (v >= 1000000 ? (v / 1000000).toFixed(1) + "م" : v >= 1000 ? Math.round(v / 1000) + "ك" : v)} width={44} />
          <Tooltip shared={false} content={<ForecastTip />} cursor={{ fill: "rgba(14,165,233,.08)" }} wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }} allowEscapeViewBox={{ x: true, y: true }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {/* v3.4.530 — النقر على عمود يصفّي القائمة أسفل بوحدات ذلك الشهر (مجدول أو متأخر)؛ النقر مجددًا يلغي */}
          <Bar dataKey="sched" name="مجدول حسب الاتفاق" radius={[6, 6, 0, 0]} cursor="pointer" label={{ position: "top", fontSize: 9, fill: "#0369a1", formatter: (v) => (v > 0 ? fmt(v) : "") }}
            onClick={(d) => onSelect && d && onSelect(pick(d, "sched"))} onMouseEnter={() => setHoverKind("sched")}>
            {model.rows.map((r, i) => <Cell key={i} fill={r.isCurrent ? "#0284c7" : r.key === "later" ? "#94a3b8" : "#7dd3fc"} stroke={isSel(r, "sched") ? "#0f172a" : "none"} strokeWidth={isSel(r, "sched") ? 2.5 : 0} opacity={selected && !isSel(r, "sched") ? 0.45 : 1} />)}
          </Bar>
          <Bar dataKey="late" name="متأخر غير معاد جدولته" radius={[6, 6, 0, 0]} cursor="pointer" label={{ position: "top", fontSize: 9, fill: "#b91c1c", formatter: (v) => (v > 0 ? fmt(v) : "") }}
            onClick={(d) => onSelect && d && onSelect(pick(d, "late"))} onMouseEnter={() => setHoverKind("late")}>
            {model.rows.map((r, i) => <Cell key={i} fill="#ef4444" stroke={isSel(r, "late") ? "#0f172a" : "none"} strokeWidth={isSel(r, "late") ? 2.5 : 0} opacity={selected && !isSel(r, "late") ? 0.45 : 1} />)}
          </Bar>
          <Bar dataKey="resched" name="متأخر معاد جدولته" radius={[6, 6, 0, 0]} cursor="pointer" label={{ position: "top", fontSize: 9, fill: "#b45309", formatter: (v) => (v > 0 ? fmt(v) : "") }}
            onClick={(d) => onSelect && d && onSelect(pick(d, "resched"))} onMouseEnter={() => setHoverKind("resched")}>
            {model.rows.map((r, i) => <Cell key={i} fill="#f59e0b" stroke={isSel(r, "resched") ? "#0f172a" : "none"} strokeWidth={isSel(r, "resched") ? 2.5 : 0} opacity={selected && !isSel(r, "resched") ? 0.45 : 1} />)}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function CollectionsTimelineChart({ data, projects = [], chartProjectId, setChartProjectId }) {
  const formattedData = useMemo(() => {
    const monthNames = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
    return (data || []).map(d => {
      const [y, m] = d.month.split("-");
      const mIdx = parseInt(m) - 1;
      return { ...d, label: `${monthNames[mIdx]} ${y}` };
    });
  }, [data]);

  // v3.3.353 — Header مع dropdown مشروع
  const chartHeader = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 2 }}>📊 الحركة الزمنية للتحصيل والديون</div>
        <div style={{ fontSize: 11, color: "#64748b" }}>تحصيل + ديون جديدة شهرياً • إجمالي الديون المتبقية تراكمياً • {formattedData.length} شهر</div>
      </div>
      {setChartProjectId && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
          <span style={{ color: "#64748b", fontWeight: 600 }}>🏢 المشروع:</span>
          <select value={chartProjectId || ""} onChange={e => setChartProjectId(e.target.value || null)}
            style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11, fontFamily: "inherit", background: "#fff", cursor: "pointer", minWidth: 140 }}>
            <option value="">📊 كل المشاريع</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  if (!data || data.length === 0) {
    return (
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        {chartHeader}
        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: 20 }}>
          📊 لا توجد بيانات كافية لعرض الرسم البياني
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      {chartHeader}
      <div style={{ width: "100%", height: 360 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={formattedData} margin={{ top: 10, right: 50, left: 10, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#64748b" }}
              angle={-40}
              textAnchor="end"
              interval="preserveStartEnd"
              height={60}
            />
            {/* Y axis اليمين: للأعمدة (التحصيل والديون الجديدة) */}
            <YAxis
              yAxisId="bars"
              orientation="right"
              tick={{ fontSize: 10, fill: "#64748b" }}
              tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}م` : v >= 1000 ? `${(v/1000).toFixed(0)}ك` : v}
            />
            {/* Y axis اليسار: للخط المنحنى (إجمالي الديون التراكمي) */}
            <YAxis
              yAxisId="line"
              orientation="left"
              tick={{ fontSize: 10, fill: "#fb7185" }}
              tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}م` : v >= 1000 ? `${(v/1000).toFixed(0)}ك` : v}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const get = (key) => {
                  const item = payload.find(p => p.dataKey === key);
                  return item ? Number(item.value) || 0 : 0;
                };
                const collections = get("collections");
                const newDebt = get("new_debt");
                const cumDebt = get("cumulative_debt");
                const fmtN = (n) => Math.round(n).toLocaleString("en-US");
                return (
                  <div style={{
                    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
                    padding: "10px 14px", fontFamily: "inherit", fontSize: 11,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)", minWidth: 200,
                  }}>
                    <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 12, marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #f1f5f9" }}>
                      📅 {label}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                      <span style={{ color: "#10b981", fontSize: 10 }}>💵 التحصيل المُسدَّد</span>
                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: 11 }}>{fmtN(collections)} ر.س</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ color: "#3b82f6", fontSize: 10 }}>📋 ديون جديدة (آجلة)</span>
                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: 11 }}>{fmtN(newDebt)} ر.س</span>
                    </div>
                    <div style={{ paddingTop: 6, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "#fb7185", fontSize: 10, fontWeight: 700 }}>📈 إجمالي الديون المتبقية</span>
                      <span style={{ color: "#fb7185", fontWeight: 800, fontSize: 13 }}>{fmtN(cumDebt)} ر.س</span>
                    </div>
                  </div>
                );
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} iconType="circle" />
            {/* أعمدة جنباً إلى جنب */}
            <Bar yAxisId="bars" dataKey="collections" name="💵 التحصيل المُسدَّد" fill="#bbf7d0" stroke="#34d399" strokeWidth={1} barSize={12} />
            <Bar yAxisId="bars" dataKey="new_debt" name="📋 ديون جديدة" fill="#bfdbfe" stroke="#60a5fa" strokeWidth={1} barSize={12} />
            {/* خط منحنى على Y axis ثاني */}
            <Line
              yAxisId="line"
              type="monotone"
              dataKey="cumulative_debt"
              name="📈 إجمالي الديون المتبقية"
              stroke="#fb7185"
              strokeWidth={2.5}
              dot={{ r: 3, fill: "#fb7185" }}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ marginTop: 8, fontSize: 10, color: "#94a3b8", textAlign: "center" }}>
        💡 الأعمدة (يمين): التحصيل + الديون الجديدة شهرياً • الخط المرجاني (يسار): إجمالي الديون المتبقية تراكمياً
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 2. CollectionsPage — التحصيلات والديون
// ═══════════════════════════════════════════════════════════════════
export function CollectionsPage() {
  const navigate = useNavigate(); // v3.4.801 — الضغط على الصف يفتح تحليل تحصيل العميل
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && !user?.impersonating;
  const showStats = isAdmin || user?.can_view_collections_stats !== false;
  // v3.3.341 — صلاحيات منفصلة لكل بطاقة
  const showCardUnitsSold = isAdmin || user?.can_view_collections_units_sold !== false;
  const showCardTotalValue = isAdmin || user?.can_view_collections_total_value !== false;
  const showCardTotalPaid = isAdmin || user?.can_view_collections_total_paid !== false;
  // v3.3.608 — بقية مكوّنات الصفحة
  const showCardRemaining = isAdmin || user?.can_view_collections_remaining !== false;
  const showCardUnitsWithDebt = isAdmin || user?.can_view_collections_units_with_debt !== false;
  const showCardOverdue = isAdmin || user?.can_view_collections_overdue !== false;
  const showTimeline = isAdmin || user?.can_view_collections_timeline !== false;
  const projects = useProjects();
  const [projectId, setProjectId] = useState(null);
  const [data, setData] = useState({ units: [], summary: {} });
  const [forecastSel, setForecastSel] = useState(null); // v3.4.530 — عمود مختار من مخطط الدفعات المتوقعة { key, kind, label, ids, idSet, total }
  const [allUnits, setAllUnits] = useState(null); // v3.4.498 — آخر تحميل لكل المشاريع (للعدّادات)
  // v3.4.210 — عمودا العقد: رابط مستند العقد المرفوع على الوحدة + تاريخ توقيع العقد (يُعدّل مباشرة من الصف)
  const canEditContract = !!user && (user.role === "admin" || user.is_admin || user.can_edit_info || user.projects_can_edit || user.collections_can_edit_contract_date); // v3.4.227 — صلاحية مستقلة من إدارة المستخدمين
  const patchUnitRow = (unitId, patch) => setData(d => ({ ...d, units: (d.units || []).map(x => x.id === unitId ? { ...x, ...patch } : x) }));
  // v3.4.226 — «متوقع سداده هذا الشهر»: خانة اختيار في أول عمود + مجموع المتبقي للوحدات المعلّمة في البطاقات العلوية
  const canMarkExpected = !!user && (user.role === "admin" || user.is_admin || user.collections_can_mark_expected); // v3.4.239 — صلاحية مستقلة للعلامة
  const toggleExpected = async (u, on) => {
    patchUnitRow(u.id, { expected_this_month: on });
    try { await financialAnalysis.setExpectedThisMonth(u.id, on); toast.success(on ? "عُلّمت: متوقع سدادها هذا الشهر" : "أُلغيت العلامة"); }
    catch (e) { patchUnitRow(u.id, { expected_this_month: !on }); toast.error(e.response?.data?.error || "فشل الحفظ"); }
  };
  // v3.4.459 — إخفاء أعمدة جدول التحصيلات (يُحفظ في المتصفح). الرقم = ترتيب العمود في الجدول
  const COLL_COLS = [[1, "👤 موظف الشركة"], [2, "✅ متوقع هذا الشهر"], [3, "المشروع"], [4, "الوحدة"], [5, "العميل"], [6, "القيمة"], [7, "المُحصّل"], [8, "متأخرات"], [9, "المتبقي"], [10, "نسبة التحصيل"], [11, "دفعات السداد"], [12, "📄 العقد"], [13, "توقيع العقد"], [14, "الاستحقاق القادم"], [15, "المتوقع حسب التفاهمات"], [16, "آخر دفعة"], [17, "ملاحظات"]]; // v3.4.510 — عمود 15 جديد
  // v3.4.510 — المفتاح v2: أُضيف عمود رقم 15، فالأعمدة المخفية المحفوظة سابقًا (15 فأعلى) تُزاح خانة واحدة
  const [hiddenCols, setHiddenCols] = useState(() => { try {
    const v2 = localStorage.getItem("collections_hidden_cols_v2");
    const v = JSON.parse(v2 || localStorage.getItem("collections_hidden_cols") || "[]");
    const arr = Array.isArray(v) ? v.filter(n => Number.isInteger(n)) : [];
    return v2 ? arr : arr.map(n => (n >= 15 ? n + 1 : n));
  } catch { return []; } });
  const [colsOpen, setColsOpen] = useState(false);
  // v3.4.461 — إخفاء وحدات موظفي الشركة من كل القوائم (إلا عند اختيار فلتر «موظفو الشركة») — مفعّل افتراضيًا ويُحفظ في المتصفح
  const [hideEmp, setHideEmp] = useState(() => { try { return localStorage.getItem("collections_hide_emp") !== "0"; } catch { return true; } });
  const setHideEmpSaved = (v) => { setHideEmp(v); try { localStorage.setItem("collections_hide_emp", v ? "1" : "0"); } catch { /* تخزين المتصفح غير متاح */ } };
  const saveHidden = (arr) => { setHiddenCols(arr); try { localStorage.setItem("collections_hidden_cols_v2", JSON.stringify(arr)); } catch { /* تخزين المتصفح غير متاح */ } };
  const toggleCol = (n) => saveHidden(hiddenCols.includes(n) ? hiddenCols.filter(x => x !== n) : [...hiddenCols, n]);
  const lead = 5 - hiddenCols.filter(n => n <= 5).length; // خلية «الإجمالي» تغطي أول 5 أعمدة
  const hideCss = hiddenCols.map(n => `.coll-tbl > thead > tr > th:nth-child(${n}), .coll-tbl > tbody > tr > td:nth-child(${n})${n > 5 ? `, .coll-tbl > tfoot > tr > td:nth-child(${n - 4})` : ""} { display: none; }`).join("\n") + (lead === 0 ? "\n.coll-tbl > tfoot > tr > td:nth-child(1) { display: none; }" : "");
  // v3.4.458 — علامة «موظف الشركة» (مثل «متوقع هذا الشهر»)
  const toggleCompanyEmployee = async (u, on) => {
    patchUnitRow(u.id, { is_company_employee: on });
    try { await financialAnalysis.setCompanyEmployee(u.id, on); toast.success(on ? "عُلّمت: موظف الشركة" : "أُلغيت العلامة"); }
    catch (e) { patchUnitRow(u.id, { is_company_employee: !on }); toast.error(e.response?.data?.error || "فشل الحفظ"); }
  };
  // v3.4.510 — «التاريخ المتوقع حسب التفاهمات»: يُدخل ويُعدّل يدويًا من الترس الرمادي بجانب التاريخ
  const canEditAgreed = canMarkExpected || !!(user && user.collections_can_edit_contract_date);
  const [agreedEdit, setAgreedEdit] = useState(null); // رقم الوحدة التي يُعدَّل تاريخها الآن
  const saveAgreedDate = async (u, value) => {
    setAgreedEdit(null);
    const v = value || null;
    if ((u.agreed_expected_date || null) === v) return;
    try {
      const r = await financialAnalysis.setAgreedDate(u.id, v);
      patchUnitRow(u.id, { agreed_expected_date: v, agreed_expected_by: r.data?.agreed_expected_by || null, agreed_expected_at: r.data?.agreed_expected_at || null });
      toast.success(v ? "حُفظ التاريخ المتوقع حسب التفاهمات" : "أُزيل التاريخ المتوقع");
    } catch (e) { toast.error(e.response?.data?.error || "فشل حفظ التاريخ"); }
  };
  const saveContractDate = async (u, value) => {
    const v = value || null;
    if ((u.contract_signed_date ? String(u.contract_signed_date).slice(0, 10) : null) === v) return;
    try { await financialAnalysis.setContractDate(u.id, v); patchUnitRow(u.id, { contract_signed_date: v }); toast.success(v ? "حُفظ تاريخ توقيع العقد" : "أُزيل تاريخ التوقيع"); }
    catch (e) { toast.error(e.response?.data?.error || "فشل حفظ التاريخ"); }
  };
  const uploadContract = async (u, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("doc_type", "عقد البيع"); fd.append("notes", "رُفع من صفحة التحصيلات والديون");
    const t = toast.loading("جارٍ رفع العقد…");
    try {
      const r = await projectsApi.uploadUnitDocument(u.id, fd);
      const d = r.data?.document || r.data || {};
      patchUnitRow(u.id, { contract_url: d.drive_web_link || d.file_url || null, contract_name: d.file_name || file.name, contract_uploaded_at: d.uploaded_at || new Date().toISOString(), contract_signed_date: u.contract_signed_date || new Date().toISOString().slice(0, 10) }); // v3.4.215 — تاريخ التوقيع = تاريخ الرفع افتراضيًا
      toast.success("رُفع العقد ✓", { id: t });
    } catch (e) { toast.error(e.response?.data?.error || "فشل رفع العقد", { id: t }); }
  };
  const [loading, setLoading] = useState(false);
  // v3.3.341 — افتراضي: عرض الوحدات بمتبقي فقط
  const [showOnlyDebt, setShowOnlyDebt] = useState(true);
  // v3.3.348 — Collections timeline chart data
  const [timeline, setTimeline] = useState([]);
  // v3.3.353 — فلتر مشروع مستقل للـ chart
  const [chartProjectId, setChartProjectId] = useState(null);
  // v3.3.372 — WhatsApp chat modal
  const [chatTarget, setChatTarget] = useState(null);
  // v3.4.691 — نص تذكير الدفعة القادمة + إعداداته
  const [reminderCfg, setReminderCfg] = useState(null);
  const [showReminderCfg, setShowReminderCfg] = useState(false);
  useEffect(() => { financialAnalysis.getReminderSettings().then(r => setReminderCfg(r.data.settings)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = projectId ? { project_id: projectId } : {};
    financialAnalysis.getCollections(params)
      .then(r => { setData(r.data); if (!projectId) setAllUnits(r.data?.units || []); }) /* v3.4.498 — وحدات كل المشاريع لعدّادات أزرار المشاريع */
      .catch(() => toast.error("فشل تحميل بيانات التحصيل"))
      .finally(() => setLoading(false));
  }, [projectId]);

  // v3.3.348 — تحميل بيانات الرسم البياني الزمني (يتأثر بـ chartProjectId المستقل)
  useEffect(() => {
    const params = chartProjectId ? { project_id: chartProjectId } : {};
    financialAnalysis.getCollectionsTimeline(params)
      .then(r => setTimeline(r.data.timeline || []))
      .catch(() => setTimeline([]));
  }, [chartProjectId]);

  // v3.3.989 — بحث + فلتر عميل + فلتر حالة (فوق فلتر «فقط الوحدات بمتبقي» الموجود)
  const [search, setSearch] = useState("");
  const [clientF, setClientF] = useState("");
  const [statusF, setStatusF] = useState(""); // "" | overdue | paid | zero | internal
  // v3.3.991 — فلاتر أعمدة بنمط جدول الحجوزات (قيم متعددة لكل عمود)
  const [colFilters, setColFilters] = useState({});
  // v3.4.693 — فلتر سنة/شهر استحقاق الدفعات المجدولة غير المسددة (بطلب المالك: صف أزرار بالسنة والشهر وبجانب كل شهر عدد الدفعات)
  const [dueYear, setDueYear] = useState(String(new Date().getFullYear()));
  const [dueMonth, setDueMonth] = useState(null); // "YYYY-MM" أو null
  const DUE_MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
  const unpaidMonthsOf = (u) => (Array.isArray(u.unpaid_schedule) ? u.unpaid_schedule : []).map((x) => String(x.due_date || "").slice(0, 7)).filter((m) => /^\d{4}-\d{2}$/.test(m));
  const getColVal = (u, k) => (k === "is_company_employee" ? (u.is_company_employee ? "👤 موظف الشركة" : "ليس موظفًا") : k === "unit_label" ? (u.unit_label || u.unit_number || "") : k === "expected_this_month" ? (u.expected_this_month ? "✅ متوقع هذا الشهر" : "غير متوقع") : (u[k] ?? "")); // v3.4.269 — قيم قابلة للفلترة للعمود الأول
  // v3.3.994 — تطبيق كل الفلاتر مع استثناء فلتر واحد (exceptKey): قوائم القيم تعرض
  // فقط ما هو ظاهر فعليًا بالقائمة الحالية (نمط إكسل)، لا كامل قاعدة البيانات
  // v3.4.455 — حالات الوحدة (أزرار الفلتر أعلى الجدول) — نفس الشروط السابقة
  const STATUS_PILLS = [["", "الكل"], ["expected", "✅ متوقع سداده هذا الشهر"], ["overdue", "⚠ عليها متأخرات"], ["zero", "✗ بدون أي سداد"], ["employee", "👤 موظفو الشركة"]]; // v3.4.456 — v3.4.458: موظفو الشركة — «المتوقع» أولًا، وأُزيل «مسددة بالكامل» و«ملك الشركة»
  const statusMatch = (u, k) => {
    if (!k) return true;
    if (k === "overdue") return parseFloat(u.overdue_amount || 0) > 0;
    if (k === "expected") return !!u.expected_this_month; // v3.4.269
    if (k === "paid") return !u.is_internal && !(u.remaining > 0);
    if (k === "zero") return !u.is_internal && !(Number(u.total_paid) > 0);
    if (k === "internal") return !!u.is_internal;
    if (k === "employee") return !!u.is_company_employee; // v3.4.458
    return true;
  };
  const applyFilters = (units, exceptKey) => units
    .filter(u => exceptKey === "forecast" || !forecastSel || forecastSel.idSet.has(u.id)) /* v3.4.530 — عمود مختار من مخطط الدفعات المتوقعة */
    .filter(u => exceptKey === "toolbar_due" || !dueMonth || (dueMonth.startsWith("all-") ? unpaidMonthsOf(u).some((m) => m.slice(5) === dueMonth.slice(4)) : unpaidMonthsOf(u).includes(dueMonth))) /* v3.4.693 — شهر استحقاق مختار (v3.4.696: «الكل» للسنوات = الشهر في أي سنة) */
    .filter(u => exceptKey === "toolbar_debt" || (showOnlyDebt ? u.remaining > 0 : true)) /* v3.4.462 */
    .filter(u => Object.entries(colFilters).every(([k, vals]) => k === exceptKey || !vals?.length || vals.includes(String(getColVal(u, k)))))
    .filter(u => exceptKey === "toolbar_client" || !clientF || u.client_name === clientF)
    .filter(u => exceptKey === "toolbar_status" || exceptKey === "toolbar_status_all" || statusMatch(u, statusF)) /* v3.4.455 */
    .filter(u => !hideEmp || !u.is_company_employee || statusF === "employee" || exceptKey === "toolbar_status_all" || exceptKey === "toolbar_emp_hide") /* v3.4.461 */
    .filter(u => {
      const q = search.trim();
      if (!q) return true;
      return String(u.client_name || "").includes(q) || String(u.unit_label || u.unit_number || "").includes(q) || String(u.project_name || "").includes(q);
    });
  const filtered = applyFilters(data.units, null);
  // v3.4.462 — أرقام البطاقات تستثني وحدات موظفي الشركة المخفية (بنفس قواعد الخادم: بدون البيع الداخلي)
  const empHidden = (u) => hideEmp && !!u.is_company_employee && statusF !== "employee";
  const visUnits = (data.units || []).filter(u => !empHidden(u));
  const extUnits = visUnits.filter(u => !u.is_internal);
  const sumV = (() => {
    const num = (v) => parseFloat(v || 0) || 0;
    const sold = extUnits.reduce((a, u) => a + num(u.effective_price), 0), paid = extUnits.reduce((a, u) => a + num(u.total_paid), 0);
    return { ...data.summary, total_units: visUnits.length, total_sold_value: sold, total_paid: paid,
      total_remaining: extUnits.reduce((a, u) => a + num(u.remaining), 0), total_overdue: extUnits.reduce((a, u) => a + num(u.overdue_amount), 0),
      units_with_debt: extUnits.filter(u => num(u.remaining) > 0).length, units_overdue: extUnits.filter(u => num(u.overdue_amount) > 0).length,
      collection_pct: sold > 0 ? (paid / sold) * 100 : 0 };
  })();
  const expectedRows = extUnits.filter(u => u.expected_this_month);
  const expectedTotal = expectedRows.reduce((s2, u) => s2 + (parseFloat(u.overdue_amount || 0) > 0 ? parseFloat(u.overdue_amount) : 0), 0); // v3.4.267 — مجموع المتأخرات فقط
  const debtCount = applyFilters(data.units, "toolbar_debt").filter(u => u.remaining > 0).length; // v3.4.462 — عدد الوحدات بمتبقي ضمن الفلاتر الحالية
  const hiddenEmpCount = applyFilters(data.units, "toolbar_emp_hide").filter(empHidden).length; // v3.4.462 — عدد وحدات الموظفين المخفية
  const distinctOf = (k) => [...new Set(applyFilters(data.units, k).map(u => String(getColVal(u, k))).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ar"));
  const clientOptions = [...new Set(applyFilters(data.units, "toolbar_client").map(u => u.client_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ar"));

  // v3.3.668 — فرز الأعمدة: ضغطة على رأس العمود تفرز تنازلياً، وضغطة ثانية تعكس الاتجاه
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const NUM_SORT_KEYS = ["effective_price", "total_paid", "remaining", "collection_pct", "scheduled_paid_count", "overdue_amount"];
  const DATE_SORT_KEYS = ["next_due_date", "last_payment_date", "agreed_expected_date"]; // v3.4.108 — فرز زمني حقيقي؛ الفارغ دائمًا في الآخر
  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        if (DATE_SORT_KEYS.includes(sortKey)) {
          const ta = a[sortKey] ? new Date(a[sortKey]).getTime() : null, tb = b[sortKey] ? new Date(b[sortKey]).getTime() : null;
          if (ta === null && tb === null) return 0;
          if (ta === null) return 1;
          if (tb === null) return -1;
          return sortDir === "asc" ? ta - tb : tb - ta;
        }
        if (NUM_SORT_KEYS.includes(sortKey)) {
          const va = Number(a[sortKey]) || 0, vb = Number(b[sortKey]) || 0;
          return sortDir === "asc" ? va - vb : vb - va;
        }
        const va = String(a[sortKey] || ""), vb = String(b[sortKey] || "");
        return sortDir === "asc" ? va.localeCompare(vb, "ar") : vb.localeCompare(va, "ar");
      })
    : filtered;
  // v3.3.991 — رأس عمود بنمط الحجوزات: ⌄ يفتح قائمة ترتيب + فلتر قيم (للأعمدة النصية)
  const SortTh = ({ label, k, align = "center", color = "#475569", filter = false }) => (
    k ? (
      <GenericHeaderMenu label={label} colKey={k} sortBy={sortKey} sortDir={sortDir}
        onSort={(key, dir) => { setSortKey(key); setSortDir(dir); }}
        values={filter ? distinctOf(k) : null}
        activeFilter={colFilters[k] || []}
        onFilter={(key, vals) => setColFilters(f => ({ ...f, [key]: vals }))}
        align={align} color={color} />
    ) : (
      <th style={{ padding: "10px 8px", textAlign: align, fontWeight: 700, color, whiteSpace: "nowrap" }}>{label}</th>
    )
  );

  // ─── Export helpers ───
  const exportCSV = () => {
    const headers = ["المشروع", "الوحدة", "العميل", "القيمة", "المُحصّل", "المتبقي", "نسبة التحصيل", "الدفعات المسددة", "إجمالي الدفعات", "الدفعة القادمة", "المتوقع حسب التفاهمات", "متأخرات", "مبلغ المتأخرات", "آخر دفعة"];
    const rows = sorted.map(u => [ // v3.4.108 — التصدير بترتيب الشاشة
      u.project_name,
      u.unit_label || u.unit_number || "",
      u.is_internal ? "(ملك الشركة)" : (u.client_name || ""),
      u.effective_price,
      u.total_paid,
      u.is_internal ? "ملك الشركة" : u.remaining,
      u.is_internal ? "" : `${Math.round(u.collection_pct)}%`,
      u.scheduled_paid_count || 0,
      u.scheduled_total_count || 0,
      u.next_due_date?.slice(0, 10) || "",
      u.agreed_expected_date || "", // v3.4.510
      u.overdue_count || 0,
      u.overdue_amount || 0,
      u.last_payment_date?.slice(0, 10) || "",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `collections_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("فشل فتح نافذة الطباعة"); return; }
    // v3.4.108 — بترتيب الشاشة
    const rowsHtml = sorted.map(u => `
      <tr${u.is_internal ? ' style="background:#faf5ff"' : (parseFloat(u.overdue_amount || 0) > 0 ? ' style="background:#fff7ed"' : "")}>
        <td>${u.project_name || ""}</td>
        <td style="text-align:center;font-weight:700">${u.unit_label || u.unit_number || ""}${u.is_internal ? " (داخلي)" : ""}</td>
        <td>${u.is_internal ? "— ملك الشركة —" : (u.client_name || "—")}</td>
        <td style="text-align:center">${fmt(u.effective_price)}</td>
        <td style="text-align:center;color:#16a34a">${fmt(u.total_paid)}</td>
        <td style="text-align:center;color:${u.is_internal ? "#7f1d1d" : (u.remaining > 0 ? "#dc2626" : "#16a34a")};font-weight:700">
          ${u.is_internal ? "ملك الشركة" : (u.remaining > 0 ? fmt(u.remaining) : "✓ مسدد")}
        </td>
        <td style="text-align:center">${u.is_internal ? "—" : `${Math.round(u.collection_pct)}%`}</td>
        <td style="text-align:center">${u.scheduled_total_count ? `${u.scheduled_paid_count}/${u.scheduled_total_count}` : "—"}</td>
        <td style="text-align:center">${u.next_due_date?.slice(0, 10) || "—"}</td>
        <td style="text-align:center">${u.is_internal ? "—" : (u.agreed_expected_date || "—")}</td>
        <td style="text-align:center;color:#f59e0b">${parseFloat(u.overdue_amount || 0) > 0 ? `${fmt(u.overdue_amount)}` : "—"}</td>
      </tr>
    `).join("");
    win.document.write(`
      <!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="utf-8">
      <title>تقرير التحصيلات والديون</title>
      <style>
        body { font-family: system-ui, -apple-system, sans-serif; padding: 20px; direction: rtl; }
        h1 { font-size: 20px; color: #1e293b; margin-bottom: 6px; }
        .subtitle { color: #64748b; font-size: 12px; margin-bottom: 20px; }
        .summary { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
        .card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; min-width: 130px; }
        .card-label { font-size: 10px; color: #94a3b8; font-weight: 600; }
        .card-value { font-size: 16px; font-weight: 800; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { padding: 8px 6px; border-bottom: 1px solid #f1f5f9; }
        th { background: #f1f5f9; text-align: right; font-weight: 700; color: #475569; }
        @media print { .no-print { display: none; } body { padding: 10px; } }
      </style></head><body>
        <h1>📉 تقرير التحصيلات والديون</h1>
        <div class="subtitle">${projectId ? (projects.find(p => p.id == projectId)?.name || "مشروع محدد") : "كل المشاريع"} — ${new Date().toLocaleDateString("ar-SA")}</div>
        <div class="summary">
          <div class="card"><div class="card-label">عدد الوحدات</div><div class="card-value">${fmt(sumV.total_units)}</div></div>
          <div class="card"><div class="card-label">قيمة المبيعات</div><div class="card-value" style="color:#2563eb">${fmt(sumV.total_sold_value)}</div></div>
          <div class="card"><div class="card-label">المُحصّل (${Math.round(sumV.collection_pct || 0)}%)</div><div class="card-value" style="color:#16a34a">${fmt(sumV.total_paid)}</div></div>
          <div class="card"><div class="card-label">المتبقي</div><div class="card-value" style="color:#dc2626">${fmt(sumV.total_remaining)}</div></div>
          <div class="card"><div class="card-label">متأخرات (${sumV.units_overdue} وحدة)</div><div class="card-value" style="color:#f59e0b">${fmt(sumV.total_overdue)}</div></div>
        </div>
        <div class="no-print" style="margin-bottom:12px"><button onclick="window.print()" style="padding:8px 20px;background:#16a34a;color:#fff;border:none;border-radius:6px;font-size:13px;cursor:pointer">🖨️ طباعة / حفظ PDF</button></div>
        <table>
          <thead><tr>
            <th>المشروع</th><th>الوحدة</th><th>العميل</th><th>القيمة</th>
            <th>المُحصّل</th><th>المتبقي</th><th>نسبة</th>
            <th>الدفعات</th><th>القادمة</th><th>حسب التفاهمات</th><th>متأخرات</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader icon={<FiTrendingDown size={26} color="#dc2626" />} title="إدارة الدفعات والتحصيلات" subtitle="تقرير التحصيلات المستحقة — من تأخّر في الدفع وكم المتبقي (الوحدات المباعة فقط)" />

      {/* Summary — v3.3.318: قابلة للإخفاء حسب الصلاحية / v3.3.341: صلاحية لكل بطاقة / v3.3.343: ترتيب جديد */}
      {showStats && (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        {/* v3.4.226 — مجموع المتبقي للوحدات المعلّمة «متوقع سداده هذا الشهر» */}
        <StatCard label={`✅ متوقع سداده هذا الشهر (${data.summary.expected_month || ""})`} value={`${fmt(expectedTotal)} ر.س`} subValue={`${expectedRows.length} وحدة معلّمة`} color="#0f766e" bg="#f0fdfa" />
        {showCardOverdue && <StatCard label="متأخرات مستحقة" value={`${fmt(sumV.total_overdue)} ر.س`} subValue={`${sumV.units_overdue} وحدة متأخرة`} color="#f59e0b" bg="#fffbeb" />}
        {/* v3.3.341 — بطاقة جديدة: عدد الوحدات التي عليها متبقي (الأولى بعد متأخرات) */}
        {showCardUnitsWithDebt && <StatCard label="وحدات بمتبقي" value={fmt(sumV.units_with_debt)} subValue="وحدة عليها مبلغ" color="#7c3aed" bg="#f5f3ff" />}
        {showCardRemaining && <StatCard label="المتبقي" value={`${fmt(sumV.total_remaining)} ر.س`} subValue={`${sumV.units_with_debt} وحدة`} color="#dc2626" bg="#fef2f2" />}
        {showCardTotalPaid && <StatCard label="المُحصّل" value={`${fmt(sumV.total_paid)} ر.س`} subValue={pct(sumV.collection_pct)} color="#16a34a" bg="#f0fdf4" />}
        {showCardTotalValue && <StatCard label="قيمة المبيعات" value={`${fmt(sumV.total_sold_value)} ر.س`} color="#2563eb" bg="#eff6ff" />}
        {showCardUnitsSold && <StatCard label="الوحدات المباعة" value={fmt(sumV.total_units)} color="#1e293b" bg="#f8fafc" />}
      </div>
      )}

      {/* v3.3.348 — رسم بياني زمني للتحصيلات والديون · v3.3.608 — قابل للإخفاء حسب الصلاحية */}
      {showTimeline && (
        <CollectionsTimelineChart
          data={timeline}
          projects={projects}
          chartProjectId={chartProjectId}
          setChartProjectId={setChartProjectId}
        />
      )}
      {showTimeline && <ExpectedPaymentsChart units={applyFilters(data.units, "forecast")} selected={forecastSel} onSelect={(sel) => setForecastSel(sel ? { ...sel, idSet: new Set(sel.ids) } : null)} />}{/* v3.4.529 / v3.4.530 */}

      {/* Filters */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <FiFilter size={14} color="#64748b" />
        {(() => { /* v3.4.498 — فلتر المشاريع أزرارًا ظاهرة (بنمط أزرار الحالات): «الكل» ثم كل مشروع بعدده حسب بقية الفلاتر */
          const src = applyFilters(allUnits || data.units || [], null);
          const cnt = {}; for (const u of src) cnt[u.project_id] = (cnt[u.project_id] || 0) + 1;
          const list = projects.filter(pr => cnt[pr.id] || String(pr.id) === String(projectId));
          const pill = (key, label, n, on, onClick) => (
            <button key={key} type="button" onClick={onClick}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: on ? 800 : 600,
                border: "1.5px solid " + (on ? "#1d4ed8" : "#e2e8f0"), background: on ? "#eff6ff" : "#fff", color: on ? "#1d4ed8" : "#475569", boxShadow: on ? "0 0 0 3px rgba(29,78,216,.08)" : "none" }}>
              {label}
              {n != null && <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: "0 7px", background: on ? "#1d4ed8" : "#f1f5f9", color: on ? "#fff" : "#64748b", fontFamily: "monospace" }}>{n}</span>}
            </button>);
          return (
            <div style={{ order: -1, flexBasis: "100%", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingBottom: 10, marginBottom: 2, borderBottom: "1px dashed #e2e8f0" }}>
              <span style={{ fontSize: 13, color: "#94a3b8" }}>🏢</span>
              {pill("all", "الكل", allUnits ? src.length : null, !projectId, () => setProjectId(null))}
              {list.map(pr => pill(pr.id, pr.name, allUnits ? (cnt[pr.id] || 0) : null, String(pr.id) === String(projectId), () => setProjectId(String(pr.id) === String(projectId) ? null : pr.id)))}
            </div>
          );
        })()}
        {(() => { /* v3.4.693 — صف السنة/الشهر: عدد الدفعات المجدولة غير المسددة المستحقة في كل شهر (حسب بقية الفلاتر) */
          const src = applyFilters(data.units, "toolbar_due");
          const byMonth = {}; const byYear = {};
          const byMM = {}; let totalAll = 0; // v3.4.696 — عدّادات «الكل»
          for (const u of src) for (const m of unpaidMonthsOf(u)) { byMonth[m] = (byMonth[m] || 0) + 1; const y = m.slice(0, 4); byYear[y] = (byYear[y] || 0) + 1; byMM[m.slice(5)] = (byMM[m.slice(5)] || 0) + 1; totalAll++; }
          const allYears = dueYear === "all";
          const years = Array.from(new Set([String(new Date().getFullYear()), ...Object.keys(byYear), ...(dueMonth ? [dueMonth.slice(0, 4)] : [])])).sort();
          const chip = (key, label, n, on, onClick, color) => (
            <button key={key} type="button" onClick={onClick} disabled={!n && !on}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 999, cursor: n || on ? "pointer" : "default", fontFamily: "inherit", fontSize: 12, fontWeight: on ? 800 : 600, opacity: n || on ? 1 : 0.45,
                border: "1.5px solid " + (on ? color : "#e2e8f0"), background: on ? color + "14" : "#fff", color: on ? color : "#475569", boxShadow: on ? `0 0 0 3px ${color}14` : "none" }}>
              {label}
              <span style={{ fontSize: 10.5, fontWeight: 800, borderRadius: 999, padding: "0 6px", background: on ? color : "#f1f5f9", color: on ? "#fff" : "#64748b", fontFamily: "monospace" }}>({n || 0})</span>
            </button>);
          return (
            <>{/* v3.4.694 — السنوات في صف والأشهر في صف (بطلب المالك) */}
            <div style={{ order: -1, flexBasis: "100%", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingBottom: 8, marginBottom: 2, borderBottom: "1px dashed #e2e8f0" }}>
              <span style={{ fontSize: 13, color: "#94a3b8" }} title="سنة استحقاق الدفعات المجدولة غير المسددة">📅</span>
              {chip("yall", "الكل", totalAll, allYears, () => { setDueYear("all"); if (dueMonth && !dueMonth.startsWith("all-")) setDueMonth(null); }, "#7c3aed")}{/* v3.4.696 */}
              {years.map((y) => chip("y" + y, y, byYear[y] || 0, dueYear === y, () => { setDueYear(y); if (dueMonth && !dueMonth.startsWith(y)) setDueMonth(null); }, "#7c3aed"))}
            </div>
            <div style={{ order: -1, flexBasis: "100%", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingBottom: 10, marginBottom: 2, borderBottom: "1px dashed #e2e8f0" }}>
              <span style={{ fontSize: 13, color: "#94a3b8" }} title="شهر الاستحقاق في السنة المختارة">🗓️</span>
              {chip("mall", "الكل", allYears ? totalAll : (byYear[dueYear] || 0), !dueMonth, () => setDueMonth(null), "#0f766e")}{/* v3.4.696 */}
              {DUE_MONTHS_AR.map((name, i) => { const mm = String(i + 1).padStart(2, "0"); const k = allYears ? `all-${mm}` : `${dueYear}-${mm}`; const cnt = allYears ? (byMM[mm] || 0) : (byMonth[k] || 0); return chip(k, name, cnt, dueMonth === k, () => setDueMonth(dueMonth === k ? null : k), "#0f766e"); })}
              {dueMonth && <button type="button" onClick={() => setDueMonth(null)} style={{ fontSize: 11.5, border: "none", background: "none", color: "#b91c1c", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>✕ إلغاء فلتر الشهر</button>}
            </div>
            </>
          );
        })()}
        {/* v3.3.989 — بحث + فلتر عميل + فلتر حالة */}
        {(() => { /* v3.4.455 — أزرار حالات الوحدة مع العدد (حسب بقية الفلاتر الحالية) */
          const base = applyFilters(data.units, "toolbar_status");
          const baseAll = applyFilters(data.units, "toolbar_status_all"); // v3.4.461 — عدّاد «موظفو الشركة» يشمل وحداتهم دائمًا
          return (
            <div style={{ order: -2, /* v3.4.498 — أزرار الحالات أولًا ثم أزرار المشاريع */ flexBasis: "100%", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingBottom: 10, marginBottom: 2, borderBottom: "1px dashed #e2e8f0" }}>
              <span style={{ fontSize: 13, color: "#94a3b8" }}>🏷️</span>
              {STATUS_PILLS.map(([k, l]) => { const on = statusF === k; const n = (k === "employee" ? baseAll : base).filter(u => statusMatch(u, k)).length; return (
                <button key={k || "all"} type="button" onClick={() => setStatusF(k)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: on ? 800 : 600,
                    border: "1.5px solid " + (on ? "#0f766e" : "#e2e8f0"), background: on ? "#f0fdfa" : "#fff", color: on ? "#0f766e" : "#475569", boxShadow: on ? "0 0 0 3px rgba(15,118,110,.08)" : "none" }}>
                  {l}
                  <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: "0 7px", background: on ? "#0f766e" : "#f1f5f9", color: on ? "#fff" : "#64748b", fontFamily: "monospace" }}>{n}</span>
                </button>); })}
            </div>
          );
        })()}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 بحث: عميل / وحدة / مشروع…"
          style={{ padding: "7px 12px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 12, fontFamily: "inherit", minWidth: 190 }} />
        <select value={clientF} onChange={e => setClientF(e.target.value)}
          style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 12, fontFamily: "inherit", maxWidth: 190 }}>
          <option value="">كل العملاء</option>
          {clientOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {/* v3.4.455 — فلتر الحالة صار أزرارًا أعلى الشريط (بنمط صفحة الحجوزات) */}
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer" }}>
          <input type="checkbox" checked={showOnlyDebt} onChange={e => setShowOnlyDebt(e.target.checked)} />
          فقط الوحدات بمتبقي <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: "0 7px", background: "#f5f3ff", color: "#7c3aed", fontFamily: "monospace" }}>{debtCount}</span>{/* v3.4.462 */}
        </label>
        {/* v3.4.461 */}
        <label title="تختفي وحدات «موظف الشركة» من الجدول والأعداد والقوائم، وتظهر فقط عند اختيار «👤 موظفو الشركة»" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: hideEmp ? "#0369a1" : "#475569", fontWeight: hideEmp ? 700 : 400, cursor: "pointer" }}>
          <input type="checkbox" checked={hideEmp} onChange={e => setHideEmpSaved(e.target.checked)} style={{ accentColor: "#0369a1" }} />
          إخفاء وحدات موظفي الشركة {hideEmp && statusF !== "employee" && <span title="عدد الوحدات المخفية" style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: "0 7px", background: "#e0f2fe", color: "#0369a1", fontFamily: "monospace" }}>{hiddenEmpCount} مخفية</span>}{/* v3.4.462 */}
        </label>
        <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 700 }}>المعروض: {filtered.length} وحدة</span>
        {/* v3.4.459 — إخفاء/إظهار الأعمدة */}
        <div style={{ position: "relative" }}>
          <button type="button" onClick={() => setColsOpen(o => !o)} title="إخفاء أو إظهار أعمدة الجدول"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", border: "1px solid " + (hiddenCols.length ? "#fca5a5" : "#e2e8f0"), borderRadius: 7, background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: "#334155", fontWeight: 700 }}>
            {hiddenCols.length > 0 && <span style={{ background: "#dc2626", color: "#fff", borderRadius: 999, padding: "0 7px", fontSize: 10.5, fontWeight: 800 }}>{hiddenCols.length}</span>}
            ▥ أعمدة
          </button>
          {colsOpen && <>
            <div onClick={() => setColsOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <div style={{ position: "absolute", top: "calc(100% + 6px)", insetInlineStart: 0, zIndex: 41, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 12px 30px rgba(15,23,42,.15)", padding: 10, width: 230 }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 900, color: "#0f172a" }}>الأعمدة الظاهرة</span>
                {hiddenCols.length > 0 && <button type="button" onClick={() => saveHidden([])} style={{ marginInlineStart: "auto", fontSize: 11, border: "none", background: "none", color: "#0f766e", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>إظهار الكل</button>}
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto", display: "grid", gap: 2 }}>
                {COLL_COLS.map(([n, l]) => { const on = !hiddenCols.includes(n); return (
                  <label key={n} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", borderRadius: 6, cursor: "pointer", fontSize: 12.5, background: on ? "transparent" : "#fef2f2", color: on ? "#1e293b" : "#94a3b8" }}>
                    <input type="checkbox" checked={on} onChange={() => toggleCol(n)} style={{ accentColor: "#0f766e" }} />{l}
                  </label>); })}
              </div>
            </div>
          </>}
        </div>
        {/* Export buttons */}
        <div style={{ display: "flex", gap: 6, marginRight: "auto" }}>
          <button onClick={exportCSV} style={{ padding: "7px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <FiDownload size={12} /> Excel
          </button>
          {isAdmin && <button onClick={() => setShowReminderCfg(true)} title="قالب «تذكير بالدفعة القادمة» ورقم الآيبان" style={{ padding: "7px 12px", background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>⚙️ نص التذكير</button>}{/* v3.4.691 */}
          <button onClick={exportPDF} style={{ padding: "7px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <FiFileText size={12} /> PDF
          </button>
        </div>
      </div>

      {/* Table */}
      {hideCss && <style>{hideCss}</style>}{/* v3.4.459 — إخفاء الأعمدة المختارة */}
      <ScrollSyncBox>{/* v3.4.244 — شريط تمرير علوي + رأس لاصق */}
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
        ) : (
          <table className="coll-tbl" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                {/* v3.3.668 — رؤوس قابلة للفرز */}
                <SortTh label="👤 موظف الشركة" k="is_company_employee" color="#0369a1" filter />{/* v3.4.457 — v3.4.458: علامة */}
                <SortTh label="✅ متوقع هذا الشهر" k="expected_this_month" color="#0f766e" filter />{/* v3.4.226 — v3.4.269: فلتر من رأس العمود */}
                <SortTh label="المشروع" k="project_name" align="right" filter />
                <SortTh label="الوحدة" k="unit_label" filter />
                <SortTh label="العميل" k="client_name" align="right" filter />
                <SortTh label="القيمة" k="effective_price" />
                <SortTh label="المُحصّل" k="total_paid" color="#16a34a" />
                <SortTh label="متأخرات" k="overdue_amount" color="#f59e0b" />{/* v3.4.260 — نُقل بعد المُحصّل */}
                <SortTh label="المتبقي" k="remaining" color="#dc2626" />
                <SortTh label="نسبة التحصيل" k="collection_pct" color="#64748b" />
                <SortTh label="دفعات السداد" k="scheduled_paid_count" color="#0891b2" />
                <SortTh label="📄 العقد" k="contract_url" color="#0f766e" />{/* v3.4.210 */}
                <SortTh label="توقيع العقد" k="contract_signed_date" color="#0f766e" />
                <SortTh label="الاستحقاق القادم" k="next_due_date" color="#7c3aed" />{/* v3.4.108 — عمود مستقل قابل للفرز */}
                <SortTh label="التاريخ المتوقع حسب التفاهمات" k="agreed_expected_date" color="#b45309" />{/* v3.4.510 */}
                <SortTh label="آخر دفعة" k="last_payment_date" />
                {/* v3.3.372 — ملاحظات + إجراءات */}
                <SortTh label="ملاحظات" align="right" />
                <SortTh label="⚙️" />
              </tr>
            </thead>
            <tbody>
              {sorted.map(u => (
                <tr key={u.id} onClick={(e) => { if (e.target.closest("input,a,button,select,textarea,label")) return; if (u.client_id) navigate(`/collections/client/${u.client_id}?unit=${u.id}`); else if (u.project_id) navigate(`/projects/${u.project_id}/units/${u.id}`); }} /* v3.4.801 */
                  title={u.client_id ? "اضغط لفتح تحليل تحصيل العميل (الوحدات، الدفعات، الملاحظات، العقد)" : undefined}
                  style={{
                  borderBottom: "1px solid #f1f5f9", cursor: u.client_id ? "pointer" : "default",
                  background: u.expected_this_month ? "#f0fdfa" : u.is_internal ? "#faf5ff" : (parseFloat(u.overdue_amount || 0) > 0 ? "#fff7ed" : "transparent"),
                }}>
                  {/* v3.4.458 — موظف الشركة (علامة) */}
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    <input type="checkbox" checked={!!u.is_company_employee} disabled={!canMarkExpected} onChange={e => toggleCompanyEmployee(u, e.target.checked)}
                      title={u.is_company_employee ? "المشتري موظف في الشركة — اضغط للإلغاء" : "تعليم: المشتري موظف في الشركة"}
                      style={{ width: 16, height: 16, cursor: canMarkExpected ? "pointer" : "default", accentColor: "#0369a1" }} />
                  </td>
                  {/* v3.4.226 — متوقع سداده هذا الشهر */}
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    <input type="checkbox" checked={!!u.expected_this_month} disabled={!canMarkExpected || u.is_internal} onChange={e => toggleExpected(u, e.target.checked)}
                      title={u.expected_this_month ? "متوقع سداد المتبقي خلال هذا الشهر — اضغط للإلغاء" : "تعليم: متوقع سداد المتبقي خلال هذا الشهر"}
                      style={{ width: 16, height: 16, cursor: canMarkExpected && !u.is_internal ? "pointer" : "default", accentColor: "#0f766e" }} />
                  </td>
                  <td style={{ padding: "8px 8px", color: "#1e293b" }}>{u.project_name}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>
                    {u.project_id && u.id ? (
                      <a href={`/projects/${u.project_id}/units/${u.id}`} style={{ color: "#1e40af", textDecoration: "none", borderBottom: "1px dashed #1e40af" }}
                         onMouseEnter={e => e.currentTarget.style.color = "#2563eb"}
                         onMouseLeave={e => e.currentTarget.style.color = "#1e40af"}>
                        {u.unit_label || u.unit_number}
                      </a>
                    ) : (u.unit_label || u.unit_number)}
                    {u.is_internal && <span style={{ marginRight: 4, fontSize: 9, color: "#7f1d1d", background: "#fef2f2", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>🏛️ داخلي</span>}
                  </td>
                  <td style={{ padding: "8px 8px", color: "#475569" }}>
                    {u.client_name && u.reservation_id ? (
                      <a href={`/?edit=${u.reservation_id}`} target="_blank" rel="noopener noreferrer"
                         style={{ color: "#475569", textDecoration: "none", borderBottom: "1px dashed #94a3b8" }}
                         onMouseEnter={e => e.currentTarget.style.color = "#2563eb"}
                         onMouseLeave={e => e.currentTarget.style.color = "#475569"}>
                        {u.client_name}
                      </a>
                    ) : (u.client_name || "—")}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace" }}>{fmt(u.effective_price)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", color: "#16a34a", fontWeight: 600 }}>{fmt(u.total_paid)}</td>
                  {/* v3.4.260 — متأخرات بعد المُحصّل */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", color: "#f59e0b", fontWeight: parseFloat(u.overdue_amount || 0) > 0 ? 700 : 400 }}>
                    {u.is_internal ? "—" : (parseFloat(u.overdue_amount || 0) > 0 ? `${fmt(u.overdue_amount)} (${u.overdue_count})` : "—")}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", color: u.is_internal ? "#7f1d1d" : (u.remaining > 0 ? "#dc2626" : "#16a34a"), fontWeight: 700 }}>
                    {u.is_internal ? "— ملك الشركة —" : (u.remaining > 0 ? fmt(u.remaining) : "✓ مسدد")}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center", color: u.is_internal ? "#7f1d1d" : (u.collection_pct >= 80 ? "#16a34a" : u.collection_pct >= 50 ? "#f59e0b" : "#dc2626"), fontWeight: 600 }}>
                    {u.is_internal ? "—" : pct(u.collection_pct)}
                  </td>
                  {/* NEW: Scheduled payments column */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontSize: 11 }}>
                    {u.scheduled_total_count > 0 ? (
                      <div>
                        <div style={{ color: "#0891b2", fontWeight: 700 }}>
                          {u.scheduled_paid_count}/{u.scheduled_total_count} دفعة
                        </div>
                      </div>
                    ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                  {/* v3.4.210 — العقد: أيقونة تفتح مستند العقد المرفوع على الوحدة، أو رفع مباشر إن لم يوجد */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontSize: 11 }}>
                    {u.contract_url ? (
                      <a href={u.contract_url} target="_blank" rel="noreferrer" title={`فتح العقد: ${u.contract_name || ""}${u.contract_uploaded_at ? " — رُفع " + String(u.contract_uploaded_at).slice(0, 10) : ""}`}
                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, background: "#ecfdf5", border: "1px solid #a7f3d0", textDecoration: "none", fontSize: 15 }}>📄</a>
                    ) : canEditContract ? (
                      <label title="لا يوجد عقد مرفوع — اضغط لرفع ملف العقد" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, background: "#f8fafc", border: "1px dashed #cbd5e1", cursor: "pointer", fontSize: 13, color: "#94a3b8" }}>
                        <input type="file" hidden accept=".pdf,image/*,.doc,.docx" onChange={e => { uploadContract(u, e.target.files?.[0]); e.target.value = ""; }} />⬆
                      </label>
                    ) : <span style={{ color: "#cbd5e1" }} title="لا يوجد عقد مرفوع">—</span>}
                  </td>
                  {/* v3.4.210 — تاريخ توقيع العقد — v3.4.259: قيمة نهائية للعرض فقط (المدخل سابقًا يبقى، وإلا تاريخ رفع العقد) */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontSize: 11 }}>
                    {u.contract_signed_date
                      ? <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#065f46" }} title={u.contract_date_source === "manual" ? "تاريخ التوقيع المُدخل" : "تاريخ رفع العقد"}>{String(u.contract_signed_date).slice(0, 10)}</span>
                      : <span style={{ color: "#cbd5e1" }} title="لا يوجد عقد مرفوع بعد">—</span>}
                  </td>
                  {/* v3.4.108 — الاستحقاق القادم: أقرب دفعة غير مسددة (بالأحمر إن كانت متأخرة) مع مبلغها */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontSize: 11 }}>
                    {u.is_internal || !u.next_due_date ? <span style={{ color: "#cbd5e1" }}>—</span> : (() => {
                      const d = u.next_due_date.slice(0, 10);
                      const late = new Date(d) < new Date(new Date().toISOString().slice(0, 10));
                      return (
                        <div>
                          <div style={{ fontFamily: "monospace", fontWeight: 700, color: late ? "#dc2626" : "#7c3aed" }}>{late ? "⚠ " : ""}{d}</div>
                          {u.next_due_amount && <div style={{ color: "#64748b", fontSize: 10, marginTop: 2, fontFamily: "monospace" }}>{fmt(u.next_due_amount)}</div>}
                        </div>
                      );
                    })()}
                  </td>
                  {/* v3.4.510 — التاريخ المتوقع حسب التفاهمات: يُدخل ويُعدّل يدويًا من الترس الرمادي (أحمر إن مرّ التاريخ وما زال هناك متبقٍ) */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontSize: 11, whiteSpace: "nowrap" }}>
                    {u.is_internal ? <span style={{ color: "#cbd5e1" }}>—</span> : agreedEdit === u.id ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <input type="date" autoFocus defaultValue={u.agreed_expected_date || ""}
                          onKeyDown={e => { if (e.key === "Enter" && e.target.value) saveAgreedDate(u, e.target.value); if (e.key === "Escape") setAgreedEdit(null); }}
                          onBlur={e => (e.target.value ? saveAgreedDate(u, e.target.value) : setAgreedEdit(null))}
                          style={{ padding: "3px 5px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit", fontSize: 11 }} />
                        {u.agreed_expected_date && (
                          <button type="button" onMouseDown={e => { e.preventDefault(); saveAgreedDate(u, ""); }} title="إزالة التاريخ"
                            style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: "#dc2626", fontSize: 12, fontWeight: 800 }}>✕</button>
                        )}
                      </span>
                    ) : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        {u.agreed_expected_date
                          ? <span style={{ fontFamily: "monospace", fontWeight: 700, color: (u.agreed_expected_date < new Date().toISOString().slice(0, 10) && parseFloat(u.remaining || 0) > 0) ? "#dc2626" : "#b45309" }}
                              title={"حسب التفاهمات مع العميل" + (u.agreed_expected_by ? " — أدخله: " + u.agreed_expected_by : "") + (u.agreed_expected_at ? " (" + String(u.agreed_expected_at).slice(0, 10) + ")" : "")}>{u.agreed_expected_date}</span>
                          : <span style={{ color: "#cbd5e1" }}>—</span>}
                        {canEditAgreed && (
                          <button type="button" onClick={() => setAgreedEdit(u.id)} title={u.agreed_expected_date ? "تعديل التاريخ المتوقع حسب التفاهمات" : "إدخال التاريخ المتوقع حسب التفاهمات"}
                            style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: "#94a3b8", display: "inline-flex", alignItems: "center" }}><FiSettings size={12} /></button>
                        )}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center", color: "#64748b", fontSize: 11 }}>{u.last_payment_date?.slice(0, 10) || "—"}</td>
                  {/* v3.3.373 — ملاحظات موحَّدة تراكمية */}
                  <td style={{ padding: "4px 6px", verticalAlign: "top", maxWidth: 220 }}>
                    {u.reservation_id ? (
                      <ReservationNotesCell reservationId={u.reservation_id} notesLog={u.notes_log || []} />
                    ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    {u.client_phone ? (
                      <button onClick={() => setChatTarget({ phone: u.client_phone, name: u.client_name, clientId: u.client_id, unit: u })} /* v3.4.691 — الوحدة للتذكير */
                        title={`واتساب → ${u.client_name || ""}`}
                        style={{ padding: "5px 10px", background: "#25d366", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 3 }}>
                        <FiMessageCircle size={11} />
                      </button>
                    ) : <span style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            {/* v3.3.318: Footer إجماليات الجدول (يعكس البيانات الظاهرة بعد الفلترة) */}
            {filtered.length > 0 && (() => {
              const totalValue = filtered.reduce((s, u) => s + parseFloat(u.effective_price || 0), 0);
              const totalPaid  = filtered.reduce((s, u) => s + parseFloat(u.total_paid || 0), 0);
              const totalRemaining = filtered.reduce((s, u) => s + (u.is_internal ? 0 : parseFloat(u.remaining || 0)), 0);
              const totalOverdue = filtered.reduce((s, u) => s + (u.is_internal ? 0 : parseFloat(u.overdue_amount || 0)), 0);
              const overdueUnits = filtered.filter(u => !u.is_internal && parseFloat(u.overdue_amount || 0) > 0).length;
              const collectionPct = totalValue > 0 ? (totalPaid / totalValue) * 100 : 0;
              return (
                <tfoot>
                  <tr style={{ background: "#f8fafc", borderTop: "2px solid #cbd5e1", fontWeight: 700 }}>
                    <td colSpan={Math.max(1, lead)} style={{ padding: "10px 8px", color: "#1e293b", fontSize: 12 }}>{/* v3.4.459: يقل مع إخفاء أعمدة — v3.4.260 — v3.4.457: 5 أعمدة: موظف الشركة + متوقع + المشروع + الوحدة + العميل */}
                      الإجمالي ({filtered.length} وحدة)
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontFamily: "monospace", color: "#2563eb", fontSize: 12 }}>
                      {fmt(totalValue)}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontFamily: "monospace", color: "#16a34a", fontSize: 12 }}>
                      {fmt(totalPaid)}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontFamily: "monospace", color: "#f59e0b", fontSize: 12 }}>
                      {totalOverdue > 0 ? `${fmt(totalOverdue)} (${overdueUnits})` : "—"}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontFamily: "monospace", color: "#dc2626", fontSize: 12 }}>
                      {fmt(totalRemaining)}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#64748b", fontSize: 11 }}>
                      {Math.round(collectionPct)}%
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#94a3b8", fontSize: 10 }}>—</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#94a3b8", fontSize: 10 }}>—</td>
                    {/* v3.3.372 — أعمدة إضافية في الـ footer — v3.4.260: دفعات/العقد/توقيع/الاستحقاق/آخر دفعة/ملاحظات — v3.4.510: + المتوقع حسب التفاهمات */}
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#94a3b8", fontSize: 10 }}>—</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#94a3b8", fontSize: 10 }}>—</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#94a3b8", fontSize: 10 }}>—</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#94a3b8", fontSize: 10 }}>—</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#94a3b8", fontSize: 10 }}>—</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: "#94a3b8", fontSize: 10 }}>—</td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        )}
      </ScrollSyncBox>{/* v3.4.244 */}

      {/* v3.3.372 — WhatsApp chat modal */}
      {chatTarget && (
        <WhatsAppChatModal
          phone={chatTarget.phone}
          name={chatTarget.name}
          session="corporate"
          onClose={() => setChatTarget(null)}
          quickTexts={[{ label: "⏰ تذكير بالدفعة القادمة", text: buildReminder(reminderCfg, chatTarget.unit), title: chatTarget.unit?.next_due_date ? "يضع نص التذكير في الحقل — اضغط إرسال" : "لا توجد دفعة مجدولة قادمة لهذه الوحدة" }]}
        />
      )}{/* v3.4.691 */}
      {showReminderCfg && (
        <ReminderSettingsModal projects={projects} settings={reminderCfg} onSaved={setReminderCfg} onClose={() => setShowReminderCfg(false)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 3. CommissionsPage — الدلالات والعمولات
// ═══════════════════════════════════════════════════════════════════
export function CommissionsPage() {
  const projects = useProjects();
  const [projectId, setProjectId] = useState(null);
  const [data, setData] = useState({ reservations: [], summary: {}, by_marketer: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = projectId ? { project_id: projectId } : {};
    financialAnalysis.getCommissions(params)
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل العمولات"))
      .finally(() => setLoading(false));
  }, [projectId]);

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader icon={<FiPercent size={26} color="#8b5cf6" />} title="الدلالات والعمولات" subtitle="توزيع العمولات على المسوقين والشركة" />

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <StatCard label="عدد الحجوزات" value={fmt(data.summary.count)} color="#2563eb" bg="#eff6ff" />
        <StatCard label="إجمالي الدلالة" value={`${fmt(data.summary.total_commission)} ر.س`} color="#8b5cf6" bg="#faf5ff" />
        <StatCard label="مسوقين خارجيين" value={`${fmt(data.summary.total_ext_marketer)} ر.س`} color="#0891b2" bg="#ecfeff" />
        <StatCard label="مسوقين داخليين" value={`${fmt(data.summary.total_int_marketer)} ر.س`} color="#16a34a" bg="#f0fdf4" />
        <StatCard label="الإدارة" value={`${fmt(data.summary.total_waha)} ر.س`} color="#7c3aed" bg="#faf5ff" />
        <StatCard label="الشركة" value={`${fmt(data.summary.total_company)} ر.س`} color="#dc2626" bg="#fef2f2" />
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <FiFilter size={14} color="#64748b" />
        <ProjectFilter value={projectId} onChange={setProjectId} projects={projects} />
      </div>

      {/* Top Marketers */}
      {data.by_marketer.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 12px", color: "#1e293b" }}>🏆 أفضل المسوقين</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {data.by_marketer.slice(0, 10).map((m, i) => (
              <div key={i} style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 12px", borderRight: `3px solid ${i === 0 ? "#f59e0b" : i === 1 ? "#94a3b8" : i === 2 ? "#d97706" : "#e2e8f0"}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>
                  {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`} {m.marketer}
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#8b5cf6", fontFamily: "monospace" }}>{fmt(m.total)} ر.س</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>{m.count} حجز</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Details Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                <th style={{ padding: "8px 6px", textAlign: "right", fontWeight: 700, color: "#475569" }}>التاريخ</th>
                <th style={{ padding: "8px 6px", textAlign: "right", fontWeight: 700, color: "#475569" }}>المشروع</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#475569" }}>الوحدة</th>
                <th style={{ padding: "8px 6px", textAlign: "right", fontWeight: 700, color: "#475569" }}>العميل</th>
                <th style={{ padding: "8px 6px", textAlign: "right", fontWeight: 700, color: "#475569" }}>المسوق</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#8b5cf6" }}>الإجمالي</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#0891b2" }}>خارجي</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#16a34a" }}>داخلي</th>
                {/* v3.3.621 — أعمدة توزيع الدلالة كاملة لتطابق محرّر الحجز/التلميح */}
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#2563eb" }}>المسوق الداخلي 1</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#2563eb" }}>المسوق الداخلي 2</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#0d9488" }}>دليلك</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#7c3aed" }}>الإدارة</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#64748b" }}>مدير مبيعات</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#ec4899" }}>مديرة المسوقات</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#dc2626" }}>الشركة</th>
                <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, color: "#475569" }}>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {data.reservations.map(r => (
                <tr key={r.reservation_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "6px 6px", fontFamily: "monospace", color: "#475569" }}>{r.date?.slice(0, 10)}</td>
                  <td style={{ padding: "6px 6px", color: "#1e293b", fontWeight: 600 }}>{r.project_name}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace", color: "#1e40af", fontWeight: 700 }}>{r.unit_label || r.unit_number}</td>
                  <td style={{ padding: "6px 6px", color: "#475569" }}>{r.client_name || "—"}</td>
                  <td style={{ padding: "6px 6px", color: "#475569" }}>{r.marketer || "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace", color: "#8b5cf6", fontWeight: 700 }}>{fmt(r.dalala_amount)}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.ext_marketer_share > 0 ? fmt(r.ext_marketer_share) : "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.int_marketer_share > 0 ? fmt(r.int_marketer_share) : "—"}</td>
                  {/* v3.3.621 — توزيع الدلالة الكامل (يطابق محرّر الحجز) */}
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.marketer1_share > 0 ? fmt(r.marketer1_share) : "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.marketer2_share > 0 ? fmt(r.marketer2_share) : "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.dalilak_share > 0 ? fmt(r.dalilak_share) : "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.waha_share > 0 ? fmt(r.waha_share) : "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.sales_mgr_share > 0 ? fmt(r.sales_mgr_share) : "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.mkt_mgr_share > 0 ? fmt(r.mkt_mgr_share) : "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", fontFamily: "monospace" }}>{r.company_share > 0 ? fmt(r.company_share) : "—"}</td>
                  <td style={{ padding: "6px 6px", textAlign: "center", color: r.commission_status_res === "مدفوعة" ? "#16a34a" : "#f59e0b", fontSize: 10 }}>{r.commission_status_res || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 3b. AdminFeesPage — الرسوم الإدارية للمشاريع (v3.3.622)
// جدول بكل الوحدات لكل المشاريع مع الرسوم الإدارية ورسوم الشركة، مع إجماليات
// لكل وحدة ومبنى ومشروع. التعديل يتم من صفحة الوحدة في قسم المشاريع.
// ═══════════════════════════════════════════════════════════════════
export function AdminFeesPage() {
  const navigate = useNavigate();
  const projects = useProjects();
  const [projectId, setProjectId] = useState(null);
  const [buildingId, setBuildingId] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ units: [], summary: {} });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = projectId ? { project_id: projectId } : {};
    financialAnalysis.getAdminFees(params)
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل الرسوم الإدارية"))
      .finally(() => setLoading(false));
    setBuildingId(""); // reset building filter on project change
  }, [projectId]);

  // خيارات المباني من الوحدات المحمّلة (ضمن نطاق المشروع المحدد)
  const buildingOptions = useMemo(() => {
    const m = {};
    (data.units || []).forEach(u => { if (u.building_id) m[u.building_id] = u.building_name || `مبنى ${u.building_id}`; });
    return Object.entries(m).map(([id, name]) => ({ id, name }));
  }, [data.units]);

  // فلترة العميل/المسوق/الوحدة + المبنى (client-side)
  const filteredUnits = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.units || []).filter(u => {
      if (buildingId && String(u.building_id) !== String(buildingId)) return false;
      if (!q) return true;
      return [u.unit_label, u.unit_number, u.client_name, u.marketer, u.project_name, u.building_name]
        .some(v => v && String(v).toLowerCase().includes(q));
    });
  }, [data.units, buildingId, search]);

  // تجميع: مشروع ← مبنى ← وحدات (مع إجماليات لكل مستوى)
  const grouped = useMemo(() => {
    const byProject = {};
    for (const u of filteredUnits) {
      const a = Number(u.admin_fees) || 0, c = Number(u.company_fees) || 0;
      const pk = u.project_id;
      if (!byProject[pk]) byProject[pk] = { project_id: u.project_id, project_name: u.project_name, admin: 0, company: 0, count: 0, buildings: {} };
      const P = byProject[pk]; P.admin += a; P.company += c; P.count++;
      const bk = u.building_id || "_none";
      if (!P.buildings[bk]) P.buildings[bk] = { building_id: u.building_id, building_name: u.building_name || "بدون مبنى", admin: 0, company: 0, count: 0, units: [] };
      const B = P.buildings[bk]; B.admin += a; B.company += c; B.count++; B.units.push(u);
    }
    return Object.values(byProject).map(P => ({ ...P, buildings: Object.values(P.buildings) }));
  }, [filteredUnits]);

  const grandAdmin = filteredUnits.reduce((s, u) => s + (Number(u.admin_fees) || 0), 0);
  const grandCompany = filteredUnits.reduce((s, u) => s + (Number(u.company_fees) || 0), 0);

  // v3.3.623 — مجموعات قابلة للطي/التوسعة (افتراضياً مطوية)
  const [expandedProjects, setExpandedProjects] = useState(() => new Set());
  const [expandedBuildings, setExpandedBuildings] = useState(() => new Set());
  const toggleProject = (id) => setExpandedProjects(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleBuilding = (key) => setExpandedBuildings(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader icon={<FiHome size={26} color="#0891b2" />} title="الرسوم الإدارية للمشاريع"
        subtitle="الرسوم الإدارية ورسوم الشركة لكل وحدة — التعديل يتم من صفحة الوحدة في قسم المشاريع" />

      {/* v3.3.623 — صندوق إجمالي كل المشاريع + صندوق لكل مشروع */}
      <div style={{ background: "linear-gradient(135deg,#0e7490,#0891b2)", borderRadius: 14, padding: "16px 20px", marginBottom: 14, color: "#fff", boxShadow: "0 4px 14px rgba(8,145,178,0.25)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.85, marginBottom: 4 }}>📊 إجمالي كل المشاريع · {fmt(filteredUnits.length)} وحدة</div>
        <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 8, fontFamily: "monospace" }}>{fmt(grandAdmin + grandCompany)} ر.س</div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
          <div>الرسوم الإدارية: <strong style={{ fontFamily: "monospace" }}>{fmt(grandAdmin)}</strong> ر.س</div>
          <div>رسوم الشركة: <strong style={{ fontFamily: "monospace" }}>{fmt(grandCompany)}</strong> ر.س</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, marginBottom: 16 }}>
        {grouped.map(P => (
          <div key={P.project_id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "12px 14px", borderTop: "3px solid #0891b2" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>🏢 {P.project_name} <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}>({fmt(P.count)} وحدة)</span></div>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0e7490", fontFamily: "monospace", marginBottom: 6 }}>{fmt(P.admin + P.company)} ر.س</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b" }}>
              <span>إدارية: <strong style={{ color: "#0891b2", fontFamily: "monospace" }}>{fmt(P.admin)}</strong></span>
              <span>شركة: <strong style={{ color: "#dc2626", fontFamily: "monospace" }}>{fmt(P.company)}</strong></span>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <FiFilter size={14} color="#64748b" />
        <ProjectFilter value={projectId} onChange={setProjectId} projects={projects} />
        <select value={buildingId} onChange={e => setBuildingId(e.target.value)}
          style={{ padding: "7px 12px", fontSize: 12, borderRadius: 7, border: "1px solid #e2e8f0", fontFamily: "inherit", cursor: "pointer" }}>
          <option value="">كل المباني</option>
          {buildingOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث: عميل / مسوق / وحدة…"
          style={{ padding: "7px 12px", fontSize: 12, borderRadius: 7, border: "1px solid #e2e8f0", fontFamily: "inherit", flex: 1, minWidth: 180 }} />
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
        ) : filteredUnits.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>لا توجد وحدات</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>الوحدة</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>العميل</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>المسوق</th>
                <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#0891b2" }}>الرسوم الإدارية</th>
                <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#dc2626" }}>رسوم الشركة</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(P => {
                const pOpen = expandedProjects.has(P.project_id);
                return (
                <React.Fragment key={P.project_id}>
                  {/* Project header + totals — clickable to expand/collapse */}
                  <tr style={{ background: "#0891b2", cursor: "pointer" }} onClick={() => toggleProject(P.project_id)}>
                    <td colSpan={3} style={{ padding: "8px 10px", color: "#fff", fontWeight: 800 }}>
                      <span style={{ display: "inline-block", width: 14 }}>{pOpen ? "▼" : "◀"}</span> 🏢 {P.project_name} <span style={{ opacity: 0.85, fontWeight: 600 }}>({fmt(P.count)} وحدة)</span>
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "center", color: "#fff", fontWeight: 800, fontFamily: "monospace" }}>{fmt(P.admin)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "center", color: "#fff", fontWeight: 800, fontFamily: "monospace" }}>{fmt(P.company)}</td>
                  </tr>
                  {pOpen && P.buildings.map(B => {
                    const bKey = `${P.project_id}::${B.building_id || "_none"}`;
                    const bOpen = expandedBuildings.has(bKey);
                    return (
                    <React.Fragment key={bKey}>
                      {/* Building subtotal header — clickable to expand/collapse */}
                      <tr style={{ background: "#e0f2fe", cursor: "pointer" }} onClick={() => toggleBuilding(bKey)}>
                        <td colSpan={3} style={{ padding: "6px 18px", color: "#0c4a6e", fontWeight: 700 }}>
                          <span style={{ display: "inline-block", width: 14 }}>{bOpen ? "▼" : "◀"}</span> 🏬 {B.building_name} <span style={{ fontWeight: 500, color: "#075985" }}>({fmt(B.count)} وحدة)</span>
                        </td>
                        <td style={{ padding: "6px 10px", textAlign: "center", color: "#0c4a6e", fontWeight: 700, fontFamily: "monospace" }}>{fmt(B.admin)}</td>
                        <td style={{ padding: "6px 10px", textAlign: "center", color: "#0c4a6e", fontWeight: 700, fontFamily: "monospace" }}>{fmt(B.company)}</td>
                      </tr>
                      {bOpen && B.units.map(u => (
                        <tr key={u.unit_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "6px 24px", fontFamily: "monospace", color: "#1e40af", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
                            title="فتح الوحدة في قسم المشاريع للتعديل"
                            onClick={() => navigate(`/projects/${u.project_id}/units/${u.unit_id}`)}>
                            {u.unit_label || u.unit_number || `#${u.unit_id}`}
                          </td>
                          <td style={{ padding: "6px 10px", color: "#475569" }}>{u.client_name || "—"}</td>
                          <td style={{ padding: "6px 10px", color: "#475569" }}>{u.marketer || "—"}</td>
                          <td style={{ padding: "6px 10px", textAlign: "center", fontFamily: "monospace" }}>{fmt(u.admin_fees)}</td>
                          <td style={{ padding: "6px 10px", textAlign: "center", fontFamily: "monospace" }}>{fmt(u.company_fees)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                    );
                  })}
                </React.Fragment>
                );
              })}
              {/* Grand total */}
              <tr style={{ background: "#1e293b" }}>
                <td colSpan={3} style={{ padding: "10px", color: "#fff", fontWeight: 800 }}>الإجمالي الكلي ({fmt(filteredUnits.length)} وحدة)</td>
                <td style={{ padding: "10px", textAlign: "center", color: "#67e8f9", fontWeight: 800, fontFamily: "monospace" }}>{fmt(grandAdmin)}</td>
                <td style={{ padding: "10px", textAlign: "center", color: "#fca5a5", fontWeight: 800, fontFamily: "monospace" }}>{fmt(grandCompany)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 4. CashFlowPage — تدفق الأموال
// ═══════════════════════════════════════════════════════════════════
export function CashFlowPage() {
  const projects = useProjects();
  const [projectId, setProjectId] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedYears, setSelectedYears] = useState([currentYear]);  // [] = all
  const [selectedMonths, setSelectedMonths] = useState([]);           // [] = all months in selected years
  const [flowType, setFlowType] = useState("all"); // all | new_sales | collections | outflow
  const [legendMode, setLegendMode] = useState("type"); // type | project
  const [data, setData] = useState({
    new_sales: [], collections: [], inflows: [], outflows: [], expected_inflows: [],
    available_years: [], summary: {},
  });
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null); // {x, y, content}

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (projectId) params.project_id = projectId;
    if (selectedYears.length > 0)  params.years = selectedYears.join(",");
    if (selectedMonths.length > 0) params.months_list = selectedMonths.join(",");
    if (selectedYears.length === 0 && selectedMonths.length === 0) params.months = 240; // ~20 years = "all"
    financialAnalysis.getCashFlow(params)
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل التدفق"))
      .finally(() => setLoading(false));
  }, [projectId, selectedYears, selectedMonths]);

  // Project color palette
  const projectColors = useMemo(() => {
    const palette = [
      "#16a34a", "#2563eb", "#dc2626", "#f59e0b", "#8b5cf6",
      "#0891b2", "#db2777", "#059669", "#7c3aed", "#ea580c",
    ];
    const map = {};
    (projects || []).forEach((p, i) => { map[p.id] = palette[i % palette.length]; });
    return map;
  }, [projects]);

  // Build monthly skeleton
  const monthlyData = useMemo(() => {
    const map = {};
    const emptyRow = (key) => ({
      month: key,
      new_sales: [], collections_same: [], collections_prior: [],
      outflow: 0, expected: 0,
      total_new_sales: 0, total_collections: 0,
      total_same: 0, total_prior: 0,
    });

    // Effective years: use selectedYears if any, else all available years (so "الكل" shows everything)
    const allAvailableYears = data.available_years || [];
    const effectiveYears = selectedYears.length > 0 ? selectedYears : allAvailableYears;
    const effectiveMonths = selectedMonths.length > 0 ? selectedMonths : [1,2,3,4,5,6,7,8,9,10,11,12];

    // Build skeleton based on effective years × effective months
    if (effectiveYears.length > 0) {
      effectiveYears.forEach(y => {
        effectiveMonths.forEach(m => {
          const key = `${y}-${String(m).padStart(2, "0")}`;
          map[key] = emptyRow(key);
        });
      });
    } else {
      // Fallback: no available years yet (initial load) — show last 12 months
      const now = new Date();
      for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        map[key] = emptyRow(key);
      }
    }

    // Merge new sales (split by project)
    (data.new_sales || []).forEach(ns => {
      const m = ns.month;
      if (!map[m]) map[m] = emptyRow(m);
      const amount = parseFloat(ns.total_amount || 0);
      map[m].new_sales.push({ project_id: ns.project_id, project_name: ns.project_name, amount, count: parseInt(ns.count || 0) });
      map[m].total_new_sales += amount;
    });

    // Merge collections (split by bucket + project)
    (data.collections || []).forEach(c => {
      const m = c.month;
      if (!map[m]) map[m] = emptyRow(m);
      const amount = parseFloat(c.total_amount || 0);
      const entry = { project_id: c.project_id, project_name: c.project_name, amount, count: parseInt(c.count || 0) };
      if (c.bucket === "same_month") {
        map[m].collections_same.push(entry);
        map[m].total_same += amount;
      } else {
        map[m].collections_prior.push(entry);
        map[m].total_prior += amount;
      }
      map[m].total_collections += amount;
    });

    // Merge outflows + expected
    (data.outflows || []).forEach(o => {
      if (!map[o.month]) return;
      map[o.month].outflow = parseFloat(o.total_amount || 0);
    });
    (data.expected_inflows || []).forEach(e => {
      if (!map[e.month]) return;
      map[e.month].expected = parseFloat(e.total_amount || 0);
    });

    return Object.values(map).sort((a, b) => b.month.localeCompare(a.month));
  }, [data, selectedYears, selectedMonths]);

  // Max value across bar types for consistent scaling
  const maxVal = Math.max(1, ...monthlyData.map(m => Math.max(m.total_new_sales, m.total_collections, m.expected)));

  const arabicMonths = {
    "01": "يناير", "02": "فبراير", "03": "مارس", "04": "أبريل",
    "05": "مايو", "06": "يونيو", "07": "يوليو", "08": "أغسطس",
    "09": "سبتمبر", "10": "أكتوبر", "11": "نوفمبر", "12": "ديسمبر",
  };
  const formatMonth = (key) => { const [y, m] = key.split("-"); return `${arabicMonths[m]} ${y}`; };

  // Tooltip handlers
  const showTooltip = (e, content) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      content,
    });
  };
  const hideTooltip = () => setTooltip(null);

  // Render one segmented bar
  const renderSegmentedBar = (segments, totalForMax, barLabel, borderColor, getSegColor, getSegTooltip) => {
    if (segments.length === 0 && totalForMax === 0) {
      return <div style={{ flex: 1, height: 22, background: "#f8fafc", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1", fontSize: 9 }}>لا يوجد</div>;
    }
    const barPct = (totalForMax / maxVal) * 100;
    return (
      <div style={{ flex: 1, height: 22, background: "#f8fafc", borderRadius: 3, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${barPct}%`, display: "flex", height: "100%" }}>
          {segments.map((seg, i) => {
            const segPct = totalForMax > 0 ? (seg.amount / totalForMax) * 100 : 0;
            return (
              <div key={i}
                onMouseEnter={e => showTooltip(e, getSegTooltip(seg))}
                onMouseLeave={hideTooltip}
                onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                style={{
                  width: `${segPct}%`,
                  background: getSegColor(seg),
                  borderRight: i < segments.length - 1 ? `1px solid rgba(255,255,255,0.5)` : "none",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 9, fontWeight: 700,
                  transition: "opacity 0.15s",
                }}>
                {segPct > 15 && fmt(seg.amount)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1400, margin: "0 auto", position: "relative" }}>
      <PageHeader icon={<FiRefreshCw size={26} color="#0891b2" />} title="تفاصيل الإيرادات" subtitle="المبيعات الجديدة والتحصيلات الشهرية" />

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <StatCard label="🆕 مبيعات جديدة" value={`${fmt(data.summary.total_new_sales)} ر.س`} color="#7c3aed" bg="#faf5ff" />
        <StatCard label="💰 إجمالي التحصيل" value={`${fmt(data.summary.total_inflow)} ر.س`} color="#16a34a" bg="#f0fdf4" />
        <StatCard label="⏳ متوقع الداخل" value={`${fmt(data.summary.total_expected)} ر.س`} color="#f59e0b" bg="#fffbeb" />
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Row 1: Project + Flow Type */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <FiFilter size={14} color="#64748b" />
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, minWidth: 55 }}>المشروع:</span>
          <ProjectFilter value={projectId} onChange={setProjectId} projects={projects} />
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, marginRight: 10 }}>نوع التدفق:</span>
          {[
            { v: "all",         label: "الكل",         color: "#1e293b" },
            { v: "new_sales",   label: "🆕 مبيعات",    color: "#7c3aed" },
            { v: "collections", label: "💰 تحصيل",     color: "#16a34a" },
          ].map(t => (
            <button key={t.v} onClick={() => setFlowType(t.v)}
              style={{
                padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6,
                border: "1px solid " + (flowType === t.v ? t.color : "#e2e8f0"),
                background: flowType === t.v ? t.color : "#fff",
                color: flowType === t.v ? "#fff" : "#475569",
                cursor: "pointer", fontFamily: "inherit",
              }}>{t.label}</button>
          ))}
        </div>

        {/* Years + Months via shared component */}
        <YearMonthFilters
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          setSelectedYears={setSelectedYears}
          setSelectedMonths={setSelectedMonths}
          availableYears={data.available_years || []}
        />
      </div>

      {/* ─────── Overview Chart — Twin bars per month: Sales + Collections (stacked) ─────── */}
      {/* v3.3.315: عمودان لكل شهر — يسار: مبيعات، يمين: تحصيلات (مقسّمة من مبيعات+سابقة) */}
      {(() => {
        // activeMonths reused below — calculate here
        const activeMonths = monthlyData.filter(m => m.total_new_sales > 0 || m.total_collections > 0);
        if (activeMonths.length === 0) return null;

        // Sort chronologically (left = oldest, right = newest)
        const chronological = [...activeMonths].sort((a, b) => a.month.localeCompare(b.month));

        // Max value for scaling — أعلى قيمة بين المبيعات أو التحصيلات
        const maxVal = Math.max(1, ...chronological.map(m => Math.max(
          (flowType === "all" || flowType === "new_sales") ? m.total_new_sales : 0,
          (flowType === "all" || flowType === "collections") ? m.total_collections : 0,
        )));

        // Project totals per month (for "project" mode)
        const projectsPerMonth = chronological.map(m => {
          const salesProjMap = {};
          m.new_sales.forEach(s => {
            if (!salesProjMap[s.project_id]) salesProjMap[s.project_id] = { project_id: s.project_id, project_name: s.project_name, amount: 0 };
            salesProjMap[s.project_id].amount += s.amount;
          });
          const collProjMap = {};
          [...m.collections_same, ...m.collections_prior].forEach(s => {
            if (!collProjMap[s.project_id]) collProjMap[s.project_id] = { project_id: s.project_id, project_name: s.project_name, amount: 0 };
            collProjMap[s.project_id].amount += s.amount;
          });
          return {
            month: m.month,
            salesSegs: Object.values(salesProjMap).sort((a, b) => b.amount - a.amount),
            collSegs: Object.values(collProjMap).sort((a, b) => b.amount - a.amount),
          };
        });

        const showSales = flowType === "all" || flowType === "new_sales";
        const showColl = flowType === "all" || flowType === "collections";

        return (
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "#1e293b" }}>
                📈 نظرة عامة — تدفق شهري ({chronological.length} شهر)
              </h3>
              <div style={{ display: "flex", gap: 4 }}>
                <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, alignSelf: "center", marginLeft: 4 }}>التلوين:</span>
                <button onClick={() => setLegendMode("type")}
                  style={{
                    padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6,
                    border: "1px solid " + (legendMode === "type" ? "#0891b2" : "#e2e8f0"),
                    background: legendMode === "type" ? "#0891b2" : "#fff",
                    color: legendMode === "type" ? "#fff" : "#475569",
                    cursor: "pointer", fontFamily: "inherit",
                  }}>حسب النوع</button>
                <button onClick={() => setLegendMode("project")}
                  style={{
                    padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6,
                    border: "1px solid " + (legendMode === "project" ? "#0891b2" : "#e2e8f0"),
                    background: legendMode === "project" ? "#0891b2" : "#fff",
                    color: legendMode === "project" ? "#fff" : "#475569",
                    cursor: "pointer", fontFamily: "inherit",
                  }}>حسب المشروع</button>
              </div>
            </div>

            {/* Twin bars per month — v3.3.317: تصميم نحيف بمسافة 8px + ألوان شفافة */}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 280, paddingBottom: 30, position: "relative", overflowX: "auto" }}>
              {chronological.map((m, idx) => {
                const arabicMonths = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
                const [yy, mm] = m.month.split("-");
                const monthName = arabicMonths[parseInt(mm) - 1];
                const fullYear = "20" + yy.slice(-2);
                const salesH = showSales ? (m.total_new_sales / maxVal) * 100 : 0;
                const collH  = showColl  ? (m.total_collections / maxVal) * 100 : 0;
                return (
                  <div key={m.month} style={{ flex: "1 0 56px", minWidth: 56, display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                    {/* Twin bars side by side */}
                    <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 4, width: "100%", justifyContent: "center" }}>
                      {/* عمود المبيعات */}
                      {showSales && (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, width: 18, height: "100%", justifyContent: "flex-end" }}>
                          <div style={{ fontSize: 10, color: "#534AB7", fontWeight: 500, whiteSpace: "nowrap" }}>{m.total_new_sales > 0 ? fmt(m.total_new_sales) : ""}</div>
                          <div style={{ width: "100%", height: `${salesH}%`, minHeight: m.total_new_sales > 0 ? 4 : 0,
                            background: legendMode === "type" ? "rgba(127, 119, 221, 0.55)" : "#f8fafc",
                            borderRadius: 6, overflow: "hidden",
                            display: "flex", flexDirection: "column-reverse"
                          }}
                          onMouseEnter={legendMode === "type" ? e => showTooltip(e, `🆕 مبيعات\n${fmt(m.total_new_sales)} ر.س`) : undefined}
                          onMouseLeave={legendMode === "type" ? hideTooltip : undefined}
                          onMouseMove={legendMode === "type" ? e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null) : undefined}>
                            {legendMode === "project" && projectsPerMonth[idx].salesSegs.map((seg, si) => (
                              <div key={si}
                                onMouseEnter={e => showTooltip(e, `🆕 ${seg.project_name}\n${fmt(seg.amount)} ر.س`)}
                                onMouseLeave={hideTooltip}
                                onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                                style={{ height: `${(seg.amount / m.total_new_sales) * 100}%`,
                                  background: projectColors[seg.project_id] || "#94a3b8", cursor: "pointer",
                                  borderTop: si > 0 ? "1px solid rgba(255,255,255,0.4)" : "none" }} />
                            ))}
                          </div>
                        </div>
                      )}
                      {/* عمود التحصيلات stacked: same + prior */}
                      {showColl && (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, width: 18, height: "100%", justifyContent: "flex-end" }}>
                          <div style={{ fontSize: 10, color: "#0F6E56", fontWeight: 500, whiteSpace: "nowrap" }}>{m.total_collections > 0 ? fmt(m.total_collections) : ""}</div>
                          <div style={{ width: "100%", height: `${collH}%`, minHeight: m.total_collections > 0 ? 4 : 0,
                            background: "#f8fafc", borderRadius: 6, overflow: "hidden",
                            display: "flex", flexDirection: "column-reverse"
                          }}>
                            {legendMode === "type" ? (
                              <>
                                {m.total_same > 0 && (
                                  <div onMouseEnter={e => showTooltip(e, `💰 تحصيل من مبيعات الشهر\n${fmt(m.total_same)} ر.س`)}
                                    onMouseLeave={hideTooltip}
                                    onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                                    style={{ height: `${(m.total_same / m.total_collections) * 100}%`,
                                      background: "rgba(29, 158, 117, 0.85)", cursor: "pointer" }} />
                                )}
                                {m.total_prior > 0 && (
                                  <div onMouseEnter={e => showTooltip(e, `📜 تحصيل من عقود سابقة\n${fmt(m.total_prior)} ر.س`)}
                                    onMouseLeave={hideTooltip}
                                    onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                                    style={{ height: `${(m.total_prior / m.total_collections) * 100}%`,
                                      background: "rgba(93, 202, 165, 0.85)", cursor: "pointer",
                                      borderTop: m.total_same > 0 ? "1px solid rgba(255,255,255,0.5)" : "none" }} />
                                )}
                              </>
                            ) : (
                              projectsPerMonth[idx].collSegs.map((seg, si) => (
                                <div key={si}
                                  onMouseEnter={e => showTooltip(e, `💰 ${seg.project_name}\n${fmt(seg.amount)} ر.س`)}
                                  onMouseLeave={hideTooltip}
                                  onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                                  style={{ height: `${(seg.amount / m.total_collections) * 100}%`,
                                    background: projectColors[seg.project_id] || "#94a3b8", cursor: "pointer",
                                    borderTop: si > 0 ? "1px solid rgba(255,255,255,0.4)" : "none" }} />
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Month label — سطرين: اسم الشهر فوق، السنة تحت */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 8, gap: 1 }}>
                      <div style={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>{monthName}</div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>{fullYear}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 14, marginTop: 8, justifyContent: "center", fontSize: 10, flexWrap: "wrap", paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
              {legendMode === "type" ? (
                <>
                  {showSales && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 12, height: 12, background: "rgba(127, 119, 221, 0.55)", borderRadius: 3 }} />
                      <span style={{ color: "#475569", fontWeight: 600 }}>🆕 مبيعات</span>
                    </span>
                  )}
                  {showColl && (
                    <>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ width: 12, height: 12, background: "rgba(29, 158, 117, 0.85)", borderRadius: 3 }} />
                        <span style={{ color: "#475569", fontWeight: 600 }}>💰 تحصيل من مبيعات الشهر</span>
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ width: 12, height: 12, background: "rgba(93, 202, 165, 0.85)", borderRadius: 3 }} />
                        <span style={{ color: "#475569", fontWeight: 600 }}>📜 تحصيل من عقود سابقة</span>
                      </span>
                    </>
                  )}
                </>
              ) : (
                <>
                  {projects.map(p => (
                    <span key={p.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 12, height: 12, background: projectColors[p.id], borderRadius: 3 }} />
                      <span style={{ color: "#475569", fontWeight: 600 }}>{p.name}</span>
                    </span>
                  ))}
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Monthly Chart */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 16px", color: "#1e293b" }}>
          📊 التدفق الشهري {
            selectedYears.length === 0 && selectedMonths.length === 0
              ? "(كل الأشهر المتوفرة)"
              : selectedYears.length > 0 && selectedMonths.length === 0
                ? `— ${selectedYears.join(", ")}`
                : selectedYears.length === 0 && selectedMonths.length > 0
                  ? `— أشهر محددة`
                  : `— ${selectedYears.length} سنة × ${selectedMonths.length} شهر`
          }
        </h3>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
        ) : (() => {
          const activeMonths = monthlyData.filter(m => m.total_new_sales > 0 || m.total_collections > 0);
          if (activeMonths.length === 0) {
            return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>لا توجد بيانات لهذه الفترة</div>;
          }
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {activeMonths.map(m => {
                // Revenue (new contracts signed this month) — separate from cash collected
                const revenue = (flowType === "all" || flowType === "new_sales") ? m.total_new_sales : 0;
                // Cash collected this month (same-month + prior contracts)
                const cashIn  = (flowType === "all" || flowType === "collections") ? m.total_collections : 0;
                return (
                <div key={m.month} style={{ paddingBottom: 10, borderBottom: "1px solid #f1f5f9" }}>
                  {/* Month title row with separate totals */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{formatMonth(m.month)}</span>
                    <div style={{ display: "flex", gap: 12, fontSize: 11, fontFamily: "monospace", fontWeight: 700 }}>
                      {(flowType === "all" || flowType === "new_sales") && (
                        <span style={{ color: "#7c3aed" }}>إيرادات: {fmt(revenue)} ر.س</span>
                      )}
                      {(flowType === "all" || flowType === "collections") && (
                        <span style={{ color: "#16a34a" }}>التحصيل: {fmt(cashIn)} ر.س</span>
                      )}
                    </div>
                  </div>

                  {/* 3 bars stacked — each with inline totals on the right */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {/* Bar 1: NEW SALES */}
                    {(flowType === "all" || flowType === "new_sales") && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, color: "#7c3aed", fontWeight: 700, minWidth: 75 }}>🆕 مبيعات</span>
                      {renderSegmentedBar(
                        m.new_sales,
                        m.total_new_sales,
                        "مبيعات",
                        "#7c3aed",
                        (seg) => projectColors[seg.project_id] || "#7c3aed",
                        (seg) => `🆕 ${seg.project_name}\n${fmt(seg.amount)} ر.س — ${seg.count} وحدة`
                      )}
                      <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 700, fontFamily: "monospace", minWidth: 95, textAlign: "left" }}>
                        {m.total_new_sales > 0 ? fmt(m.total_new_sales) : "—"}
                      </span>
                    </div>
                    )}

                    {/* Bar 2a: COLLECTIONS — تحصيل من بيع الشهر (نفس الشهر) */}
                    {(flowType === "all" || flowType === "collections") && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, color: "#16a34a", fontWeight: 700, minWidth: 75 }}>💰 تحصيل عقد</span>
                      <div style={{ flex: 1, height: 20, background: "#f8fafc", borderRadius: 3, overflow: "hidden", display: "flex" }}>
                        {m.total_same > 0 && (
                          <div style={{ width: `${(m.total_same / maxVal) * 100}%`, display: "flex", height: "100%" }}>
                            {m.collections_same.map((seg, i) => {
                              const segPct = m.total_same > 0 ? (seg.amount / m.total_same) * 100 : 0;
                              return (
                                <div key={`s-${i}`}
                                  onMouseEnter={e => showTooltip(e, `💰 تحصيل من بيع الشهر\n${seg.project_name}\n${fmt(seg.amount)} ر.س — ${seg.count} دفعة`)}
                                  onMouseLeave={hideTooltip}
                                  onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                                  style={{
                                    width: `${segPct}%`,
                                    background: projectColors[seg.project_id] || "#16a34a",
                                    borderRight: i < m.collections_same.length - 1 ? "1px solid rgba(255,255,255,0.5)" : "none",
                                    cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#fff", fontSize: 9, fontWeight: 700,
                                  }}>
                                  {segPct > 15 && fmt(seg.amount)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 11, fontFamily: "monospace", minWidth: 95, textAlign: "left", color: "#16a34a", fontWeight: 700 }}>
                        {m.total_same > 0 ? fmt(m.total_same) : "—"}
                      </span>
                    </div>
                    )}

                    {/* Bar 2b: COLLECTIONS — تحصيل من عقود سابقة */}
                    {(flowType === "all" || flowType === "collections") && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, color: "#16a34a", fontWeight: 700, minWidth: 75, opacity: 0.7 }}>💰 تحصيل سابق</span>
                      <div style={{ flex: 1, height: 20, background: "#f8fafc", borderRadius: 3, overflow: "hidden", display: "flex" }}>
                        {m.total_prior > 0 && (
                          <div style={{ width: `${(m.total_prior / maxVal) * 100}%`, display: "flex", height: "100%" }}>
                            {m.collections_prior.map((seg, i) => {
                              const segPct = m.total_prior > 0 ? (seg.amount / m.total_prior) * 100 : 0;
                              const baseColor = projectColors[seg.project_id] || "#16a34a";
                              return (
                                <div key={`p-${i}`}
                                  onMouseEnter={e => showTooltip(e, `💰 تحصيل من عقد سابق\n${seg.project_name}\n${fmt(seg.amount)} ر.س — ${seg.count} دفعة`)}
                                  onMouseLeave={hideTooltip}
                                  onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                                  style={{
                                    width: `${segPct}%`,
                                    background: baseColor,
                                    opacity: 0.55,
                                    borderRight: i < m.collections_prior.length - 1 ? "1px solid rgba(255,255,255,0.5)" : "none",
                                    cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#fff", fontSize: 9, fontWeight: 700,
                                  }}>
                                  {segPct > 15 && fmt(seg.amount)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 11, fontFamily: "monospace", minWidth: 95, textAlign: "left", color: "#16a34a", opacity: 0.7, fontWeight: 700 }}>
                        {m.total_prior > 0 ? fmt(m.total_prior) : "—"}
                      </span>
                    </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          );
        })()}

        {/* Legend */}
        <div style={{ display: "flex", gap: 14, marginTop: 16, justifyContent: "center", fontSize: 10, flexWrap: "wrap" }}>
          <span style={{ color: "#7c3aed", fontWeight: 600 }}>🆕 مبيعات جديدة (حسب تاريخ أول دفعة)</span>
          <span style={{ color: "#16a34a", fontWeight: 600 }}>💰 تحصيل نفس الشهر (لون ثابت)</span>
          <span style={{ color: "#16a34a", opacity: 0.5, fontWeight: 600 }}>💰 تحصيل من عقود سابقة (شفاف)</span>
        </div>
        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 8, textAlign: "center" }}>
          💡 تمرير الفأرة فوق البارات لمشاهدة التفاصيل — الألوان تمثل المشاريع
        </div>

        {/* Project legend */}
        {projects.length > 0 && (
          <div style={{ display: "flex", gap: 10, marginTop: 12, justifyContent: "center", fontSize: 10, flexWrap: "wrap", paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
            {projects.map(p => (
              <span key={p.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 10, height: 10, background: projectColors[p.id], borderRadius: 2, display: "inline-block" }} />
                <span style={{ color: "#475569" }}>{p.name}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: "fixed",
          left: tooltip.x + 12,
          top: tooltip.y + 12,
          background: "#1e293b",
          color: "#fff",
          padding: "8px 12px",
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "pre-line",
          pointerEvents: "none",
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          maxWidth: 260,
        }}>
          {tooltip.content}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 5. BenchmarkingPage — مقارنة المشاريع
// ═══════════════════════════════════════════════════════════════════
export function BenchmarkingPage() {
  const [data, setData] = useState({ projects: [] });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("sales_pct");

  useEffect(() => {
    financialAnalysis.getBenchmarking()
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل المقارنة"))
      .finally(() => setLoading(false));
  }, []);

  const sorted = useMemo(() => {
    return [...data.projects].sort((a, b) => parseFloat(b[sortBy] || 0) - parseFloat(a[sortBy] || 0));
  }, [data.projects, sortBy]);

  const maxValueForSort = Math.max(1, ...sorted.map(p => parseFloat(p[sortBy] || 0)));

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader icon={<FiGrid size={26} color="#2563eb" />} title="مقارنة المشاريع" subtitle="ترتيب المشاريع حسب الأداء" />

      {/* Sort options */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginLeft: 6 }}>ترتيب حسب:</span>
        {[
          { key: "sales_pct", label: "نسبة البيع" },
          { key: "count_sales_pct", label: "نسبة البيع (عدد)" },
          { key: "collection_pct", label: "نسبة التحصيل" },
          { key: "transacted_value", label: "قيمة المبيعات" },
          { key: "total_commission", label: "العمولات" },
          { key: "avg_price_per_sqm", label: "متوسط سعر م²" },
        ].map(s => (
          <button key={s.key} onClick={() => setSortBy(s.key)}
            style={{
              padding: "6px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6,
              border: "1px solid " + (sortBy === s.key ? "#2563eb" : "#e2e8f0"),
              background: sortBy === s.key ? "#2563eb" : "#fff",
              color: sortBy === s.key ? "#fff" : "#475569",
              cursor: "pointer", fontFamily: "inherit",
            }}>{s.label}</button>
        ))}
      </div>

      {/* Comparison table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                {/* v3.3.453 — All numeric columns: textAlign right + direction ltr for proper RTL layout */}
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#475569", width: 40 }}>#</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#475569" }}>المشروع</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#475569" }}>إجمالي</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#16a34a" }}>متاح</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#2563eb" }}>محجوز</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#dc2626" }}>مُباع</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#9333ea" }} title="الوحدات المباعة للشركة (سجل داخلي)">مباع داخلي</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#475569" }}>قيمة المشروع</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#0891b2" }}>المبيعات</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#16a34a" }}>المُحصّل</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#8b5cf6" }}>العمولات</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#475569", minWidth: 110 }}>نسبة البيع</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#475569", minWidth: 110 }}>نسبة التحصيل</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#475569" }}>م² سعر</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => (
                <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontWeight: 700, color: i === 0 ? "#f59e0b" : "#94a3b8" }}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, color: "#1e293b" }}>{p.name}</td>
                  {/* v3.3.453 — Numeric cells: right-aligned with LTR direction for clean RTL display */}
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmt(p.total_units)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#16a34a" }}>{fmt(p.available_units)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#2563eb" }}>{fmt(p.reserved_units)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#dc2626" }}>{fmt(p.sold_units)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#9333ea", fontWeight: 600 }}>{fmt(p.sold_internal_units)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(p.total_value)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#0891b2", fontWeight: 600 }}>{fmt(p.transacted_value)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#16a34a", fontWeight: 600 }}>{fmt(p.total_collected)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#8b5cf6" }}>{fmt(p.total_commission)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ flex: 1, height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${Math.min(100, p.sales_pct)}%`, height: "100%", background: "#2563eb" }} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#2563eb", minWidth: 35, direction: "ltr" }}>{pct(p.sales_pct)}</span>
                    </div>
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ flex: 1, height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${Math.min(100, p.collection_pct)}%`, height: "100%", background: "#16a34a" }} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", minWidth: 35, direction: "ltr" }}>{pct(p.collection_pct)}</span>
                    </div>
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "right", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#475569" }}>{fmt(p.avg_price_per_sqm)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 6. ReportsPage — التقارير المالية (P&L ملخص)
// ═══════════════════════════════════════════════════════════════════
export function FinancialReportsPage() {
  const projects = useProjects();
  const [projectId, setProjectId] = useState(null);
  const [data, setData] = useState({ revenue: {}, cash: {} });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = projectId ? { project_id: projectId } : {};
    financialAnalysis.getReports(params)
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل التقرير"))
      .finally(() => setLoading(false));
  }, [projectId]);

  const rev = data.revenue || {};
  const cash = data.cash || {};

  const LineItem = ({ label, value, color = "#1e293b", isTotal, isNegative, indent = 0 }) => (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: isTotal ? "12px 14px" : "8px 14px",
      background: isTotal ? "#f1f5f9" : "transparent",
      borderTop: isTotal ? "2px solid #1e293b" : "1px solid #f1f5f9",
      fontWeight: isTotal ? 800 : 500,
      paddingRight: 14 + indent * 20,
    }}>
      <span style={{ fontSize: isTotal ? 14 : 12, color: isTotal ? "#1e293b" : "#475569" }}>
        {isNegative && "−"} {label}
      </span>
      <span style={{ fontSize: isTotal ? 15 : 13, fontFamily: "monospace", color, fontWeight: isTotal ? 800 : 600 }}>
        {fmt(value)} ر.س
      </span>
    </div>
  );

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1000, margin: "0 auto" }}>
      <PageHeader icon={<FiFileText size={26} color="#dc2626" />} title="التقارير المالية" subtitle="ملخص الإيرادات والمصروفات والتدفق النقدي" />

      {/* Filters */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
        <FiFilter size={14} color="#64748b" />
        <ProjectFilter value={projectId} onChange={setProjectId} projects={projects} />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
      ) : (
        <>
          {/* Revenue (P&L-like) */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ background: "#1e293b", color: "#fff", padding: "12px 14px", fontWeight: 700, fontSize: 14 }}>
              💼 قائمة الدخل (Revenue Statement)
            </div>
            <LineItem label={`إجمالي قيمة المشروع (${fmt(rev.units_count)} وحدة)`} value={rev.official_value} />
            {/* Internal sales reference — separated from executed totals */}
            {rev.internal_count > 0 && (
              <>
                <LineItem 
                  label={`🏛️ المباع الداخلي (${rev.internal_count} وحدة — مرجعي)`}
                  value={rev.internal_value} 
                  color="#7f1d1d" 
                  indent={1}
                />
                <div style={{ height: 1, background: "#e2e8f0", margin: "0 14px" }} />
              </>
            )}
            <LineItem label="الإجمالي التنفيذي (بعد الخصومات)" value={rev.executed_value} color="#2563eb" indent={1} />
            <LineItem label="الخصومات المطبقة" value={rev.total_discount} color="#f59e0b" isNegative indent={1} />
            <LineItem label="إجمالي العمولات (الدلالة)" value={rev.total_commission} color="#8b5cf6" isNegative indent={1} />
            <LineItem label="الرسوم الإدارية" value={rev.total_admin_fees} color="#64748b" isNegative indent={1} />
            <LineItem label="رسوم الشركة" value={rev.total_company_fees} color="#64748b" isNegative indent={1} />
            <LineItem label="✅ صافي الإيرادات" value={rev.net_revenue} color="#16a34a" isTotal />
          </div>

          {/* Cash Flow Summary */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ background: "#0891b2", color: "#fff", padding: "12px 14px", fontWeight: 700, fontSize: 14 }}>
              💰 التدفق النقدي (Cash Flow)
            </div>
            <LineItem label="المحصّل من العملاء (نقدي)" value={cash.collected} color="#16a34a" />
            <LineItem label="المستحق للمسوقين (دلالات قيد الصرف)" value={cash.disbursed_due || 0} color="#f59e0b" isNegative indent={1} />
            <LineItem label="المصروف للمسوقين (دلالات صُرفت)" value={cash.disbursed} color="#dc2626" isNegative />
            <LineItem label="✅ صافي التدفق النقدي" value={cash.net_cash} color={cash.net_cash >= 0 ? "#16a34a" : "#dc2626"} isTotal />
          </div>

          {/* Summary Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <StatCard label="هامش الربح" value={rev.executed_value > 0 ? pct((rev.net_revenue / rev.executed_value) * 100) : "—"} color="#16a34a" bg="#f0fdf4" />
            <StatCard label="نسبة التحصيل" value={rev.executed_value > 0 ? pct((cash.collected / rev.executed_value) * 100) : "—"} color="#2563eb" bg="#eff6ff" />
            <StatCard label="نسبة العمولات" value={rev.executed_value > 0 ? pct((rev.total_commission / rev.executed_value) * 100) : "—"} color="#8b5cf6" bg="#faf5ff" />
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 7. CompanyAssetsPage — أصول الشركة (المباع داخلياً)
// ═══════════════════════════════════════════════════════════════════
export function CompanyAssetsPage() {
  const projects = useProjects();
  const [projectId, setProjectId] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all"); // all | commercial | residential
  const [data, setData] = useState({ units: [], by_project: [], summary: {} });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = projectId ? { project_id: projectId } : {};
    financialAnalysis.getCompanyAssets(params)
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل البيانات"))
      .finally(() => setLoading(false));
  }, [projectId]);

  const filtered = useMemo(() => {
    if (typeFilter === "commercial") return data.units.filter(u => u.is_commercial);
    if (typeFilter === "residential") return data.units.filter(u => !u.is_commercial);
    return data.units;
  }, [data.units, typeFilter]);

  // ─── Export ───
  const exportCSV = () => {
    const headers = ["المشروع", "المبنى", "الوحدة", "النوع", "الدور", "المساحة م²", "الغرف", "سعر الوحدة", "سعر الحجز", "القيمة المعتمدة", "العميل"];
    const rows = filtered.map(u => [
      u.project_name,
      u.building_name || "",
      u.unit_label || u.unit_number || "",
      u.is_commercial ? "تجاري" : "سكني",
      u.floor_number || "",
      u.area_sqm || "",
      u.rooms || "",
      u.unit_price,
      u.reservation_value || "",
      u.effective_value,
      u.client_name || "",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `company_assets_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("فشل فتح نافذة الطباعة"); return; }
    const rowsHtml = filtered.map(u => `
      <tr>
        <td>${u.project_name}</td>
        <td>${u.building_name || "—"}</td>
        <td style="text-align:center;font-weight:700">${u.unit_label || u.unit_number || ""}</td>
        <td style="text-align:center;color:${u.is_commercial ? "#f59e0b" : "#2563eb"};font-weight:700">${u.is_commercial ? "🏪 تجاري" : "🏠 سكني"}</td>
        <td style="text-align:center">${u.floor_number || "—"}</td>
        <td style="text-align:center">${u.area_sqm ? u.area_sqm + " م²" : "—"}</td>
        <td style="text-align:center">${u.rooms || "—"}</td>
        <td style="text-align:center;color:#7f1d1d;font-weight:700">${fmt(u.effective_value)}</td>
      </tr>
    `).join("");
    win.document.write(`
      <!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="utf-8">
      <title>أصول الشركة</title>
      <style>
        body { font-family: system-ui, sans-serif; padding: 20px; direction: rtl; }
        h1 { font-size: 20px; color: #1e293b; }
        .subtitle { color: #64748b; font-size: 12px; margin-bottom: 20px; }
        .summary { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
        .card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; min-width: 130px; }
        .card-label { font-size: 10px; color: #94a3b8; font-weight: 600; }
        .card-value { font-size: 16px; font-weight: 800; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { padding: 8px 6px; border-bottom: 1px solid #f1f5f9; }
        th { background: #f1f5f9; text-align: right; font-weight: 700; color: #475569; }
        @media print { .no-print { display: none; } body { padding: 10px; } }
      </style></head><body>
        <h1>🏛️ أصول الشركة</h1>
        <div class="subtitle">${projectId ? (projects.find(p => p.id == projectId)?.name || "") : "كل المشاريع"} — ${new Date().toLocaleDateString("ar-SA")}</div>
        <div class="summary">
          <div class="card"><div class="card-label">إجمالي الوحدات</div><div class="card-value">${fmt(data.summary.total_count)}</div></div>
          <div class="card"><div class="card-label">القيمة الإجمالية</div><div class="card-value" style="color:#7f1d1d">${fmt(data.summary.total_value)} ر.س</div></div>
          <div class="card"><div class="card-label">🏪 تجاري (${data.summary.commercial_count})</div><div class="card-value" style="color:#f59e0b">${fmt(data.summary.commercial_value)}</div></div>
          <div class="card"><div class="card-label">🏠 سكني (${data.summary.residential_count})</div><div class="card-value" style="color:#2563eb">${fmt(data.summary.residential_value)}</div></div>
          <div class="card"><div class="card-label">نسبة من المحفظة</div><div class="card-value" style="color:#0891b2">${Math.round(data.summary.overall_pct || 0)}%</div></div>
        </div>
        <div class="no-print" style="margin-bottom:12px"><button onclick="window.print()" style="padding:8px 20px;background:#7f1d1d;color:#fff;border:none;border-radius:6px;cursor:pointer">🖨️ طباعة / حفظ PDF</button></div>
        <table>
          <thead><tr>
            <th>المشروع</th><th>المبنى</th><th>الوحدة</th><th>النوع</th>
            <th>الدور</th><th>المساحة</th><th>الغرف</th><th>القيمة</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body></html>
    `);
    win.document.close();
  };

  // Commercial vs Residential split for visual bar
  const commercialPct = data.summary.total_value > 0 ? (data.summary.commercial_value / data.summary.total_value) * 100 : 0;
  const residentialPct = data.summary.total_value > 0 ? (data.summary.residential_value / data.summary.total_value) * 100 : 0;

  const pillBtn = (active, color = "#7f1d1d") => ({
    padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6,
    border: "1px solid " + (active ? color : "#e2e8f0"),
    background: active ? color : "#fff",
    color: active ? "#fff" : "#475569",
    cursor: "pointer", fontFamily: "inherit",
  });

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader 
        icon={<FiHome size={26} color="#7f1d1d" />} 
        title="أصول الشركة" 
        subtitle="الوحدات المباعة داخلياً (ملك الشركة) — قيمتها وتوزيعها حسب المشاريع والنوع" 
      />

      {/* Summary Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <StatCard label="🏛️ إجمالي الوحدات" value={fmt(data.summary.total_count)} color="#1e293b" bg="#f8fafc" />
        <StatCard label="💰 القيمة الإجمالية" value={`${fmt(data.summary.total_value)} ر.س`} color="#7f1d1d" bg="#fef2f2" />
        <StatCard label="🏪 تجاري" value={`${fmt(data.summary.commercial_value)} ر.س`} subValue={`${data.summary.commercial_count || 0} وحدة`} color="#f59e0b" bg="#fffbeb" />
        <StatCard label="🏠 سكني" value={`${fmt(data.summary.residential_value)} ر.س`} subValue={`${data.summary.residential_count || 0} وحدة`} color="#2563eb" bg="#eff6ff" />
        <StatCard label="📊 نسبة من المحفظة" value={pct(data.summary.overall_pct)} subValue={`من ${fmt(data.summary.total_portfolio_value)}`} color="#0891b2" bg="#ecfeff" />
      </div>

      {/* ─────── Commercial vs Residential Visual ─────── */}
      {data.summary.total_value > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 12px", color: "#1e293b" }}>🎯 توزيع الأصول حسب النوع</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", minWidth: 110 }}>
              🏪 تجاري ({Math.round(commercialPct)}%)
            </span>
            <div style={{ flex: 1, height: 30, background: "#f8fafc", borderRadius: 6, overflow: "hidden", display: "flex" }}>
              {commercialPct > 0 && (
                <div style={{
                  width: `${commercialPct}%`,
                  background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 11, fontWeight: 700,
                }}>
                  {commercialPct > 10 && `${fmt(data.summary.commercial_value)} ر.س`}
                </div>
              )}
              {residentialPct > 0 && (
                <div style={{
                  width: `${residentialPct}%`,
                  background: "linear-gradient(90deg, #2563eb, #3b82f6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 11, fontWeight: 700,
                }}>
                  {residentialPct > 10 && `${fmt(data.summary.residential_value)} ر.س`}
                </div>
              )}
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", minWidth: 110, textAlign: "left" }}>
              🏠 سكني ({Math.round(residentialPct)}%)
            </span>
          </div>

          {/* Area stats */}
          {data.summary.total_area > 0 && (
            <div style={{ display: "flex", gap: 20, marginTop: 14, paddingTop: 12, borderTop: "1px solid #f1f5f9", fontSize: 11, color: "#475569" }}>
              <span>📐 إجمالي المساحة: <strong style={{ color: "#1e293b" }}>{fmt(data.summary.total_area)} م²</strong></span>
              <span>🏪 تجاري: <strong style={{ color: "#f59e0b" }}>{fmt(data.summary.commercial_area)} م²</strong></span>
              <span>🏠 سكني: <strong style={{ color: "#2563eb" }}>{fmt(data.summary.residential_area)} م²</strong></span>
              <span style={{ marginRight: "auto" }}>💲 متوسط السعر/م²: <strong style={{ color: "#7f1d1d" }}>{fmt(data.summary.avg_price_per_sqm)} ر.س</strong></span>
            </div>
          )}
        </div>
      )}

      {/* ─────── Per-Project Breakdown ─────── */}
      {data.by_project.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 12px", color: "#1e293b" }}>📈 التوزيع حسب المشاريع</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {data.by_project.map(p => {
              const maxInternal = Math.max(...data.by_project.map(x => x.internal_total_value)) || 1;
              const commPct = p.internal_total_value > 0 ? (p.internal_commercial_value / p.internal_total_value) * 100 : 0;
              const resPct  = p.internal_total_value > 0 ? (p.internal_residential_value / p.internal_total_value) * 100 : 0;
              return (
                <div key={p.project_id}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 600, marginBottom: 5 }}>
                    <span style={{ color: "#1e293b" }}>{p.project_name}</span>
                    <span style={{ color: "#64748b" }}>
                      <strong style={{ color: "#7f1d1d" }}>{fmt(p.internal_total_value)} ر.س</strong> — {p.internal_count} وحدة ({Math.round(p.internal_pct)}% من قيمة المشروع)
                    </span>
                  </div>
                  <div style={{ height: 22, background: "#f8fafc", borderRadius: 5, overflow: "hidden", display: "flex" }}>
                    <div style={{ width: `${(p.internal_total_value / maxInternal) * 100}%`, height: "100%", display: "flex" }}>
                      {commPct > 0 && (
                        <div style={{
                          width: `${commPct}%`, background: "#f59e0b",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontSize: 9, fontWeight: 700,
                          borderLeft: resPct > 0 ? "1px solid rgba(255,255,255,0.5)" : "none",
                        }}>
                          {commPct > 15 && `🏪 ${p.internal_commercial_count}`}
                        </div>
                      )}
                      {resPct > 0 && (
                        <div style={{
                          width: `${resPct}%`, background: "#2563eb",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontSize: 9, fontWeight: 700,
                        }}>
                          {resPct > 15 && `🏠 ${p.internal_residential_count}`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─────── Filters ─────── */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <FiFilter size={14} color="#64748b" />
        <ProjectFilter value={projectId} onChange={setProjectId} projects={projects} />
        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, marginRight: 8 }}>النوع:</span>
        <button onClick={() => setTypeFilter("all")} style={pillBtn(typeFilter === "all", "#7f1d1d")}>الكل</button>
        <button onClick={() => setTypeFilter("commercial")} style={pillBtn(typeFilter === "commercial", "#f59e0b")}>🏪 تجاري</button>
        <button onClick={() => setTypeFilter("residential")} style={pillBtn(typeFilter === "residential", "#2563eb")}>🏠 سكني</button>
        <div style={{ display: "flex", gap: 6, marginRight: "auto" }}>
          <button onClick={exportCSV} style={{ padding: "7px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <FiDownload size={12} /> Excel
          </button>
          <button onClick={exportPDF} style={{ padding: "7px 14px", background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <FiFileText size={12} /> PDF
          </button>
        </div>
      </div>

      {/* ─────── Units Table ─────── */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>لا توجد أصول داخلية</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#475569" }}>المشروع</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#475569" }}>المبنى</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#475569" }}>الوحدة</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#475569" }}>النوع</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#475569" }}>الدور</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#475569" }}>المساحة</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#475569" }}>الغرف</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#64748b" }}>سعر الوحدة</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#64748b" }}>سعر الحجز</th>
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#7f1d1d" }}>القيمة المعتمدة</th>
                <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "#475569" }}>العميل</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={{ borderBottom: "1px solid #f1f5f9", background: u.is_commercial ? "#fffbeb" : "transparent" }}>
                  <td style={{ padding: "8px 8px", color: "#1e293b", fontWeight: 600 }}>{u.project_name}</td>
                  <td style={{ padding: "8px 8px", color: "#64748b" }}>{u.building_name || "—"}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>
                    <a href={`/projects/${u.project_id}/units/${u.id}`} style={{ color: "#1e40af", textDecoration: "none", borderBottom: "1px dashed #1e40af" }}>
                      {u.unit_label || u.unit_number}
                    </a>
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    {u.is_commercial ? (
                      <span style={{ background: "#fffbeb", color: "#92400e", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>🏪 تجاري</span>
                    ) : (
                      <span style={{ background: "#eff6ff", color: "#1e40af", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>🏠 سكني</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center", color: "#64748b" }}>{u.floor_number || "—"}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", color: "#64748b", fontSize: 11 }}>{u.area_sqm ? `${u.area_sqm} م²` : "—"}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", color: "#64748b" }}>{u.rooms || "—"}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", color: "#94a3b8" }}>{fmt(u.unit_price)}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", color: u.reservation_value ? "#2563eb" : "#cbd5e1" }}>
                    {u.reservation_value ? fmt(u.reservation_value) : "—"}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", color: "#7f1d1d", fontWeight: 700 }}>{fmt(u.effective_value)}</td>
                  <td style={{ padding: "8px 8px", color: "#475569", fontSize: 11 }}>
                    {u.client_name && u.reservation_id ? (
                      <a href={`/?edit=${u.reservation_id}`} target="_blank" rel="noopener noreferrer"
                         style={{ color: "#475569", textDecoration: "none", borderBottom: "1px dashed #94a3b8" }}>
                        {u.client_name}
                      </a>
                    ) : (u.client_name || "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CashFlowStatementPage — التدفقات النقدية (v3.3.387)
// كشف تدفقات نقدية بصيغة Excel:
//   Cash-in: Sales (تلقائي) + Loans + Investors (يدوي)
//   Cash-out: Assets (Brokerage Fees تلقائي + باقي يدوي) + Receivable + Expense
//   عمود "الفرق" يدوي اختياري (للتسوية)
// ═══════════════════════════════════════════════════════════════════

const CF_SECTIONS = {
  cash_in: {
    title: "Cash-in",
    color: "#16a34a",
    bg: "#f0fdf4",
    subs: [
      { key: "sales",     label: "Sales",     autoOnly: false, hint: "المبيعات (تلقائي من الدفعات)" },
      { key: "loans",     label: "Loans",     autoOnly: false, hint: "القروض" },
      { key: "investors", label: "Investors", autoOnly: false, hint: "المستثمرون" },
      // v3.3.485 — Manual "Other" subsection for miscellaneous cash-in items
      { key: "other",     label: "Other",     autoOnly: false, hint: "إيرادات أخرى (إدخال يدوي)" },
    ],
  },
  cash_out: {
    title: "Cash-out",
    color: "#dc2626",
    bg: "#fef2f2",
    subs: [
      // v3.3.462 — Restructured into clearer categories
      { key: "assets",     label: "Assets",     autoOnly: false, hint: "الأصول (Brokerage Fees تلقائي)" },  // legacy — kept for back-compat
      { key: "receivable", label: "Receivable", autoOnly: false, hint: "المستحقات" },                     // legacy
      { key: "expense",    label: "Expense",    autoOnly: false, hint: "المصاريف" },                       // legacy
    ],
  },
};

// v3.3.462 — Cash-out visual categorization
// v3.3.465 — Split "cash & equivalents" into two: pure cash + equivalents (receivables/advances)
const CASH_OUT_CATEGORIES = [
  { key: "cash",          label: "💵 النقد",                  color: "#0891b2", bg: "#cffafe", order: 1 },
  { key: "cash_equiv",    label: "📋 ما يوازي النقد",         color: "#0e7490", bg: "#ecfeff", order: 2 },
  { key: "projects",      label: "🏗️ المشاريع",                color: "#16a34a", bg: "#dcfce7", order: 3 },
  { key: "side",          label: "🌍 المشاريع الجانبية",       color: "#92400e", bg: "#fef3c7", order: 4 },
  { key: "equipment",     label: "🔧 العدة والمعدات",          color: "#475569", bg: "#f1f5f9", order: 5 },
  { key: "other",         label: "📦 أخرى",                    color: "#64748b", bg: "#f8fafc", order: 6 },
];

// Within "المشاريع" each project has 3 buckets
const PROJECT_BUCKETS = [
  { key: "costs",     label: "📋 تكاليف المشروع", color: "#15803d" },
  { key: "dalala",    label: "🤝 الدلالات",       color: "#9333ea" },
  { key: "financing", label: "💸 مصاريف التمويل", color: "#be185d" },
];

// Classify an item into one of the cash-out categories above
// v3.3.465 — Split cash vs cash-equivalents
function categorizeCashOutItem(it) {
  const label = String(it.label || "");
  const sk = String(it.source_key || "");
  const sub = String(it.subsection || "");

  // 1. Receivables → cash equivalents (per user request — they "owe us")
  if (sub === "receivable") return { cat: "cash_equiv" };

  // 2. Projects (auto-linked via source_key)
  // v3.3.471 — capex:project:N:GROUP suffix routes to proper bucket
  //   - capex:project:N:execution → costs (تكاليف المشروع)
  //   - capex:project:N:marketing → dalala (الدلالات/التسويق)
  //   - capex:project:N:financing → financing (مصاريف التمويل)
  // Legacy `capex:project:N` (no suffix) still routes to costs
  if (sk.startsWith("capex:")) {
    if (sk.endsWith(":financing")) return { cat: "projects", bucket: "financing" };
    if (sk.endsWith(":marketing")) return { cat: "projects", bucket: "dalala" };
    return { cat: "projects", bucket: "costs" };
  }
  if (sk.startsWith("dalala:") || sk.startsWith("brokerage:")) return { cat: "projects", bucket: "dalala" };

  // 3. Manual items: classify by label keywords
  if (/^(دلالات|دلالة|عمولة|عمولات|سعي)\b/.test(label)) return { cat: "projects", bucket: "dalala" };
  if (/(تمويل|مصاريف تمويلية|قسط|سداد قرض|سداد صكوك|رسوم بنك|فائدة|كوبون|admin fee|program fee)/i.test(label)) {
    return { cat: "projects", bucket: "financing" };
  }
  // v3.3.639 — البنوك: الحسابات البنكية (تُفصل عن الصناديق النقدية).
  // نتجنّب "الرياض" (اسم مدينة) و"bank" المفردة لمنع تصنيف بنود غير بنكية خطأً.
  if (/(بنك|مصرف|الراجحي|الأهلي|الاهلي|البلاد|الإنماء|الانماء|سامبا|الجزيرة|السعودي الفرنسي|العربي الوطني|bank account|حساب جاري|حساب توفير)/i.test(label)) {
    return { cat: "bank" };
  }
  // v3.3.465 — PURE CASH: الصناديق النقدية + الخزينة
  // v3.3.637 — "صندوق/صناديق" (الصندوق العام، صناديق التحصيل…) تُصنّف نقداً
  if (/^(كاش|نقد|صندوق|صناديق|وديعة|deposit)\b/.test(label) || /(صندوق|صناديق|كاش في|كاش الخزينة|كاش الخزنة|الخزينة|الخزنة|cash)/i.test(label)) {
    return { cat: "cash" };
  }
  // v3.3.465 — CASH EQUIVALENTS: receivables, supplier advances, contractor advances, etc.
  if (/(دفعات مقدمة|دفعة مقدمة|مقدمة موردين|مقدمة مقاولين|سلفة|سلفه|تأمين|تامين|عربون|advance|prepaid|receivable)/i.test(label)) {
    return { cat: "cash_equiv" };
  }
  if (/(الساحل للمقاولات|معدات|عدد|أدوات|ادوات|سيارة|سيارات|مركبة|equipment|tools|آلات|آلة)/i.test(label)) {
    return { cat: "equipment" };
  }
  if (/(الحمراء|أرض المروه|ارض المروة|أرض المروة|قطعة)/i.test(label)) {
    return { cat: "side" };
  }
  if (/(أرض|ارض|اراضي|أراضي|عقار)/i.test(label)) {
    return { cat: "side" };
  }
  // expense subsection items default to other
  if (sub === "expense") return { cat: "other" };
  // assets default — main project
  return { cat: "projects", bucket: "costs" };
}

function CFItemRow({ item, canEdit, onUpdate, onDelete, indent, bucketLabel, bucketColor, bucketTotal, bucketRowSpan, onMoveUp, onMoveDown }) {
  const [editing, setEditing] = React.useState(false);
  // v3.3.640 — بوب-أب تفاصيل الحساب (نفس صفحة الخزينة) عند الضغط على حساب مرتبط بدفترة
  const [showAccount, setShowAccount] = React.useState(false);
  const acctCode = item.linked_journal_account_code || null;
  const acctKind = /بنك|مصرف|الراجحي|الأهلي|الاهلي|البلاد|الإنماء|الانماء|سامبا/i.test(String(item.label || "")) ? "bank"
    : /صندوق|صناديق|نقد|كاش|خزينة|خزنة/i.test(String(item.label || "")) ? "cash" : "any";
  const [label, setLabel] = React.useState(item.label || "");
  const [amount, setAmount] = React.useState(item.amount || 0);
  const [diff, setDiff] = React.useState(item.diff_amount ?? "");
  // v3.3.450 — Linked contract dropdown for "loans" items
  const [linkedContractId, setLinkedContractId] = React.useState(item.linked_contract_id || "");
  const [linkableContracts, setLinkableContracts] = React.useState([]);
  const isLoansItem = item.subsection === "loans";

  const isAuto = item.is_auto;
  // v3.3.580 — Linked to a Daftra account or cost center
  // v3.3.649 — …or to an accounting-tree branch by ref_no (rolled-up net)
  const isLinkedDaftra = !!(item.linked_journal_account_code || item.linked_cost_center_id || item.linked_tree_ref_no);
  // v3.3.457 — Items linked to sukuk contracts are also "linked to system" (treated as auto for visual)
  const isLinked = isAuto || !!item.linked_contract_id;

  React.useEffect(() => {
    setLabel(item.label || "");
    setAmount(item.amount || 0);
    setDiff(item.diff_amount ?? "");
    setLinkedContractId(item.linked_contract_id || "");
  }, [item]);

  // v3.3.450 — Load linkable contracts when editing a loans item
  React.useEffect(() => {
    if (editing && isLoansItem && linkableContracts.length === 0) {
      financialAnalysis.getLinkableContracts()
        .then(r => setLinkableContracts(r.data.contracts || []))
        .catch(e => console.warn("Failed to load linkable contracts:", e.message));
    }
  }, [editing, isLoansItem, linkableContracts.length]);

  const save = async () => {
    const payload = { diff_amount: diff === "" ? null : parseFloat(diff) };
    if (!isAuto) {
      payload.label = label;
      // v3.3.450 — If linked, backend computes amount; otherwise use manual amount
      if (isLoansItem) {
        payload.linked_contract_id = linkedContractId ? parseInt(linkedContractId) : null;
        if (!linkedContractId) {
          payload.amount = parseFloat(amount) || 0;
        }
      } else {
        payload.amount = parseFloat(amount) || 0;
      }
    }
    await onUpdate(item.id, payload);
    setEditing(false);
  };

  const cancel = () => {
    setLabel(item.label || "");
    setAmount(item.amount || 0);
    setDiff(item.diff_amount ?? "");
    setLinkedContractId(item.linked_contract_id || "");
    setEditing(false);
  };

  // Color: linked (auto OR contract-linked) = green tint, purely manual = red tint
  const bg = isLinked ? "#dcfce7" : "#fee2e2";
  const labelColor = isLinked ? "#15803d" : "#991b1b";

  // v3.3.457 — Navigate to source page when clicking on a linked item
  const navigate = useNavigate();
  const handleSourceNavigate = (e) => {
    e.stopPropagation();
    // 1. Sukuk contract link (Loans subsection)
    if (item.linked_contract_id) {
      navigate(`/sukuk?contract=${item.linked_contract_id}`);
      return;
    }
    // 2. Auto items have a source_key like "sales:project:5" or "capex:project:10"
    if (item.source_key) {
      const parts = String(item.source_key).split(":");
      const kind = parts[0]; // sales, dalala, capex, brokerage, etc.
      const scopeKind = parts[1]; // project, etc.
      const targetId = parts[2];
      if (scopeKind === "project" && targetId) {
        if (kind === "capex" || kind === "brokerage") {
          navigate(`/capex?project=${targetId}`);
        } else {
          // sales, dalala → projects page
          navigate(`/projects?project=${targetId}`);
        }
        return;
      }
    }
  };
  const isClickable = isLinked && (item.linked_contract_id || item.source_key);

  return (
    <tr style={{ borderBottom: "1px solid #f1f5f9", background: indent ? "#fafafa" : undefined }}>
      {/* v3.3.471 — Bucket label cell: only rendered on first row in bucket, spans all rows in that bucket */}
      {bucketRowSpan > 0 && (
        <td
          rowSpan={bucketRowSpan}
          style={{
            padding: "8px 10px",
            width: 130,
            background: "#fafafa",
            borderLeft: "1px solid #f1f5f9",
            verticalAlign: "middle",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: bucketColor, lineHeight: 1.4 }}>
            {bucketLabel}
          </div>
          {bucketRowSpan > 1 && bucketTotal != null && (
            <div style={{ fontSize: 11, fontWeight: 700, color: bucketColor, direction: "ltr", fontVariantNumeric: "tabular-nums", marginTop: 3 }}>
              {fmt(bucketTotal)}
            </div>
          )}
        </td>
      )}
      <td style={{ padding: "8px 10px", width: 36, textAlign: "center" }}>
        {isLinked ? (
          <span title={isAuto ? "تلقائي (من النظام)" : "مرتبط بعقد صكوك"} style={{ fontSize: 14 }}>🟢</span>
        ) : (
          <span title="يدوي" style={{ fontSize: 14 }}>🔴</span>
        )}
      </td>
      <td style={{ padding: "8px 10px", background: bg, fontWeight: 600, color: labelColor, paddingRight: indent ? 30 : undefined }}>
        {indent && <span style={{ color: "#94a3b8", marginLeft: 4 }}>└</span>}
        {editing && !isAuto ? (
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            style={{ width: "100%", padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit", fontSize: 13 }}
          />
        ) : acctCode ? (
          // v3.3.640 — الحسابات المرتبطة بدفترة: الضغط يفتح تفاصيل الحساب (نفس بوب-أب الخزينة)
          <span
            onClick={() => setShowAccount(true)}
            style={{ cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3, color: "#0e7490" }}
            title="عرض تفاصيل الحساب والحركات"
          >
            {item.label}
          </span>
        ) : (
          // v3.3.457 — Make label clickable for navigation to source page
          isClickable ? (
            <span
              onClick={handleSourceNavigate}
              style={{ cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}
              title="اضغط للذهاب إلى الصفحة المصدر"
            >
              {item.label}
            </span>
          ) : (
            <span>{item.label}</span>
          )
        )}
        {/* v3.3.640 — بوب-أب تفاصيل الحساب */}
        {showAccount && acctCode && (
          <AccountDetailSlideOver account={{ code: acctCode, kind: acctKind }} onClose={() => setShowAccount(false)} />
        )}
        {/* v3.3.580 — Daftra link badge — v3.3.649 adds tree-branch (ref_no) rollup */}
        {!editing && isLinkedDaftra && (
          <span
            title={
              item.linked_tree_ref_no
                ? `مرتبط بفرع الشجرة (رقم مرجعي ${item.linked_tree_ref_no})${item.linked_tree_ref_name ? " — " + item.linked_tree_ref_name : ""} • القيمة = مجموع كل ما تحت الفرع، تُحدّث تلقائياً`
                : `مرتبط بـ ${item.linked_journal_account_code ? `حساب ${item.linked_journal_account_code}` : `مركز تكلفة #${item.linked_cost_center_id}`}${item.linked_journal_account_name || item.linked_cost_center_name ? " — " + (item.linked_journal_account_name || item.linked_cost_center_name) : ""} • القيمة تُحدّث تلقائياً من دفترة`
            }
            style={{
              marginInlineStart: 6, display: "inline-flex", alignItems: "center", gap: 3,
              padding: "1px 6px",
              background: item.linked_tree_ref_no ? "#dcfce7" : "#dbeafe",
              color: item.linked_tree_ref_no ? "#166534" : "#1e40af",
              borderRadius: 4, fontSize: 10, fontWeight: 600,
            }}>
            {item.linked_tree_ref_no
              ? `🌳 فرع ${item.linked_tree_ref_no}`
              : `🔗 ${item.linked_journal_account_code ? `حساب ${item.linked_journal_account_code}` : `م.تكلفة`}`}
          </span>
        )}
        {/* v3.3.450 — Linked contract dropdown (for loans items only) */}
        {editing && !isAuto && isLoansItem && (
          <div style={{ marginTop: 6 }}>
            <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>🔗 ربط بعقد صكوك (اختياري):</label>
            <select
              value={linkedContractId}
              onChange={e => setLinkedContractId(e.target.value)}
              style={{ width: "100%", padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit", fontSize: 12, marginTop: 2 }}
            >
              <option value="">— بدون ربط (مبلغ يدوي) —</option>
              {linkableContracts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} (المبلغ القائم: {Number(c.net_received_active).toLocaleString()})
                </option>
              ))}
            </select>
            {linkedContractId && (
              <div style={{ fontSize: 10, color: "#1e40af", marginTop: 3, fontWeight: 600 }}>
                💡 المبلغ سيُحسب تلقائياً من إجمالي مبالغ الإصدارات القائمة (نشطة - مسددة) — بدون خصم رسوم
              </div>
            )}
          </div>
        )}
      </td>
      <td style={{ padding: "8px 10px", textAlign: "right", direction: "ltr", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
        {/* v3.3.580 — also disable amount editing when linked to Daftra */}
        {editing && !isAuto && !linkedContractId && !isLinkedDaftra ? (
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            style={{ width: 130, padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
          />
        ) : (
          <span
            style={(item.linked_contract_id || isLinkedDaftra) ? { color: "#1e40af" } : {}}
            title={
              item.linked_contract_id ? "محسوب تلقائياً من العقد المرتبط" :
              isLinkedDaftra ? "محسوب تلقائياً من دفترة (لا يمكن التعديل اليدوي)" : ""
            }>
            {fmt(item.amount)}
          </span>
        )}
      </td>
      <td style={{ padding: "8px 10px", textAlign: "right", direction: "ltr", color: "#64748b", fontVariantNumeric: "tabular-nums" }}>
        {editing ? (
          <input
            type="number"
            value={diff}
            onChange={e => setDiff(e.target.value)}
            placeholder="—"
            style={{ width: 130, padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
          />
        ) : (
          item.diff_amount != null && item.diff_amount !== ""
            ? `(${fmt(Math.abs(item.diff_amount))})`
            : ""
        )}
      </td>
      <td style={{ padding: "8px 10px", width: 130, textAlign: "center" }}>
        {!canEdit ? null : editing ? (
          <span style={{ display: "inline-flex", gap: 4 }}>
            <button onClick={save} style={{ padding: "4px 10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>حفظ</button>
            <button onClick={cancel} style={{ padding: "4px 10px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>إلغاء</button>
          </span>
        ) : (
          <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
            {/* v3.3.637 — تحريك العنصر أعلى/أسفل داخل القسم */}
            {(onMoveUp || onMoveDown) && (
              <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.8 }}>
                <button onClick={onMoveUp} disabled={!onMoveUp} title="أعلى" style={{ background: "transparent", border: "none", cursor: onMoveUp ? "pointer" : "default", opacity: onMoveUp ? 0.7 : 0.2, fontSize: 10, padding: 0 }}>▲</button>
                <button onClick={onMoveDown} disabled={!onMoveDown} title="أسفل" style={{ background: "transparent", border: "none", cursor: onMoveDown ? "pointer" : "default", opacity: onMoveDown ? 0.7 : 0.2, fontSize: 10, padding: 0 }}>▼</button>
              </span>
            )}
            <button onClick={() => setEditing(true)} style={{ padding: "4px 10px", background: "#e0e7ff", color: "#3730a3", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>تعديل</button>
            {!isAuto && (
              <button onClick={() => onDelete(item.id)} style={{ padding: "4px 10px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>🗑️</button>
            )}
          </span>
        )}
      </td>
    </tr>
  );
}

// v3.3.580 — Autocomplete that searches Daftra journal accounts + cost centers.
// When user selects a suggestion, the parent's onPick fires with { kind, code, id, name }
// and the amount field auto-fills (live-computed by backend on save + every GET).
function CFAccountAutocomplete({ value, onChange, onPick, onClearLink, linked, placeholder }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [highlightIdx, setHighlightIdx] = React.useState(-1);
  const blurTimer = React.useRef(null);
  const debounceTimer = React.useRef(null);

  // Debounced search
  React.useEffect(() => {
    if (linked) return; // when linked, search is disabled
    if (!value || value.trim().length < 1) { setSuggestions([]); return; }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await financialAnalysis.cfAccountSearch(value.trim(), 20);
        setSuggestions(r.data.items || []);
      } catch (e) { setSuggestions([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceTimer.current);
  }, [value, linked]);

  const pick = (s) => {
    setOpen(false);
    setSuggestions([]);
    onPick(s);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && highlightIdx >= 0) { e.preventDefault(); pick(suggestions[highlightIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div style={{ position: "relative" }}>
      {linked ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 6, fontSize: 12 }}>
          <span style={{ fontSize: 12 }}>🔗</span>
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600, color: "#1e40af" }}>
            {linked.kind === "tree_ref" ? "فرع (مرجعي)" : linked.kind === "account" ? "حساب" : "م. تكلفة"} {linked.code ? `${linked.code} ` : ""}{linked.name || value}
          </span>
          <button type="button" onClick={onClearLink} title="إلغاء الربط"
            style={{ background: "none", border: "none", color: "#1e40af", cursor: "pointer", fontWeight: 700, fontSize: 14, padding: "0 4px" }}>
            ✕
          </button>
        </div>
      ) : (
        <>
          <input
            autoFocus
            value={value}
            onChange={e => { onChange(e.target.value); setOpen(true); setHighlightIdx(-1); }}
            onFocus={() => setOpen(true)}
            onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
            onKeyDown={onKeyDown}
            placeholder={placeholder || "اسم العنصر أو رقم حساب/مركز تكلفة/فرع مرجعي..."}
            style={{ width: "100%", padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit", fontSize: 13 }}
          />
          {open && (suggestions.length > 0 || loading) && (
            <div style={{
              position: "absolute", top: "100%", insetInlineStart: 0, insetInlineEnd: 0,
              marginTop: 2, background: "white", border: "1px solid #cbd5e1", borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100, maxHeight: 280, overflowY: "auto",
            }} onMouseDown={e => { e.preventDefault(); clearTimeout(blurTimer.current); }}>
              {loading && <div style={{ padding: "8px 10px", fontSize: 12, color: "#94a3b8" }}>جاري البحث...</div>}
              {!loading && suggestions.length === 0 && <div style={{ padding: "8px 10px", fontSize: 12, color: "#94a3b8" }}>لا توجد نتائج — اكتب لإضافة بند يدوي</div>}
              {suggestions.map((s, idx) => (
                <div key={`${s.kind}-${s.id}`} onClick={() => pick(s)}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  style={{
                    padding: "7px 10px", cursor: "pointer", fontSize: 12,
                    background: highlightIdx === idx ? "#f1f5f9" : "white",
                    borderBottom: idx < suggestions.length - 1 ? "1px solid #f1f5f9" : "none",
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                  <span style={{ fontSize: 14 }}>{s.kind === "tree_ref" ? "🌳" : s.kind === "account" ? "🏦" : "🏷️"}</span>
                  <span style={{ fontFamily: "monospace", color: "#64748b", fontSize: 11, minWidth: 60 }}>{s.code || "—"}</span>
                  <span style={{ flex: 1, fontWeight: 500 }}>{s.name}</span>
                  <span style={{ fontSize: 10, color: s.kind === "tree_ref" ? "#16a34a" : "#94a3b8", fontWeight: s.kind === "tree_ref" ? 700 : 400 }}>{s.kind === "tree_ref" ? "فرع (مجموع)" : s.kind === "account" ? "حساب" : "م. تكلفة"}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CFAddItemForm({ onAdd, onCancel }) {
  const [label, setLabel] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [diff, setDiff] = React.useState("");
  // v3.3.580 — linked source (null = manual entry)
  const [linked, setLinked] = React.useState(null); // { kind, code, id, name }
  const [loadingAmount, setLoadingAmount] = React.useState(false);

  const onPickAccount = async (s) => {
    setLabel(s.name);
    setLinked({ kind: s.kind, code: s.code, id: s.id, name: s.name });
    // Preview amount immediately (the backend will recompute on save anyway, but show it now)
    setLoadingAmount(true);
    try {
      // No dedicated endpoint — submit will compute. For preview, do a quick POST-less estimate
      // by calling the search-then-balance helper. We'll just leave the field empty for now and
      // let the backend fill it in on save.
      setAmount("...");
    } finally { setLoadingAmount(false); }
  };

  const clearLink = () => {
    setLinked(null);
    setLabel("");
    setAmount("");
  };

  const submit = async () => {
    if (!label.trim() && !linked) { toast.error("الاسم مطلوب"); return; }
    const payload = {
      label: (linked?.name || label).trim(),
      amount: linked ? 0 : (parseFloat(amount) || 0), // backend recomputes when linked
      diff_amount: diff === "" ? null : parseFloat(diff),
    };
    if (linked?.kind === "account") payload.linked_journal_account_code = linked.code;
    if (linked?.kind === "cost_center") payload.linked_cost_center_id = parseInt(linked.id);
    if (linked?.kind === "tree_ref") payload.linked_tree_ref_no = linked.code; // v3.3.649 — code = ref_no
    await onAdd(payload);
    setLabel(""); setAmount(""); setDiff(""); setLinked(null);
  };

  return (
    <tr style={{ background: "#f8fafc" }}>
      <td style={{ padding: "8px 10px", textAlign: "center" }}>🔴</td>
      <td style={{ padding: "8px 10px" }}>
        <CFAccountAutocomplete
          value={label}
          onChange={(v) => setLabel(v)}
          onPick={onPickAccount}
          onClearLink={clearLink}
          linked={linked}
        />
      </td>
      <td style={{ padding: "8px 10px", textAlign: "right" }}>
        <input
          type={linked ? "text" : "number"}
          value={linked ? "محسوب تلقائياً" : amount}
          onChange={e => setAmount(e.target.value)}
          disabled={!!linked}
          placeholder="0"
          title={linked ? "القيمة تُحسب تلقائياً من دفترة عند الحفظ" : ""}
          style={{
            width: 130, padding: "5px 8px",
            border: "1px solid " + (linked ? "#bfdbfe" : "#cbd5e1"),
            background: linked ? "#dbeafe" : "white",
            color: linked ? "#1e40af" : "inherit",
            borderRadius: 6, textAlign: linked ? "center" : "right", fontVariantNumeric: "tabular-nums",
            fontSize: linked ? 11 : 13, fontStyle: linked ? "italic" : "normal",
            cursor: linked ? "not-allowed" : "text",
          }}
        />
      </td>
      <td style={{ padding: "8px 10px", textAlign: "right" }}>
        <input
          type="number"
          value={diff}
          onChange={e => setDiff(e.target.value)}
          placeholder="فرق (اختياري)"
          style={{ width: 130, padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 6, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
        />
      </td>
      <td style={{ padding: "8px 10px", textAlign: "center" }}>
        <span style={{ display: "inline-flex", gap: 4 }}>
          <button onClick={submit} style={{ padding: "4px 10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>إضافة</button>
          <button onClick={onCancel} style={{ padding: "4px 10px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>إلغاء</button>
        </span>
      </td>
    </tr>
  );
}

// v3.3.462 — Restructured Cash-out view
// Replaces the 3 flat subsections with 4 categories: cash_equiv, projects, side, equipment
// Inside "projects", each project is broken into 3 buckets: costs, dalala, financing
function CashOutRestructured({ items, canEdit, onAdd, onUpdate, onDelete, onReorder }) {
  const [collapsed, setCollapsed] = React.useState({});
  const [adding, setAdding] = React.useState({ cat: null, bucket: null, projectId: null, sub: null });

  const cashOutItems = items.filter(it => it.section === "cash_out");

  // Group items by category and (for projects) by project name + bucket
  const grouped = React.useMemo(() => {
    // v3.3.465 — added "cash" as separate bucket
    const out = { cash: [], bank: [], cash_equiv: [], side: [], equipment: [], other: [] };
    const projects = {}; // { projectKey: { name, costs: [], dalala: [], financing: [] } }
    const dalalaPrefix = /^(دلالات|دلالة)\s+/;
    const norm = (s) => String(s || "").replace(/[\u064B-\u0652\u0670]/g, "").replace(/\s+/g, " ").trim().toLowerCase();
    const tokensOf = (s) => norm(s).split(/[\s&,\-_]+/).filter(Boolean);

    // First pass: classify and bucket
    const projectItems = [];
    for (const it of cashOutItems) {
      const { cat, bucket } = categorizeCashOutItem(it);
      if (cat === "projects") {
        projectItems.push({ ...it, _bucket: bucket });
      } else {
        out[cat].push(it);
      }
    }

    // For projects: figure out main project name for each item
    // Strategy: main project items (costs) define group keys; dalala/financing items match by token overlap
    const mainItems = projectItems.filter(i => i._bucket === "costs");
    const subItems = projectItems.filter(i => i._bucket !== "costs");

    const getProjectKey = (label) => norm(label).replace(dalalaPrefix, "");

    // Initialize groups from main project items
    // v3.3.482/483 — Strip the "📊 " emoji prefix AND any legacy "— group label" suffix
    // (older v3.3.471-481 inserted labels like "📊 ميلينيوم — 🏗️ تكاليف التنفيذ".
    //  After v3.3.482 the backend emits clean labels, but old DB rows may persist
    //  until auto-items are deleted+rebuilt. This defensive strip handles both.)
    const cleanProjectName = (lbl) => String(lbl || "")
      .replace(/^📊\s*/, "")
      .replace(/\s*[—\-–]\s*(🏗️|📋|🤝|💸|📢|🌸)?\s*(تكاليف\s+التنفيذ|التكاليف\s+التسويقية|التكاليف\s+التمويلية|الدلالات|المصاريف\s+التسويقية).*$/u, "")
      .trim();
    for (const m of mainItems) {
      const cleaned = cleanProjectName(m.label);
      const key = getProjectKey(cleaned);
      if (!projects[key]) projects[key] = { key, name: cleaned, costs: [], dalala: [], financing: [] };
      projects[key].costs.push(m);
    }

    // v3.3.627 — مطابقة متسامحة مع تباين تهجئة أحرف العلّة (ا/و/ي/ى) بين اسم مشروع
    // المصاريف الرأسمالية (capex_projects) واسم مشروع المبيعات (projects)، إذ يُربطان
    // بالاسم فقط. مثال: "ميلينيوم" ↔ "ملينيوم رئال" (الدلالات كانت تظهر كمجموعة منفصلة).
    const skel = (t) => t.replace(/[اأإآوويىئ]/g, "");
    const tokenMatch = (a, b) => a === b || a.includes(b) || b.includes(a)
      || (skel(a).length >= 3 && skel(a) === skel(b));
    // Match sub items (dalala/financing) to projects by token overlap
    for (const s of subItems) {
      // جرّد البادئة الرمزية (💼/📊) وكلمة "دلالات" للوصول لاسم المشروع النظيف
      const baseLabel = s.label.replace(/^\s*[^\p{L}\p{N}]+/u, "").replace(dalalaPrefix, "").trim();
      const stokens = tokensOf(baseLabel);
      let bestMatch = null, bestScore = 0;
      for (const pk in projects) {
        const ptokens = tokensOf(projects[pk].name);
        const score = stokens.filter(t => ptokens.some(pt => tokenMatch(pt, t))).length;
        if (score > bestScore) { bestScore = score; bestMatch = pk; }
      }
      if (bestMatch && bestScore > 0) {
        projects[bestMatch][s._bucket].push(s);
      } else {
        // No matching project — create a standalone group named after the item
        // v3.3.482 — Strip emoji prefix
        const cleaned = cleanProjectName(baseLabel || s.label);
        const key = getProjectKey(cleaned);
        if (!projects[key]) projects[key] = { key, name: cleaned, costs: [], dalala: [], financing: [] };
        projects[key][s._bucket].push(s);
      }
    }

    // Convert to sorted array (largest project first)
    const projectList = Object.values(projects).sort((a, b) => {
      const tA = a.costs.concat(a.dalala, a.financing).reduce((s, x) => s + Number(x.amount || 0), 0);
      const tB = b.costs.concat(b.dalala, b.financing).reduce((s, x) => s + Number(x.amount || 0), 0);
      return tB - tA;
    });

    return { ...out, projects: projectList };
  }, [cashOutItems]);

  const catTotal = (arr) => arr.reduce((s, it) => s + Number(it.amount || 0), 0);

  // Helper: add to a specific category (asks for subsection automatically)
  const addToCategory = (cat, bucket = null) => {
    // Determine which DB subsection to use:
    // v3.3.465 — cash and cash_equiv map to different subsections for clarity
    let sub = "assets";
    if (cat === "cash") sub = "assets";              // pure cash → assets
    if (cat === "cash_equiv") sub = "receivable";    // equivalents → receivable
    if (cat === "other") sub = "expense";
    setAdding({ cat, bucket, sub });
  };

  return (
    <div>
      {/* v3.3.465 — 1a. النقد — الصناديق النقدية فقط */}
      <CashOutCategoryBlock
        catKey="cash"
        label="💵 النقد (الصناديق)"
        color="#0891b2" bg="#cffafe"
        items={grouped.cash}
        total={catTotal(grouped.cash)}
        canEdit={canEdit}
        collapsed={!!collapsed.cash}
        onToggle={() => setCollapsed(p => ({ ...p, cash: !p.cash }))}
        onAddRequest={() => addToCategory("cash")}
        onUpdate={onUpdate} onDelete={onDelete} onReorder={onReorder}
      />

      {/* v3.3.639 — 1a-bis. البنوك — الحسابات البنكية */}
      <CashOutCategoryBlock
        catKey="bank"
        label="🏦 البنوك"
        color="#1d4ed8" bg="#dbeafe"
        items={grouped.bank}
        total={catTotal(grouped.bank)}
        canEdit={canEdit}
        collapsed={!!collapsed.bank}
        onToggle={() => setCollapsed(p => ({ ...p, bank: !p.bank }))}
        onAddRequest={() => addToCategory("bank")}
        onUpdate={onUpdate} onDelete={onDelete} onReorder={onReorder}
      />

      {/* v3.3.465 — 1b. ما يوازي النقد (receivables, advances, prepayments) */}
      <CashOutCategoryBlock
        catKey="cash_equiv"
        label="📋 ما يوازي النقد (مستحقات ودفعات مقدمة)"
        color="#0e7490" bg="#ecfeff"
        items={[...grouped.cash_equiv, ...cashOutItems.filter(it => it.subsection === "receivable" && !grouped.cash_equiv.some(g => g.id === it.id))]}
        total={catTotal(grouped.cash_equiv) + catTotal(cashOutItems.filter(it => it.subsection === "receivable" && !grouped.cash_equiv.some(g => g.id === it.id)))}
        canEdit={canEdit}
        collapsed={!!collapsed.cash_equiv}
        onToggle={() => setCollapsed(p => ({ ...p, cash_equiv: !p.cash_equiv }))}
        onAddRequest={() => addToCategory("cash_equiv")}
        onUpdate={onUpdate} onDelete={onDelete} onReorder={onReorder}
      />

      {/* 2. المشاريع — كل مشروع له 3 buckets */}
      <div style={{ background: "#dcfce7", border: "2px solid #16a34a", borderRadius: 10, padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <button onClick={() => setCollapsed(p => ({ ...p, projects: !p.projects }))}
            style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
            {collapsed.projects ? "▶" : "▼"} 🏗️ المشاريع <span style={{ fontSize: 11, opacity: 0.8 }}>({grouped.projects.length})</span>
          </button>
          <span style={{ fontWeight: 800, fontSize: 14, color: "#14532d", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
            {fmt(grouped.projects.reduce((s, p) => s + p.costs.concat(p.dalala, p.financing).reduce((ss, x) => ss + Number(x.amount || 0), 0), 0))}
          </span>
        </div>
        {!collapsed.projects && grouped.projects.map(proj => {
          const projTotal = [...proj.costs, ...proj.dalala, ...proj.financing].reduce((s, x) => s + Number(x.amount || 0), 0);
          const projKey = "proj:" + proj.key;
          const isProjCollapsed = !!collapsed[projKey];
          return (
            <div key={proj.key} style={{ background: "#fff", borderRadius: 8, marginBottom: 8, border: "1px solid #bbf7d0", overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f0fdf4", borderBottom: "1px solid #bbf7d0" }}>
                <button onClick={() => setCollapsed(p => ({ ...p, [projKey]: !p[projKey] }))}
                  style={{ background: "transparent", border: "none", color: "#14532d", fontWeight: 800, fontSize: 13, cursor: "pointer", padding: 0 }}>
                  {isProjCollapsed ? "▶" : "▼"} 🏢 {proj.name}
                </button>
                <span style={{ fontWeight: 800, color: "#14532d", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(projTotal)}</span>
              </div>
              {/* v3.3.471 — Bucket label inlined into the item row (same row, not separate header above) */}
              {!isProjCollapsed && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    {PROJECT_BUCKETS.flatMap(b => {
                      const bItems = proj[b.key] || [];
                      if (bItems.length === 0) return [];
                      const bTotal = bItems.reduce((s, x) => s + Number(x.amount || 0), 0);
                      return bItems.map((it, idx) => (
                        <CFItemRow
                          key={it.id}
                          item={it}
                          canEdit={canEdit}
                          onUpdate={onUpdate}
                          onDelete={onDelete}
                          bucketLabel={idx === 0 ? b.label : null}
                          bucketColor={b.color}
                          bucketTotal={idx === 0 ? bTotal : null}
                          bucketRowSpan={idx === 0 ? bItems.length : 0}
                        />
                      ));
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      {/* 3. المشاريع الجانبية */}
      <CashOutCategoryBlock
        catKey="side"
        label="🌍 المشاريع الجانبية"
        color="#92400e" bg="#fef3c7"
        items={grouped.side}
        total={catTotal(grouped.side)}
        canEdit={canEdit}
        collapsed={!!collapsed.side}
        onToggle={() => setCollapsed(p => ({ ...p, side: !p.side }))}
        onAddRequest={() => addToCategory("side")}
        onUpdate={onUpdate} onDelete={onDelete} onReorder={onReorder}
      />

      {/* 4. العدة والمعدات */}
      <CashOutCategoryBlock
        catKey="equipment"
        label="🔧 العدة والمعدات"
        color="#475569" bg="#f1f5f9"
        items={grouped.equipment}
        total={catTotal(grouped.equipment)}
        canEdit={canEdit}
        collapsed={!!collapsed.equipment}
        onToggle={() => setCollapsed(p => ({ ...p, equipment: !p.equipment }))}
        onAddRequest={() => addToCategory("equipment")}
        onUpdate={onUpdate} onDelete={onDelete} onReorder={onReorder}
      />

      {/* 5. أخرى */}
      {grouped.other.length > 0 && (
        <CashOutCategoryBlock
          catKey="other"
          label="📦 أخرى"
          color="#64748b" bg="#f8fafc"
          items={grouped.other}
          total={catTotal(grouped.other)}
          canEdit={canEdit}
          collapsed={!!collapsed.other}
          onToggle={() => setCollapsed(p => ({ ...p, other: !p.other }))}
          onAddRequest={() => addToCategory("other")}
          onUpdate={onUpdate} onDelete={onDelete} onReorder={onReorder}
        />
      )}

      {/* Add form when adding */}
      {adding.cat && (
        <div style={{ background: "#fff", border: "1px solid #2563eb", borderRadius: 8, padding: 12, marginTop: 8 }}>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
            إضافة عنصر إلى: <strong>{CASH_OUT_CATEGORIES.find(c => c.key === adding.cat)?.label}</strong>
          </div>
          <CFAddItemForm
            onAdd={async (payload) => {
              await onAdd({ section: "cash_out", subsection: adding.sub, ...payload });
              setAdding({ cat: null, sub: null });
            }}
            onCancel={() => setAdding({ cat: null, sub: null })}
          />
        </div>
      )}
    </div>
  );
}

// Generic category block (collapsible)
function CashOutCategoryBlock({ catKey, label, color, bg, items, total, canEdit, collapsed, onToggle, onAddRequest, onUpdate, onDelete, onReorder }) {
  // v3.3.637 — تحريك عنصر داخل القسم: نعيد إرسال ترتيب معرّفات القسم بالكامل
  const moveItem = (from, to) => {
    if (!onReorder) return;
    const arr = items.slice();
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    onReorder(arr.map(x => x.id));
  };
  return (
    <div style={{ background: bg, border: "2px solid " + color, borderRadius: 10, padding: 12, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: items.length > 0 && !collapsed ? 8 : 0 }}>
        <button onClick={onToggle}
          style={{ background: color, color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
          {collapsed ? "▶" : "▼"} {label} <span style={{ fontSize: 11, opacity: 0.8 }}>({items.length})</span>
        </button>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontWeight: 800, fontSize: 14, color, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
            {fmt(total)}
          </span>
          {canEdit && (
            <button onClick={onAddRequest}
              style={{ background: "#fff", color: color, border: "1px solid " + color, borderRadius: 5, padding: "3px 10px", fontWeight: 700, cursor: "pointer", fontSize: 11 }}>
              + إضافة
            </button>
          )}
        </div>
      </div>
      {!collapsed && items.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 6, overflow: "hidden" }}>
          <tbody>
            {items.map((it, idx) => (
              <CFItemRow key={it.id} item={it} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete}
                onMoveUp={canEdit && onReorder && idx > 0 ? () => moveItem(idx, idx - 1) : null}
                onMoveDown={canEdit && onReorder && idx < items.length - 1 ? () => moveItem(idx, idx + 1) : null} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CFSubsection({ flowKey, sub, items, canEdit, onAdd, onUpdate, onDelete }) {
  const [adding, setAdding] = React.useState(false);
  // v3.3.437 — Track which project groups are expanded
  const [collapsedGroups, setCollapsedGroups] = React.useState({});
  // v3.3.461 — Collapse/expand asset categories (cash, projects, brokerage, land, equipment, other)
  const [collapsedCategories, setCollapsedCategories] = React.useState({});

  const subItems = items.filter(it => it.section === flowKey && (it.subsection || "") === sub.key);
  const subTotal = subItems.reduce((s, it) => s + (Number(it.amount) || 0), 0);

  // v3.3.461 — For "assets" subsection, split into visual categories
  // Categories detected by source_key (linked items) or by label keywords (manual items)
  const useAssetCategories = sub.key === "assets";
  const assetCategoryOf = (it) => {
    const label = String(it.label || "");
    const sk = String(it.source_key || "");
    // 1. Linked: dalala/brokerage source_key
    if (sk.startsWith("dalala:") || sk.startsWith("brokerage:")) return "brokerage";
    if (/^(دلالات|دلالة|عمولة|عمولات|سعي)\b/.test(label)) return "brokerage";
    // 2. Linked: capex source_key → projects
    if (sk.startsWith("capex:")) return "projects";
    // 3. Manual keywords
    if (/(كاش|بنك|خزنة|خزينة|نقد|cash)/i.test(label)) return "cash";
    if (/(أرض|ارض|اراضي|أراضي|قطعة|محل|عقار)/i.test(label)) return "land";
    if (/(معدات|عدد|أدوات|ادوات|سيارة|سيارات|مركبة|equipment|tools)/i.test(label)) return "equipment";
    return "other";
  };
  const ASSET_CATEGORIES = [
    { key: "cash",       label: "💵 النقد ومايوازيه",        color: "#0891b2", bg: "#cffafe" },
    { key: "projects",   label: "🏗️ المشاريع",                color: "#16a34a", bg: "#dcfce7" },
    { key: "brokerage",  label: "🤝 الدلالات والعمولات",     color: "#9333ea", bg: "#f3e8ff" },
    { key: "land",       label: "🌍 الأراضي",                 color: "#92400e", bg: "#fef3c7" },
    { key: "equipment",  label: "🔧 العدد والمعدات",         color: "#475569", bg: "#f1f5f9" },
    { key: "other",      label: "📦 أخرى",                    color: "#64748b", bg: "#f8fafc" },
  ];

  // v3.3.437/441 — Build hierarchical groups: main project + its "دلالات [name]" items
  // v3.3.441 — Smart matching: a dalala can match a main project if either contains the other's key part
  //            Example: "دلالات بارك رزيدنس 2" matches "بارك رزيدنس 2&3"
  // Apply only to assets section (where this pattern exists)
  const useGrouping = sub.key === "assets";
  const groups = React.useMemo(() => {
    if (!useGrouping) {
      return subItems.map(it => ({ kind: "single", item: it }));
    }
    // Helpers for matching
    const dalalaPrefix = /^(دلالات|دلالة)\s+/;
    // Normalize: lowercase, trim, collapse whitespace, strip diacritics & punctuation that doesn't matter
    const norm = (s) => String(s || "")
      .replace(/[\u064B-\u0652\u0670]/g, "") // diacritics
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
    // Extract tokens for "best match" — keep number tokens (1, 2, 3) separate from text
    const tokensOf = (s) => norm(s).split(/[\s&,\-_]+/).filter(Boolean);

    const dalalaItems = subItems.filter(it => dalalaPrefix.test(it.label || ""));
    const mainItems = subItems.filter(it => !dalalaPrefix.test(it.label || ""));

    // For each dalala: extract the base name and tokens
    const dalalaInfo = dalalaItems.map(d => {
      const base = (d.label || "").replace(dalalaPrefix, "").trim();
      return {
        item: d,
        baseName: base,
        baseNameNorm: norm(base),
        tokens: tokensOf(base),
      };
    });

    // Score a (dalala, main) pair:
    //   • exact normalized match → 100
    //   • main contains dalala's full base name → 80
    //   • dalala's tokens are subset of main's tokens AND share a numeric token (1, 2, 3, etc) → 70
    //   • token overlap ratio (Jaccard) > 0.6 → 50
    //   • else → 0 (no match)
    const scoreMatch = (dInfo, main) => {
      const mNorm = norm(main.label);
      const mTokens = tokensOf(main.label);
      if (!dInfo.baseNameNorm || !mNorm) return 0;
      if (dInfo.baseNameNorm === mNorm) return 100;
      // Check if main label contains the full base name as a substring
      if (mNorm.includes(dInfo.baseNameNorm)) return 80;
      // Numeric tokens check
      const dNums = dInfo.tokens.filter(t => /^\d+$/.test(t));
      const mNums = mTokens.filter(t => /^\d+$/.test(t));
      const sharedNum = dNums.some(n => mNums.includes(n));
      // Token subset check (text tokens, excluding numbers)
      const dTextTokens = dInfo.tokens.filter(t => !/^\d+$/.test(t));
      const mTextTokens = new Set(mTokens.filter(t => !/^\d+$/.test(t)));
      const allTextInMain = dTextTokens.length > 0 && dTextTokens.every(t => mTextTokens.has(t));
      if (allTextInMain && sharedNum) return 70;
      // Jaccard similarity on all tokens
      const dSet = new Set(dInfo.tokens);
      const mSet = new Set(mTokens);
      const intersection = [...dSet].filter(t => mSet.has(t)).length;
      const union = new Set([...dSet, ...mSet]).size;
      const jaccard = union === 0 ? 0 : intersection / union;
      if (jaccard >= 0.6) return Math.round(jaccard * 50);
      return 0;
    };

    // For each dalala, find the best-scoring main project
    const dalalaToMain = {}; // dalala.id → main.id
    for (const dInfo of dalalaInfo) {
      let bestMainId = null;
      let bestScore = 0;
      for (const main of mainItems) {
        const score = scoreMatch(dInfo, main);
        if (score > bestScore) {
          bestScore = score;
          bestMainId = main.id;
        }
      }
      // Require minimum score of 50 to be considered a match
      if (bestScore >= 50 && bestMainId) {
        dalalaToMain[dInfo.item.id] = bestMainId;
      }
    }

    // Build groups: collect children per main
    const childrenByMain = {};
    for (const d of dalalaItems) {
      const targetMainId = dalalaToMain[d.id];
      if (targetMainId) {
        if (!childrenByMain[targetMainId]) childrenByMain[targetMainId] = [];
        childrenByMain[targetMainId].push(d);
      }
    }

    // Build result
    const result = [];
    const usedDalalaIds = new Set();
    for (const main of mainItems) {
      const matchingDalalas = childrenByMain[main.id] || [];
      if (matchingDalalas.length > 0) {
        for (const d of matchingDalalas) usedDalalaIds.add(d.id);
        const groupTotal = (Number(main.amount) || 0) + matchingDalalas.reduce((s, d) => s + (Number(d.amount) || 0), 0);
        result.push({
          kind: "group",
          mainItem: main,
          children: matchingDalalas,
          groupTotal,
          groupKey: `g_${main.id}`,
        });
      } else {
        result.push({ kind: "single", item: main });
      }
    }
    // Orphan dalala items (no matching main project) → show standalone
    for (const d of dalalaItems) {
      if (!usedDalalaIds.has(d.id)) {
        result.push({ kind: "single", item: d });
      }
    }
    return result;
  }, [subItems, useGrouping]);

  return (
    <div style={{ marginBottom: 18 }}>
      {/* Subsection header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "8px 12px", background: "#f1f5f9", borderRadius: "8px 8px 0 0",
        borderBottom: "2px solid #e2e8f0",
      }}>
        <div>
          <span style={{ fontWeight: 800, fontSize: 14, color: "#1e293b" }}>{sub.label}</span>
          {sub.hint && <span style={{ fontSize: 11, color: "#64748b", marginRight: 8 }}>· {sub.hint}</span>}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", fontVariantNumeric: "tabular-nums", direction: "ltr" }}>
            {fmt(subTotal)}
          </span>
          {canEdit && !adding && (
            <button
              onClick={() => setAdding(true)}
              title="إضافة عنصر"
              style={{
                padding: "4px 12px", background: "#2563eb", color: "#fff",
                border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}
            >+ إضافة</button>
          )}
        </div>
      </div>

      {/* Items table */}
      <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: "0 0 8px 8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
        <tbody>
          {subItems.length === 0 && !adding && (
            <tr><td colSpan={5} style={{ padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
              {canEdit ? "لا توجد عناصر — اضغط + لإضافة" : "لا توجد عناصر"}
            </td></tr>
          )}
          {/* v3.3.461 — For assets section: render items grouped by category (cash/projects/brokerage/land/equipment/other) */}
          {useAssetCategories ? (() => {
            // Map each group to its category (use mainItem for grouped, item for single)
            const categorized = {};
            ASSET_CATEGORIES.forEach(c => { categorized[c.key] = []; });
            for (const g of groups) {
              const refItem = g.kind === "single" ? g.item : g.mainItem;
              const cat = assetCategoryOf(refItem);
              categorized[cat].push(g);
            }
            return ASSET_CATEGORIES.map(cat => {
              const groupsOfCat = categorized[cat.key];
              if (groupsOfCat.length === 0) return null;
              const catTotal = groupsOfCat.reduce((s, g) => {
                if (g.kind === "single") return s + Number(g.item.amount || 0);
                return s + Number(g.groupTotal || 0);
              }, 0);
              const isCatCollapsed = !!collapsedCategories[cat.key];
              return (
                <React.Fragment key={cat.key}>
                  {/* Category header */}
                  <tr style={{ background: cat.bg, borderTop: "2px solid " + cat.color }}>
                    <td style={{ padding: "6px 10px", width: 30, textAlign: "center" }}>
                      <button
                        onClick={() => setCollapsedCategories(p => ({ ...p, [cat.key]: !p[cat.key] }))}
                        style={{ width: 20, height: 20, padding: 0, border: "none", borderRadius: 4, background: cat.color, color: "#fff", cursor: "pointer", fontSize: 9, fontWeight: 700 }}
                        title={isCatCollapsed ? "عرض" : "إخفاء"}
                      >{isCatCollapsed ? "▶" : "▼"}</button>
                    </td>
                    <td style={{ padding: "6px 10px", fontWeight: 800, color: cat.color, fontSize: 12 }}>
                      {cat.label} <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.7 }}>({groupsOfCat.length})</span>
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "left", fontWeight: 800, color: cat.color, direction: "ltr", fontVariantNumeric: "tabular-nums", fontSize: 13 }}>
                      {Number(catTotal).toLocaleString()}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                  {/* Items of this category */}
                  {!isCatCollapsed && groupsOfCat.map((g, idx) => {
                    if (g.kind === "single") {
                      return <CFItemRow key={g.item.id} item={g.item} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete} />;
                    }
                    const isCollapsed = !!collapsedGroups[g.groupKey];
                    return (
                      <React.Fragment key={g.groupKey}>
                        <tr style={{ background: "#f0fdf4", borderTop: "1px solid #bbf7d0" }}>
                          <td style={{ padding: "6px 10px", width: 30, textAlign: "center" }}>
                            <button
                              onClick={() => setCollapsedGroups(prev => ({ ...prev, [g.groupKey]: !prev[g.groupKey] }))}
                              style={{ width: 20, height: 20, padding: 0, border: "none", borderRadius: 4, background: isCollapsed ? "#dcfce7" : "#16a34a", color: isCollapsed ? "#166534" : "#fff", cursor: "pointer", fontSize: 9, fontWeight: 700 }}
                              title={isCollapsed ? "عرض التفاصيل" : "إخفاء التفاصيل"}
                            >{isCollapsed ? "▶" : "▼"}</button>
                          </td>
                          <td style={{ padding: "6px 10px", fontWeight: 700, color: "#14532d", fontSize: 12 }}>
                            🏢 {g.mainItem.label} <span style={{ fontSize: 10, color: "#15803d", fontWeight: 600 }}>(مع الدلالات)</span>
                          </td>
                          <td style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#14532d", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
                            {Number(g.groupTotal).toLocaleString()}
                          </td>
                          <td colSpan={2} style={{ padding: "6px 10px", textAlign: "center", fontSize: 10, color: "#15803d", fontWeight: 700 }}>الإجمالي</td>
                        </tr>
                        {!isCollapsed && <CFItemRow key={g.mainItem.id} item={g.mainItem} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete} indent />}
                        {!isCollapsed && g.children.map(child => (
                          <CFItemRow key={child.id} item={child} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete} indent />
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            });
          })() : (
            /* Original rendering for non-assets sections */
            groups.map((g, idx) => {
              if (g.kind === "single") {
                return <CFItemRow key={g.item.id} item={g.item} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete} />;
              }
              const isCollapsed = !!collapsedGroups[g.groupKey];
              return (
                <React.Fragment key={g.groupKey}>
                  <tr style={{ background: "#f0fdf4", borderTop: "2px solid #16a34a", borderBottom: "1px solid #bbf7d0" }}>
                    <td style={{ padding: "8px 10px", width: 30, textAlign: "center" }}>
                      <button onClick={() => setCollapsedGroups(prev => ({ ...prev, [g.groupKey]: !prev[g.groupKey] }))}
                        style={{ width: 22, height: 22, padding: 0, border: "none", borderRadius: 4, background: isCollapsed ? "#dcfce7" : "#16a34a", color: isCollapsed ? "#166534" : "#fff", cursor: "pointer", fontSize: 10, fontWeight: 700 }}
                        title={isCollapsed ? "عرض التفاصيل" : "إخفاء التفاصيل"}>{isCollapsed ? "▶" : "▼"}</button>
                    </td>
                    <td style={{ padding: "8px 10px", fontWeight: 800, color: "#14532d", fontSize: 13 }}>
                      🏢 {g.mainItem.label} <span style={{ fontSize: 10, color: "#15803d", fontWeight: 600 }}>(مع الدلالات)</span>
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "left", fontWeight: 800, color: "#14532d", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
                      {Number(g.groupTotal).toLocaleString()}
                    </td>
                    <td style={{ padding: "8px 10px", color: "#94a3b8", fontSize: 10 }}></td>
                    <td style={{ padding: "8px 10px", textAlign: "center", fontSize: 10, color: "#15803d", fontWeight: 700 }}>الإجمالي</td>
                  </tr>
                  {!isCollapsed && <CFItemRow key={g.mainItem.id} item={g.mainItem} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete} indent />}
                  {!isCollapsed && g.children.map(child => (
                    <CFItemRow key={child.id} item={child} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete} indent />
                  ))}
                </React.Fragment>
              );
            })
          )}
          {adding && (
            <CFAddItemForm
              onAdd={async (payload) => {
                await onAdd({ section: flowKey, subsection: sub.key, ...payload });
                setAdding(false);
              }}
              onCancel={() => setAdding(false)}
            />
          )}
        </tbody>
      </table>
    </div>
  );
}

// v3.3.641 — لوحة مقارنة المبيعات: المستلمة/الآجلة من نظام الحجوزات مقابل النظام المحاسبي
const ACC_MODE_LABEL = { debit: "مدين", credit: "دائن", net: "الرصيد" };
function SalesTotalsPanel({ totals, statementId, canEdit, onSaved }) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [rc, setRc] = React.useState("");
  const [rm, setRm] = React.useState("debit");
  const [dc, setDc] = React.useState("");
  const [dm, setDm] = React.useState("debit");
  const [ec, setEc] = React.useState(""); // v3.3.648 — حساب الإجمالي التنفيذي (المباع)
  const [em, setEm] = React.useState("debit");

  React.useEffect(() => {
    setRc(totals?.received_account_code || "");
    setRm(totals?.received_account_mode || "debit");
    setDc(totals?.deferred_account_code || "");
    setDm(totals?.deferred_account_mode || "debit");
    setEc(totals?.executed_account_code || "");
    setEm(totals?.executed_account_mode || "debit");
  }, [totals?.received_account_code, totals?.received_account_mode, totals?.deferred_account_code, totals?.deferred_account_mode, totals?.executed_account_code, totals?.executed_account_mode]);

  if (!totals) return null;
  const save = async () => {
    setSaving(true);
    try {
      await financialAnalysis.updateStatement(statementId, {
        received_account_code: rc.trim() || null, received_account_mode: rm,
        deferred_account_code: dc.trim() || null, deferred_account_mode: dm,
        executed_account_code: ec.trim() || null, executed_account_mode: em, // v3.3.648
      });
      toast.success("تم حفظ إعداد الحسابات");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    finally { setSaving(false); }
  };

  const Cell = ({ label, value, sub, color }) => (
    <div style={{ flex: 1, minWidth: 130, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{value == null ? "—" : fmt(value)}</div>
      {sub && <div style={{ fontSize: 9, color: "#94a3b8" }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: 10, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#0369a1" }}>📊 مقارنة المبيعات — الحجوزات مقابل المحاسبي</div>
        {canEdit && (
          <button onClick={() => setOpen(o => !o)} style={{ fontSize: 10, fontWeight: 700, background: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit" }}>
            {open ? "إغلاق" : "⚙ إعداد الحسابات"}
          </button>
        )}
      </div>
      {/* v3.3.653 — صفّان متطابقان: الحجوزات فوق والمحاسبي تحت، نفس الأعمدة الثلاثة */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        <Cell label="الإجمالي التنفيذي (المباع) — الحجوزات" value={totals.res_executed_sold} color="#1d4ed8" />
        <Cell label="مستلمة — الحجوزات" value={totals.res_received} color="#16a34a" />
        <Cell label="آجلة — الحجوزات" value={totals.res_deferred} color="#d97706" />
        <Cell label="الإجمالي التنفيذي (المباع) — المحاسبي" value={totals.acc_executed_sold}
          sub={totals.executed_account_code ? `حساب ${totals.executed_account_code} · ${ACC_MODE_LABEL[totals.executed_account_mode] || ""}` : "لم يُحدَّد الحساب"} color="#1d4ed8" />
        <Cell label="مستلمة — المحاسبي" value={totals.acc_received}
          sub={totals.received_account_code ? `حساب ${totals.received_account_code} · ${ACC_MODE_LABEL[totals.received_account_mode] || ""}` : "لم يُحدَّد الحساب"} color="#16a34a" />
        <Cell label="آجلة — المحاسبي" value={totals.acc_deferred}
          sub={totals.deferred_account_code ? `حساب ${totals.deferred_account_code} · ${ACC_MODE_LABEL[totals.deferred_account_mode] || ""}` : "لم يُحدَّد الحساب"} color="#d97706" />
      </div>
      {open && canEdit && (
        <div style={{ marginTop: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", minWidth: 90 }}>حساب المستلمة:</span>
            <input value={rc} onChange={e => setRc(e.target.value)} placeholder="رقم الحساب أو مرجع الفرع" style={{ padding: "5px 8px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 6, width: 150, fontFamily: "inherit" }} />
            <select value={rm} onChange={e => setRm(e.target.value)} style={{ padding: "5px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit" }}>
              <option value="debit">مدين</option><option value="credit">دائن</option><option value="net">الرصيد</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", minWidth: 90 }}>حساب الآجلة:</span>
            <input value={dc} onChange={e => setDc(e.target.value)} placeholder="رقم الحساب أو مرجع الفرع" style={{ padding: "5px 8px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 6, width: 150, fontFamily: "inherit" }} />
            <select value={dm} onChange={e => setDm(e.target.value)} style={{ padding: "5px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit" }}>
              <option value="debit">مدين</option><option value="credit">دائن</option><option value="net">الرصيد</option>
            </select>
          </div>
          {/* v3.3.648 — حساب الإجمالي التنفيذي (المباع) */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", minWidth: 90 }}>حساب التنفيذي (المباع):</span>
            <input value={ec} onChange={e => setEc(e.target.value)} placeholder="رقم الحساب أو مرجع الفرع" style={{ padding: "5px 8px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 6, width: 150, fontFamily: "inherit" }} />
            <select value={em} onChange={e => setEm(e.target.value)} style={{ padding: "5px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit" }}>
              <option value="debit">مدين</option><option value="credit">دائن</option><option value="net">الرصيد</option>
            </select>
          </div>
          <div>
            <button onClick={save} disabled={saving} style={{ padding: "6px 16px", background: saving ? "#94a3b8" : "#0891b2", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>
              {saving ? "جارٍ الحفظ…" : "حفظ"}
            </button>
            <span style={{ fontSize: 10, color: "#94a3b8", marginRight: 8 }}>الأرقام تتبع فترة الكشف ومشروعه. يمكن إدخال رقم حساب واحد، أو حساب رئيسي (يجمع فروعه)، أو الرقم المرجعي 🔖 لفرع الشجرة (يجمع كل ما تحته).</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function CashFlowStatementPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin" && !user?.impersonating) || user?.is_admin;
  const canEdit = isAdmin || user?.can_view_projects;

  const [statements, setStatements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [statement, setStatement] = useState(null);
  const [items, setItems] = useState([]);
  const [salesTotals, setSalesTotals] = useState(null); // v3.3.641
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Create form
  const [newTitle, setNewTitle] = useState("");
  const [newYear, setNewYear] = useState("");
  const [newMonth, setNewMonth] = useState("");
  const [newProjectId, setNewProjectId] = useState(""); // v3.3.389

  // v3.3.389 — Turnover chart
  const [turnover, setTurnover] = useState(null);
  const [turnoverLoading, setTurnoverLoading] = useState(false);
  // v3.3.455 — In-chart project filter (overrides statement.project_id without creating new statement)
  const [turnoverProjectFilter, setTurnoverProjectFilter] = useState("");
  const projects = useProjects();

  const loadList = async () => {
    try {
      const r = await financialAnalysis.listStatements();
      const list = r.data.statements || [];
      setStatements(list);
      if (!selectedId && list.length > 0) {
        setSelectedId(list[0].id);
      } else if (list.length === 0) {
        setStatement(null); setItems([]); setLoading(false);
      }
    } catch (e) {
      console.error("listStatements error:", e);
      // Silent fail on first load — likely empty DB; toast only if explicit
      setStatements([]);
      setStatement(null); setItems([]);
      setLoading(false);
    }
  };

  const loadStatement = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await financialAnalysis.getStatement(id);
      setStatement(r.data.statement);
      setItems(r.data.items || []);
      setSalesTotals(r.data.sales_totals || null); // v3.3.641
      // v3.3.389 — fetch turnover chart in parallel
      setTurnoverLoading(true);
      financialAnalysis.getTurnover(id)
        .then(tr => setTurnover(tr.data))
        .catch(err => { console.error("turnover error:", err); setTurnover(null); })
        .finally(() => setTurnoverLoading(false));
    } catch (e) {
      console.error(e);
      toast.error("فشل تحميل الكشف");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (selectedId) loadStatement(selectedId); }, [selectedId]);
  // v3.3.455 — Reload turnover chart when project filter changes
  useEffect(() => {
    if (!selectedId) return;
    setTurnoverLoading(true);
    const params = turnoverProjectFilter ? { project_id: turnoverProjectFilter } : {};
    financialAnalysis.getTurnover(selectedId, params)
      .then(tr => setTurnover(tr.data))
      .catch(err => { console.error("turnover error:", err); setTurnover(null); })
      .finally(() => setTurnoverLoading(false));
  }, [turnoverProjectFilter, selectedId]);

  const handleCreate = async () => {
    try {
      const projectName = newProjectId ? (projects.find(p => p.id == newProjectId)?.name || "") : "";
      const autoTitle = `كشف تدفقات نقدية${projectName ? ` — ${projectName}` : ""}${newYear ? ` (${newYear}${newMonth ? "/" + newMonth : ""})` : ""}`;
      const r = await financialAnalysis.createStatement({
        title: newTitle.trim() || autoTitle,
        period_year: newYear ? parseInt(newYear) : null,
        period_month: newMonth ? parseInt(newMonth) : null,
        project_id: newProjectId ? parseInt(newProjectId) : null,
      });
      toast.success("تم إنشاء كشف جديد");
      setCreating(false);
      setNewTitle(""); setNewYear(""); setNewMonth(""); setNewProjectId("");
      const newId = r.data.statement.id;
      await loadList();
      setSelectedId(newId);
    } catch (e) {
      console.error("createStatement error:", e);
      const msg = e.response?.data?.error || e.message || "فشل الإنشاء";
      toast.error(msg);
    }
  };

  const handleDeleteStatement = async () => {
    if (!statement) return;
    if (!window.confirm(`هل أنت متأكد من حذف "${statement.title}"؟`)) return;
    try {
      await financialAnalysis.deleteStatement(statement.id);
      toast.success("تم الحذف");
      setSelectedId(null);
      setStatement(null); setItems([]);
      loadList();
    } catch (e) {
      toast.error("فشل الحذف");
    }
  };

  const handleAddItem = async (payload) => {
    if (!statement) return;
    try {
      await financialAnalysis.addItem(statement.id, payload);
      toast.success("تمت الإضافة");
      await loadStatement(statement.id);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الإضافة");
    }
  };

  const handleUpdateItem = async (itemId, payload) => {
    if (!statement) return;
    try {
      await financialAnalysis.updateItem(statement.id, itemId, payload);
      await loadStatement(statement.id);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل التعديل");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!statement) return;
    if (!window.confirm("حذف هذا العنصر؟")) return;
    try {
      await financialAnalysis.deleteItem(statement.id, itemId);
      toast.success("تم الحذف");
      await loadStatement(statement.id);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الحذف");
    }
  };

  const handleRefresh = () => {
    if (statement) loadStatement(statement.id);
  };

  // v3.3.638 — إعادة ترتيب عناصر القسم (تحريك أعلى/أسفل)
  const handleReorder = async (itemIds) => {
    if (!statement) return;
    try {
      await financialAnalysis.reorderItems(statement.id, itemIds);
      await loadStatement(statement.id);
    } catch (e) {
      toast.error(e.response?.data?.error || "تعذّر إعادة الترتيب");
    }
  };

  // ─── Totals ───
  const totals = useMemo(() => {
    let cashIn = 0, cashOut = 0, diffIn = 0, diffOut = 0;
    for (const it of items) {
      const amt = Number(it.amount) || 0;
      const diff = it.diff_amount != null ? (Number(it.diff_amount) || 0) : null;
      if (it.section === "cash_in") {
        cashIn += amt;
        if (diff != null) diffIn += diff;
      } else if (it.section === "cash_out") {
        cashOut += amt;
        if (diff != null) diffOut += diff;
      }
    }
    return {
      cashIn, cashOut,
      net: cashIn - cashOut,
      diffIn, diffOut,
      diffNet: diffIn - diffOut,
    };
  }, [items]);

  return (
    <div style={{ padding: "16px 20px", direction: "rtl" }}>
      <PageHeader
        icon={<FiRefreshCw style={{ color: "#0891b2" }} />}
        title="التدفقات النقدية"
        subtitle="كشف تدفقات نقدية مرن — عناصر تلقائية (🟢) + يدوية (🔴) قابلة للإضافة والحذف"
      />

      {/* Top bar — statement selector + actions */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
        marginBottom: 16, padding: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
      }}>
        <select
          value={selectedId || ""}
          onChange={e => setSelectedId(e.target.value ? parseInt(e.target.value) : null)}
          style={{ padding: "7px 12px", fontSize: 13, borderRadius: 7, border: "1px solid #cbd5e1", fontFamily: "inherit", minWidth: 280, cursor: "pointer" }}
        >
          {statements.length === 0 && <option value="">— لا توجد كشوف —</option>}
          {statements.map(s => (
            <option key={s.id} value={s.id}>
              {s.title}
              {s.project_name ? ` · ${s.project_name}` : ""}
              {s.period_year ? ` (${s.period_year}${s.period_month ? "/" + String(s.period_month).padStart(2, "0") : ""})` : ""}
            </option>
          ))}
        </select>

        {canEdit && (
          <button
            onClick={() => setCreating(v => !v)}
            style={{ padding: "7px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >
            {creating ? "إلغاء" : "+ كشف جديد"}
          </button>
        )}

        {statement && (
          <button
            onClick={handleRefresh}
            title="تحديث البيانات التلقائية"
            style={{ padding: "7px 14px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <FiRefreshCw size={12} /> تحديث
          </button>
        )}

        {canEdit && statement && (
          <button
            onClick={handleDeleteStatement}
            style={{ padding: "7px 14px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", marginRight: "auto" }}
          >
            🗑️ حذف الكشف
          </button>
        )}
      </div>

      {/* Create new statement form */}
      {creating && (
        <div style={{ marginBottom: 16, padding: 14, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 10, color: "#075985" }}>إنشاء كشف تدفقات نقدية جديد</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              placeholder="العنوان (اختياري — يُولَّد تلقائياً)"
              style={{ flex: 1, minWidth: 220, padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontFamily: "inherit", fontSize: 13 }}
            />
            <select
              value={newProjectId}
              onChange={e => setNewProjectId(e.target.value)}
              style={{ minWidth: 200, padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 13, fontFamily: "inherit" }}
            >
              <option value="">كل المشاريع</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input
              type="number"
              value={newYear}
              onChange={e => setNewYear(e.target.value)}
              placeholder="السنة (اختياري)"
              style={{ width: 140, padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 13 }}
            />
            <select
              value={newMonth}
              onChange={e => setNewMonth(e.target.value)}
              style={{ width: 140, padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 13, fontFamily: "inherit" }}
            >
              <option value="">كل السنة</option>
              {ARABIC_MONTHS.map(m => <option key={m.n} value={m.n}>{m.label}</option>)}
            </select>
            <button
              onClick={handleCreate}
              style={{ padding: "7px 18px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            >إنشاء</button>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: "#0369a1" }}>
            💡 اختر مشروعاً محدداً لتفعيل شارت نقطة التعادل (Turnover Point)
          </div>
        </div>
      )}

      {loading && <div style={{ padding: 30, textAlign: "center", color: "#64748b" }}>جاري التحميل...</div>}

      {!loading && !statement && statements.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#475569", marginBottom: 6 }}>لا توجد كشوف بعد</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>اضغط "+ كشف جديد" لإنشاء أول كشف تدفقات نقدية</div>
        </div>
      )}

      {!loading && statement && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          {/* CASH-IN */}
          <div style={{ background: CF_SECTIONS.cash_in.bg, borderRadius: 12, padding: 14, border: `2px solid ${CF_SECTIONS.cash_in.color}` }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: CF_SECTIONS.cash_in.color, marginBottom: 12 }}>
              {CF_SECTIONS.cash_in.title}
            </div>
            {/* v3.3.641 — مقارنة المستلمة/الآجلة: نظام الحجوزات مقابل النظام المحاسبي */}
            <SalesTotalsPanel totals={salesTotals} statementId={statement.id} canEdit={canEdit} onSaved={() => loadStatement(statement.id)} />
            {CF_SECTIONS.cash_in.subs.map(sub => (
              <CFSubsection
                key={sub.key}
                flowKey="cash_in"
                sub={sub}
                items={items}
                canEdit={canEdit}
                onAdd={handleAddItem}
                onUpdate={handleUpdateItem}
                onDelete={handleDeleteItem}
              />
            ))}
            <div style={{
              marginTop: 10, padding: "10px 14px", background: CF_SECTIONS.cash_in.color, color: "#fff",
              borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontWeight: 800 }}>إجمالي Cash-in</span>
              <span style={{ fontWeight: 800, fontSize: 16, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(totals.cashIn)}</span>
            </div>
          </div>

          {/* CASH-OUT */}
          <div style={{ background: CF_SECTIONS.cash_out.bg, borderRadius: 12, padding: 14, border: `2px solid ${CF_SECTIONS.cash_out.color}` }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: CF_SECTIONS.cash_out.color, marginBottom: 12 }}>
              {CF_SECTIONS.cash_out.title}
            </div>
            {/* v3.3.462 — Restructured Cash-out UI with categories + project buckets */}
            <CashOutRestructured
              items={items}
              canEdit={canEdit}
              onAdd={handleAddItem}
              onUpdate={handleUpdateItem}
              onDelete={handleDeleteItem}
              onReorder={handleReorder}
            />
            {/* Legacy view (hidden) — kept for any future debugging
            {false && CF_SECTIONS.cash_out.subs.map(sub => (
              <CFSubsection key={sub.key} flowKey="cash_out" sub={sub} items={items} canEdit={canEdit} onAdd={handleAddItem} onUpdate={handleUpdateItem} onDelete={handleDeleteItem} />
            ))} */}
            <div style={{
              marginTop: 10, padding: "10px 14px", background: CF_SECTIONS.cash_out.color, color: "#fff",
              borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontWeight: 800 }}>إجمالي Cash-out</span>
              <span style={{ fontWeight: 800, fontSize: 16, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(totals.cashOut)}</span>
            </div>
          </div>
        </div>
      )}

      {/* NET TOTAL */}
      {!loading && statement && (
        <div style={{
          marginTop: 18, padding: "16px 22px",
          background: totals.net >= 0 ? "#f0fdf4" : "#fef2f2",
          border: `2px solid ${totals.net >= 0 ? "#16a34a" : "#dc2626"}`,
          borderRadius: 12,
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap",
        }}>
          <div>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>صافي التدفق النقدي</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Cash-in − Cash-out</div>
          </div>
          <div style={{
            fontSize: 26, fontWeight: 800,
            color: totals.net >= 0 ? "#15803d" : "#9a3412",
            direction: "ltr", fontVariantNumeric: "tabular-nums",
          }}>
            {totals.net >= 0 ? "+" : ""}{fmt(totals.net)}
          </div>
          {(totals.diffIn !== 0 || totals.diffOut !== 0) && (
            <div style={{ borderRight: "1px solid #cbd5e1", paddingRight: 16 }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>صافي الفرق (التسوية)</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#9a3412", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
                ({fmt(Math.abs(totals.diffIn - totals.diffOut))})
              </div>
            </div>
          )}
        </div>
      )}

      {/* TURNOVER POINT CHART — v3.3.389 */}
      {!loading && statement && (
        <TurnoverChart
          turnover={turnover}
          loading={turnoverLoading}
          projectName={statement.project_name}
          hasProject={!!statement.project_id}
          projects={projects}
          filterProjectId={turnoverProjectFilter}
          onFilterChange={setTurnoverProjectFilter}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TurnoverChart — v3.3.389
// شارت تراكمي يعرض Cash-in vs Cash-out مع تحديد نقطة التعادل (Turnover Point)
// النقطة: أول شهر يتجاوز فيه Cash-in التراكمي Cash-out التراكمي
// ═══════════════════════════════════════════════════════════════════
function TurnoverChart({ turnover, loading, projectName, hasProject, projects, filterProjectId, onFilterChange }) {
  // v3.3.455 — Determine the currently displayed project name from filter (override) or statement
  const activeFilterName = React.useMemo(() => {
    if (!filterProjectId || filterProjectId === "all") return null;
    const p = (projects || []).find(p => String(p.id) === String(filterProjectId));
    return p ? p.name : null;
  }, [filterProjectId, projects]);
  const displayedProjectName = activeFilterName || projectName;
  const showProjectsAllText = filterProjectId === "all" || (!filterProjectId && !hasProject);

  if (loading) {
    return (
      <div style={{ marginTop: 18, padding: 30, textAlign: "center", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", color: "#64748b" }}>
        جاري تحميل شارت نقطة التعادل...
      </div>
    );
  }
  if (!turnover || !turnover.series || turnover.series.length === 0) {
    return (
      <div style={{ marginTop: 18, padding: 20, background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1", color: "#64748b", fontSize: 13, textAlign: "center" }}>
        {/* v3.3.455 — Show filter even when empty so user can pick a project */}
        {projects && projects.length > 0 && (
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>المشروع:</span>
            <select
              value={filterProjectId || ""}
              onChange={(e) => onFilterChange && onFilterChange(e.target.value)}
              style={{ padding: "5px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}
            >
              <option value="">— حسب الكشف —</option>
              <option value="all">كل المشاريع</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        {hasProject || filterProjectId ? "لا توجد بيانات كافية لرسم شارت التعادل" : "💡 لتفعيل شارت نقطة التعادل (Turnover Point) — اختر مشروعاً من القائمة أعلاه، أو اربط الكشف بمشروع محدد"}
      </div>
    );
  }

  const series = turnover.series;
  const tp = turnover.turnover_point;

  // Format month label: 2025-01 → "يناير 25"
  const monthLabel = (key) => {
    const [y, m] = key.split("-");
    const monthName = ARABIC_MONTHS[parseInt(m) - 1]?.label || m;
    return `${monthName.substring(0, 3)} ${y.substring(2)}`;
  };

  // Recharts data format
  const chartData = series.map(s => ({
    month: monthLabel(s.key),
    monthKey: s.key,
    "إيرادات تراكمية": Math.round(s.cumulative_in),
    "تكاليف تراكمية": Math.round(s.cumulative_out),
    "صافي": Math.round(s.net),
  }));

  // Find turnover index for ReferenceLine
  const tpIndex = tp ? series.findIndex(s => s.key === tp.key) : -1;
  const tpMonthLabel = tp ? monthLabel(tp.key) : null;

  return (
    <div style={{ marginTop: 22, background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>
            📈 نقطة التعادل (Turnover Point)
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {showProjectsAllText ? "كل المشاريع" : (displayedProjectName ? `مشروع: ${displayedProjectName}` : "كل المشاريع")} — تتبع متى تجاوزت الإيرادات التراكمية التكاليف التراكمية
          </div>
        </div>
        {/* v3.3.455 — Project filter dropdown */}
        {projects && projects.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>🎯 فلتر:</span>
            <select
              value={filterProjectId || ""}
              onChange={(e) => onFilterChange && onFilterChange(e.target.value)}
              style={{ padding: "5px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit", background: "#fff" }}
            >
              <option value="">— حسب الكشف —</option>
              <option value="all">كل المشاريع</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        {tp ? (
          <div style={{ background: "#dcfce7", border: "2px solid #16a34a", borderRadius: 8, padding: "8px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#15803d", fontWeight: 700 }}>تم التعادل في</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#15803d" }}>{tpMonthLabel}</div>
            <div style={{ fontSize: 10, color: "#166534", direction: "ltr" }}>
              فائض: +{fmt(tp.surplus)} ({tp.months_to_turnover} شهر)
            </div>
          </div>
        ) : (
          <div style={{ background: "#fef3c7", border: "2px solid #f59e0b", borderRadius: 8, padding: "8px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#92400e", fontWeight: 700 }}>لم يتم التعادل بعد</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#92400e", direction: "ltr" }}>
              عجز: {fmt(turnover.total_out - turnover.total_in)}
            </div>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: "#475569" }}
            angle={-30}
            textAnchor="end"
            height={60}
            interval={Math.max(0, Math.floor(chartData.length / 12))}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#475569" }}
            tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
            width={70}
          />
          <Tooltip
            formatter={(value) => fmt(value)}
            labelStyle={{ direction: "rtl", color: "#0f172a", fontWeight: 700 }}
            contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />

          {/* Cash-in cumulative — green area */}
          <Area
            type="monotone"
            dataKey="إيرادات تراكمية"
            stroke="#16a34a"
            strokeWidth={2.5}
            fill="#16a34a"
            fillOpacity={0.18}
            dot={false}
            activeDot={{ r: 4 }}
          />
          {/* Cash-out cumulative — red area */}
          <Area
            type="monotone"
            dataKey="تكاليف تراكمية"
            stroke="#dc2626"
            strokeWidth={2.5}
            fill="#dc2626"
            fillOpacity={0.12}
            dot={false}
            activeDot={{ r: 4 }}
          />
          {/* Net line — blue dashed */}
          <Line
            type="monotone"
            dataKey="صافي"
            stroke="#2563eb"
            strokeWidth={1.5}
            strokeDasharray="5 3"
            dot={false}
          />

          {/* Turnover point marker — vertical reference line */}
          {tp && tpIndex >= 0 && (
            <ReferenceLine
              x={chartData[tpIndex].month}
              stroke="#16a34a"
              strokeWidth={2}
              strokeDasharray="6 4"
              label={{ value: "🎯 التعادل", position: "top", fill: "#15803d", fontSize: 12, fontWeight: 700 }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Summary boxes */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 14 }}>
        <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>إجمالي الإيرادات</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#15803d", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(turnover.total_in)}</div>
        </div>
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600 }}>إجمالي التكاليف</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#dc2626", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(turnover.total_out)}</div>
        </div>
        <div style={{ background: turnover.net >= 0 ? "#eff6ff" : "#fffbeb", border: `1px solid ${turnover.net >= 0 ? "#93c5fd" : "#fcd34d"}`, borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: turnover.net >= 0 ? "#1e40af" : "#92400e", fontWeight: 600 }}>الصافي الحالي</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: turnover.net >= 0 ? "#2563eb" : "#d97706", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
            {turnover.net >= 0 ? "+" : ""}{fmt(turnover.net)}
          </div>
        </div>
        <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>عدد الأشهر المرصودة</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", direction: "ltr" }}>{series.length}</div>
        </div>
      </div>
    </div>
  );
}
